import { customerContext, customerContextWait, getCtx } from '@theme/rs-current-company-location';
import { getPlAppBaseUrl } from "@theme/gadget-envs";

/**
 * @typedef {{ __t?: (key: string, ...args: any[]) => string; gadget_env?: string; myJWT?: string; ResideoPL?: any; Shopify?: any }} ShopifyGlobals
 * @type {Window & typeof globalThis & ShopifyGlobals}
 */
const _win = /** @type {any} */ (window);

/** @param {string} p */
function _img(p) { return p; }

const LABEL_RENDER = {
  LINE_HEIGHT_MULT: 0.9,
  JPEG_PX_PER_LINE: Math.ceil(24 * 3 * 0.9), // 65px per line in the generated JPEG
  JPEG_OVERHEAD_PX_T: 26,                     // T Series: minimal +2 + pngBufferToJpeg 12px padding × 2
  JPEG_OVERHEAD_PX_NON_T: 44,                 // Non-T: paddedTop 18 + paddedBottom 18 + JPEG_PADDING 4×2
};

const FONT_MODE = {
  normal:    { maxChars: 28, fontSize: 9,  lineHeight: 11 },
  condensed: { maxChars: 30, fontSize: 8,  lineHeight: 10 },
};

// Stands in for "some realistic string at the character limit" when capping font size by
// width — using the actual typed text instead would make font size change per keystroke.
// Mixed case, no unusually wide/narrow glyphs.
const WIDTH_REFERENCE_STRING = "AaBbCcDdEeFfGgHhIiJjKkLlMmNnO".repeat(2);

// Extra oversampling (on top of dpr) for the zoom preview's text layer, so the
// several-fold stretch into the zoom canvas has real pixel detail instead of blur.
const ZOOM_TEXT_RENDER_SCALE = 4;

/** @typedef {{ ascent: number, descent: number, fba: number }} LineInkMetrics */

// Extra clearance needed between line i and line i+1 BEYOND the default per-line
// advance (fontSize × LINE_HEIGHT_MULT), so line i's descenders don't collide with
// line i+1's glyphs. For equal font sizes the required gap reduces to D_u + A_l,
// so the extra beyond the default 0.9em advance is max(0, D_u + A_l - 0.9em). We add
// a safety margin (0.1em) to give descender lines generous visual breathing room —
// at 72px font size this gives ~10px extra clearance for pairs like "p"→"P", which
// reads as comfortable spacing. Mirrors web/lib/labelTextLayout.ts in the admin repo.
/** @param {LineInkMetrics} upper @param {LineInkMetrics} lower @param {number} fontSize */
function extraLineGap(upper, lower, fontSize) {
  const SAFETY_MARGIN = 0.1 * fontSize;
  return upper.descent + lower.ascent - LABEL_RENDER.LINE_HEIGHT_MULT * fontSize + SAFETY_MARGIN;
}

// Measures one line's ink at a given font size. The caller sets ctx.font first (with
// the SAME weight the renderer will use for that line); textBaseline must be
// "alphabetic" during the call (see callers) because "top" corrupts the bounding-box
// metrics in some browsers even though the spec defines them as baseline-independent.
/** @param {any} ctx @param {string} text @param {number} fontSize @returns {LineInkMetrics} */
function measureLineInk(ctx, text, fontSize) {
  const m = ctx.measureText(text);
  return {
    ascent: m.actualBoundingBoxAscent || 0,
    descent: m.actualBoundingBoxDescent || 0,
    // FBA ≈ em size is a safe over-estimate if the metric is unavailable.
    fba: m.fontBoundingBoxAscent || fontSize,
  };
}

// Waits for the label's web font (loaded via Google Fonts in init) before canvas
// measurement/rendering. The server renders the JPG/BMP with the same Roboto family;
// measuring or drawing before the web font is ready would fall back to another
// family whose ink metrics differ, desyncing descender clearance between the live
// previews and the generated files. Resolves early if the font is already cached.
/** @param {string} fontFamily @returns {Promise<void>} */
async function ensureLabelFontLoaded(fontFamily) {
  if (!document.fonts || !fontFamily) return;
  const family = String(fontFamily).split(',')[0].trim();
  try {
    await Promise.race([
      Promise.all([
        document.fonts.load('400 16px "' + family + '"'),
        document.fonts.load('700 16px "' + family + '"'),
      ]),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  } catch (e) {
    // Fall through on failure — rendering proceeds with whatever font is available.
  }
}

// Caps the zoom preview's destination size. Uncapped, it can grow as wide as the
// source photo, which combined with the theme's `canvas { max-width: 100% }` rule
// makes it fill its container's width.
const ZOOM_MAX_WIDTH = 380;
const ZOOM_MAX_HEIGHT = 380;

/** @type {Record<string, { maxLines: number; condensed: boolean }>} */
const LABEL_TYPE = {
  "t-series":         { maxLines: 3, condensed: false },
  "t-series-digital": { maxLines: 4, condensed: false },
  "non-t-series":     { maxLines: 3, condensed: true  },
};

/** Status names that mean a part number is obsolete (used for filtering in APN dropdown) */
const OBSOLETE_STATUSES = new Set(["Obsolete", "Sent to SAP Obsolete", "Error in SAP Obsolete"]);

class PrivateLabelApp {
  constructor({ env = "development" } = {}) {
    this.env = env;

    // -----------------------------
    // STATE
    // -----------------------------
    this.state = {
      token: "",
      fergusonFilteringEnabled: false, // default disabled; updated from API response

      // current step (derived from visible section or fallback)
      step: null, // "label-selection" | "label-creation" | "label-confirmation" | etc.

      // edit mode (for draft edit button)
      edit: {
        enabled: false,
        labelId: "",
        previewOnImageId: "", // drives dropdown + canvas image in edit
        assignedBasePartNumbers: /** @type {any[]} */ ([]),
        isLogo: false,
      },

      // selection (type + products)
      selection: {
        types: /** @type {any[]} */ ([]),
        typeId: "",
        typeHandle: "", // folder handle (preferred) OR derived from title
        products: /** @type {any[]} */ ([]),
        productsTypeId: "",
        partSetups: /** @type {any[]} */ ([]),
        partSetupsKey: "",
        accountNumber: "",
      },

      // list state
      list: {
        limit: 12,
        page: 1,
        total: 0,
        totalPages: 0,
        searchText: "",
        filterStatus: "all",
        filterPartNumber: "",
        showSuccessMessage: false,
        // cursor stack for fast-path pagination (no search text)
        // index 0 = cursor to reach page 2, index 1 = page 3, etc.
        cursorStack: /** @type {string[]} */ ([]),
        nextCursor: /** @type {string|null} */ (null),
        useCursor: false,
      },

      cpnView: {
        limit: 12,
        page: 1,
        total: 0,
        totalPages: 0,
      },

      // form values
      form: {
        contractorName: "",
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        previewImage: null,
        fullPreviewImage: null,
      },

      // canvas config
      canvas: {
        width: 400,
        height: 400,
        sourceImgWidth: 1280,
        sourceImgHeight: 1280,
        src: "",
        paddingLeft: 70,
        paddingBottom: 45,
        lineHeight: 11,
        fontSize: 9,
        color: "#323232",
        maxChars: 28,
        textAlign: "left",
        textBaseline: "top",
        condensedFont: false,

        previewOnImageId: "",
        partNumbers: "",
        refWidth: 0,
        textY: 0,
        labelAreaH: 0,
        labelAreaW: 0,
      },

      // confirmation “prefix labels” captured once
      confirmationPrefix: {
        accountNumber: "",
        contractorName: "",
        labelType: "",
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        apnSelectedAll: "",
        apnActionBtnLabel: "",
      },

      assignPartNumber: {
        selectedCount: 0,
      }
    };

    // -----------------------------
    // CONSTANTS
    // -----------------------------
    this.ERRORS = {
      required: "This field is required.",
      maxChars: "Maximum character limit exceeded.",
      apnPartNumber: "Max 18 characters exceeded",
      emailFormat: "Please enter a valid email address.",
      atLeastOneRow: "Please complete at least one row before submitting.",
      duplicate: "A label with this information already exists for your account. Please modify the details and try again.",
    };

    const stepLabel = (/** @type {string} */ key, /** @type {string} */ fallback) =>
      (typeof window.__t === "function" && window.__translations?.[key]) ? window.__t(key) : fallback;

    this.STEP_LABELS = {
      "private-label": stepLabel("private_labels", "Private Labels"),
      "label-selection": stepLabel("private_label_label_creation", "Label Creation"),
      "label-creation": stepLabel("private_label_label_creation", "Label Creation"),
      "label-confirmation": stepLabel("private_label_label_confirmation", "Label Confirmation"),
      "assign-part-number": stepLabel("private_label_assign_part_number", "Assign Part Number"),
      "customer-part-number": stepLabel("private_label_customer_part_number", "Customer Part Number"),
    };

    // Fallback when data-step-handle is missing from rendered HTML
    this.SECTION_CLASS_TO_STEP = [
      ["rs-pl-listening-section", "private-label"],
      ["rs-pl-table-section", "private-label"],
      ["rs-private-label-selection-section", "label-selection"],
      ["rs-private-label-creation-section", "label-creation"],
      ["rs-private-label-confirmation-section", "label-confirmation"],
      ["rs-pl-assign-part-number-section", "assign-part-number"],
      ["rs-pl-apn-table-section", "assign-part-number"],
      ["rs-pl-customer-part-number-section", "customer-part-number"],
      ["rs-pl-cpn-table-section", "customer-part-number"],
    ];

    // -----------------------------
    // SELECTORS
    // -----------------------------
    this.S = {
      breadCrumb: ".rs-breadcrumb",
      breadCrumbList: ".rs-breadcrumb__list",
      breadCrumbCurrent: ".rs-breadcrumb__item--current",

      tabButtons: ".rs-private-label-tab-btn",
      tabSection: ".rs-private-label-tabs-section",
      sections: ".rs-pl-sections",

      // selection step
      typesWrapper: ".rs-pl-select-wrapper",
      typesItem: ".rs-pl-select-item",

      // creation step
      canvasMain: "#rs-canvas",
      canvasWrapper: ".rs-pl-canavas-wrapper",
      companyNameInput: '.rs-input[name="name"]',
      canvasInputs: [
        '.rs-input[name="company"]',
        '.rs-input[name="phone"]',
        '.rs-input[name="optional"]',
        '.rs-input[name="optional_digital"]',
      ],
      condensedWrap: ".rs-pl-condensed-font",
      condensedToggle: ".rs-toggle__checkbox",

      // confirmation step
      confAccount: ".rs-pl-account-number p",
      confLabelType: ".rs-pl-label-type p",
      confContractor: ".rs-pl-constractor-name",
      confLine1: ".rs-pl-line-1 p",
      confLine2: ".rs-pl-line-2 p",
      confLine3: ".rs-pl-line-3 p",
      confLine4: ".rs-pl-line-4 p",

      // list/table
      tableBody: ".rs-table-body",
      tableRowTemplate: ".rs-table-body__row",
      tableEmptyTemplate: ".rs-table-body__empty",
      apnTableBody: ".rs-pl-apn-table-section .rs-table-body",
      cpnTableBody: ".rs-pl-cpn-table-section .rs-table-body",
      apnRowTemplate: ".rs-pl-apn-table-section .rs-table-body__row",
      cpnRowTemplate: ".rs-pl-cpn-table-section .rs-table-body__row",

      // dropdown root
      productDropdownRoot:
        ".rs-private-label-pdp-select .rs-custom-select__wrapper, .rs-private-label-pdp-select",

      // modal
      modal: "#rs-private-label-save-modal",
      modalPreviewImage: '.rs-pl-preview-image-popup',
      previewImageWrapper: ".rs-pl-preview-image-wrapper",

      // Banners
      successBanner: ".rs-banner--success",

      // Assign Part Number
      apnAccountNumber: '.rs-pl-apn-account-number p',
      apnSalesArea: '.rs-pl-apn-sales-area p',
      apnPreviewImageWrapper: '.rs-pl-apn-image-wrapper',
      apnEmail: '.rs-pl-apn-email-address input[type="email"]',
      apnSecondEmail: '.rs-pl-apn-second-email-address input[type="email"]',
      apnLine1: '.rs-pl-apn-line1 p',
      apnLine2: '.rs-pl-apn-line2 p',
      apnLine3: '.rs-pl-apn-line3 p',
      apnLine4: '.rs-pl-apn-line4 p',
      apnResideoPartNumber: '.rs-pl-apn-resideo-part-number-select',
      apnResideoPartNumberDescription: '.rs-pl-apn-resideo-part-number-description p',
      apnCustomerPartNumber: '.rs-pl-apn-customer-part-number input[type="text"]',
      apnLabelPosition: '.rs-pl-apn-label-position-select',
      apnSelectedBanner: '.rs-pl-apn-selected-banner',
      apnSelectRow: '.rs-pl-apn-select-checkbox',
      apnSelectAllRows: '.rs-pl-apn-select-all-checkbox',
      apnActionButtonLabel: '.rs-actions__toggle .rs-actions__label',
      apnActionSelectedAllButtonLabel: '.rs-pl-apn-select-all-btn span',
      apnPreviewModal: '.rs-pl-apn-popup',


      // Customer Part Number
      cpnAccountNumber: '.rs-pl-cpn-account-number p',
      cpnSalesArea: '.rs-pl-cpn-sales-area p',
      cpnPreviewImageWrapper: '.rs-pl-cpn-image-wrapper',
      cpnDescriptionWrapper: '.rs-pl-cpn-description-wrapper',
      cpnLine1: '.rs-pl-cpn-line1 p',
      cpnLine2: '.rs-pl-cpn-line2 p',
      cpnLine3: '.rs-pl-cpn-line3 p',
      cpnLine4: '.rs-pl-cpn-line4 p',
      cpnResideoPartNumber: '.rs-pl-cpn-resideo-part-number-select',
      cpnCustomerPartNumber: '.rs-pl-cpn-customer-part-number input[type="text"]',
      cpnLabelPosition: '.rs-pl-cpn-label-position-select',
      cpnPartStatus: '.rs-pl-cpn-part-status p',
      cpnPartCreated: '.rs-pl-cpn-part-created p',
      cpnPartApproved: '.rs-pl-cpn-part-approved p',
      cpnPreviewButton: '.rs-pl-cpn-preview-btn',

      // Paginations
      apnPagination: ".rs-pl-apn-pagination",
      cpnPagination: ".rs-pl-cpn-pagination",
      apnPaginationInfo: ".rs-pl-apn-pagination .rs-cart-pagination__info",
      cpnPaginationInfo: ".rs-pl-cpn-pagination .rs-cart-pagination__info",

    };

    /** @type {Record<string, any>} */
    this.els = {};
    this.templates = { row: /** @type {Element|null} */ (null), empty: /** @type {Element|null} */ (null), apnRow: /** @type {Element|null} */ (null), cpnRow: /** @type {Element|null} */ (null) };
    this._pendingRequests = 0;
    this._searchDebounceTimer = undefined;
    this._previewImageCache = new Map();

    // bind once
    this.onInput = this.onInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onChangeCapture = this.onChangeCapture.bind(this);
    this.onChangeBubble = this.onChangeBubble.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onPopState = this.onPopState.bind(this);
  }

  // =========================================================
  // INIT
  // =========================================================
  async init() {
    this.cacheElements();
    this.cacheTemplates();
    this.captureConfirmationPrefixes();
    this.detectInitialStep();
    this.bindEvents();

    if (!document.getElementById('rs-pl-spinner-styles')) {
      const s = document.createElement('style');
      s.id = 'rs-pl-spinner-styles';
      s.textContent = `.rs-pl-list-loading{display:flex;justify-content:center;align-items:center;padding:32px 0;}` +
        `#rs-pl-topbar{position:fixed;top:0;left:0;width:100%;height:3px;z-index:9998;overflow:hidden;pointer-events:none;transition:opacity .2s;}` +
        `#rs-pl-topbar::after{content:'';position:absolute;top:0;height:100%;width:40%;background:var(--color-button,#323232);animation:rs-pl-bar 1.2s ease-in-out infinite;}` +
        `@keyframes rs-pl-bar{0%{left:-40%}100%{left:110%}}` +
        `.rs-pl-skeleton-card{display:flex;flex-direction:column;align-items:center;justify-content:space-between;gap:24px;width:700px;max-width:100%;padding:24px;border:1px solid var(--color-border-secondary,#e0e0e0);border-radius:6px;background:#fff;min-height:200px}` +
        `.rs-pl-skeleton-img{width:100px;height:100px;flex-shrink:0;border-radius:4px;background:linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%);background-size:200% 100%;animation:rs-pl-shimmer 1.5s infinite}` +
        `.rs-pl-skeleton-text{display:flex;flex-direction:column;gap:12px;align-items:center;width:100%}` +
        `.rs-pl-skeleton-line{height:14px;border-radius:4px;background:linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%);background-size:200% 100%;animation:rs-pl-shimmer 1.5s infinite}` +
        `.rs-pl-skeleton-line.rs-pl-skeleton-title{height:18px;width:40%}` +
        `.rs-pl-skeleton-line.rs-pl-skeleton-desc{width:80%}` +
        `.rs-pl-skeleton-btn{height:36px;width:160px;border-radius:4px;background:linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%);background-size:200% 100%;animation:rs-pl-shimmer 1.5s infinite}` +
`@keyframes rs-pl-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`;
      document.head.appendChild(s);
    }

    if (!document.getElementById('rs-pl-roboto')) {
      // Regular Roboto (not just Condensed): non-condensed labels measure and draw
      // with it, and the server renders the JPG/BMP with the same font — a fallback
      // to another family would change ink metrics and desync descender spacing.
      const link = document.createElement('link');
      link.id = 'rs-pl-roboto';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap';
      document.head.appendChild(link);
    }

    if (!document.getElementById('rs-pl-roboto-condensed')) {
      const link = document.createElement('link');
      link.id = 'rs-pl-roboto-condensed';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,400;0,700;1,400;1,700&display=swap';
      document.head.appendChild(link);
    }

    this.applyStepFromUrl({ render: true });

    await this.generateToken();

    // Snapshot URL state before restoreFromUrl() rewrites it (it adds ?step=private-label
    // when there are no params, making the post-restore parseUrlState check always return
    // a step value and skip the initial list load).
    const urlStateBeforeRestore = this.parseUrlState();

    await this.restoreFromUrl();

    // If there was no deep-link (no step/id/preview), restoreFromUrl() only syncs the URL
    // and returns — it never calls setStep/renderStepContent. Load the list manually here.
    if (!urlStateBeforeRestore.step && !urlStateBeforeRestore.id && !urlStateBeforeRestore.preview) {
      await this.loadAndRenderList();
      // Pre-fetch label types in background so the selection step feels instant
      this.fetchTypes().catch(() => {});
    }
  }

  cacheElements() {
    const qs = (/** @type {any} */ sel, root = document) => root.querySelector(sel);
    const qsa = (/** @type {any} */ sel, root = document) => Array.from(root.querySelectorAll(sel));

    this.els.breadCrumb = qs(this.S.breadCrumb);
    // Snapshot original breadcrumb markup + styles so renderBreadcrumb() can restore or replace.
    const _crumbList = qs(this.S.breadCrumbList, /** @type {any} */ (this.els).breadCrumb);
    const _crumbLink = _crumbList?.querySelector('.rs-breadcrumb__link');
    const _crumbSep  = _crumbList?.querySelector('.rs-breadcrumb__separator');
    const _els = /** @type {any} */ (this.els);
    _els.breadCrumbOriginalHTML = _crumbList?.innerHTML || '';
    _els.breadCrumbLinkStyle    = _crumbLink?.getAttribute('style') || '';
    _els.breadCrumbSepStyle     = _crumbSep?.getAttribute('style') || '';
    _els.breadCrumbSepText      = _crumbSep?.textContent?.trim() || '/';

    this.els.tabButtons = qsa(this.S.tabButtons);
    this.els.tabSection = qs(this.S.tabSection);
    this.els.sections = qsa(this.S.sections);

    this.els.typesWrapper = qs(this.S.typesWrapper);
    this.els.typesItem = qs(this.S.typesItem);

    this.els.canvas = qs(this.S.canvasMain);
    this.els.canvasWrapper = qs(this.S.canvasWrapper);
    this.els.companyNameInput = qs(this.S.companyNameInput);
    this.els.canvasInputs = this.S.canvasInputs.map((s) => qs(s));

    this.els.condensedWrap = qs(this.S.condensedWrap);
    this.els.condensedToggle = qs(this.S.condensedToggle);

    this.els.confAccount = qs(this.S.confAccount);
    this.els.confLabelType = qs(this.S.confLabelType);
    this.els.confContractor = qs(this.S.confContractor);
    this.els.confLine1 = qs(this.S.confLine1);
    this.els.confLine2 = qs(this.S.confLine2);
    this.els.confLine3 = qs(this.S.confLine3);
    this.els.confLine4 = qs(this.S.confLine4);

    this.els.tableBody = qs(this.S.tableBody);
    this.els.rowTemplate = qs(this.S.tableRowTemplate);
    this.els.emptyTemplate = qs(this.S.tableEmptyTemplate);

    this.els.modal = qs(this.S.modal);
    this.els.modalPreviewImage = qs(this.S.modalPreviewImage);
    this.els.previewImageWrapper = qs(this.S.previewImageWrapper);

    this.els.successBanner = qs(this.S.successBanner);


    this.els.apnAccountNumber = qs(this.S.apnAccountNumber);
    this.els.apnSalesArea = qs(this.S.apnSalesArea);
    this.els.apnPreviewImageWrapper = qs(this.S.apnPreviewImageWrapper);
    this.els.apnEmail = qs(this.S.apnEmail);
    this.els.apnSecondEmail = qs(this.S.apnSecondEmail);
    this.els.apnLine1 = qs(this.S.apnLine1);
    this.els.apnLine2 = qs(this.S.apnLine2);
    this.els.apnLine3 = qs(this.S.apnLine3);
    this.els.apnLine4 = qs(this.S.apnLine4);
    this.els.apnCustomerPartNumber = qs(this.S.apnCustomerPartNumber);
    this.els.apnResideoPartNumberDescription = qs(this.S.apnResideoPartNumberDescription);
    this.els.apnTableBody = qs(this.S.apnTableBody);
    this.els.cpnTableBody = qs(this.S.cpnTableBody);
    this.els.apnRowTemplate = qs(this.S.apnRowTemplate);
    this.els.cpnRowTemplate = qs(this.S.cpnRowTemplate);
    this.els.apnSelectedBanner = qs(this.S.apnSelectedBanner);
    this.els.apnActionBtnLabel = qs(this.S.apnActionButtonLabel);
    this.els.apnActionSelectedAllBtnLabel = qs(this.S.apnActionSelectedAllButtonLabel);
    this.els.apnPreviewModal = qs(this.S.apnPreviewModal);


    this.els.cpnAccountNumber = qs(this.S.cpnAccountNumber);
    this.els.cpnSalesArea = qs(this.S.cpnSalesArea);
    this.els.cpnPreviewImageWrapper = qs(this.S.cpnPreviewImageWrapper);
    this.els.cpnDescriptionWrapper = qs(this.S.cpnDescriptionWrapper);
    this.els.cpnLine1 = qs(this.S.cpnLine1);
    this.els.cpnLine2 = qs(this.S.cpnLine2);
    this.els.cpnLine3 = qs(this.S.cpnLine3);
    this.els.cpnLine4 = qs(this.S.cpnLine4);
    this.els.cpnCustomerPartNumber = qs(this.S.cpnCustomerPartNumber);
    this.els.cpnResideoPartNumber = qs(this.S.cpnResideoPartNumber);
    this.els.cpnLabelPosition = qs(this.S.cpnLabelPosition);
    this.els.cpnPartStatus = qs(this.S.cpnPartStatus);
    this.els.cpnPartCreated = qs(this.S.cpnPartCreated);
    this.els.cpnPartApproved = qs(this.S.cpnPartApproved);

    this.els.apnPagination = qs(this.S.apnPagination);
    this.els.cpnPagination = qs(this.S.cpnPagination);
    this.els.apnPaginationInfo = qs(this.S.apnPaginationInfo);
    this.els.cpnPaginationInfo = qs(this.S.cpnPaginationInfo);
  }

  cacheTemplates() {
    this.templates.row = this.els.rowTemplate ? this.els.rowTemplate.cloneNode(true) : null;
    this.templates.empty = this.els.emptyTemplate ? this.els.emptyTemplate.cloneNode(true) : null;
    // Hide the original in the DOM immediately — only the clone is shown after data fetch confirms no records
    /** @type {any} */ (this.els).emptyTemplate?.classList.add("rs-hidden");
    this.templates.apnRow = this.els.apnRowTemplate ? this.els.apnRowTemplate.cloneNode(true) : null;
    // Strip initialized flags so rs-custom-select.js can attach listeners to cloned rows
    this.templates.apnRow?.querySelectorAll('[data-initialized]').forEach((el) => el.removeAttribute('data-initialized'));
    this.templates.cpnRow = this.els.cpnRowTemplate ? this.els.cpnRowTemplate.cloneNode(true) : null;
    // The cpn row template ships with sample placeholder text (e.g. "TH1110DV1009/UA") and no
    // rs-hidden class, so remove the live original immediately — only clones of it are shown
    // after a real fetch resolves, same as the private-label list's emptyTemplate above.
    this.els.cpnRowTemplate?.remove();
    this.state.confirmationPrefix.apnActionBtnLabel = this.els.apnActionBtnLabel ? this.els.apnActionBtnLabel.textContent : "";
    this.state.confirmationPrefix.apnSelectedAll = this.els.apnActionSelectedAllBtnLabel ? this.els.apnActionSelectedAllBtnLabel.textContent : "";
  }

  captureConfirmationPrefixes() {
    this.state.confirmationPrefix.accountNumber = this.els.confAccount?.textContent || "";
    this.state.confirmationPrefix.contractorName = this.els.confContractor?.textContent || "";
    this.state.confirmationPrefix.labelType = this.els.confLabelType?.textContent || "";
    this.state.confirmationPrefix.line1 = this.els.confLine1?.textContent || "";
    this.state.confirmationPrefix.line2 = this.els.confLine2?.textContent || "";
    this.state.confirmationPrefix.line3 = this.els.confLine3?.textContent || "";
    this.state.confirmationPrefix.line4 = this.els.confLine4?.textContent || "";
  }

  detectInitialStep() {
    const visible = this.els.sections?.find((/** @type {any} */ s) => !s.classList.contains("rs-hidden"));
    this.state.step = this.getSectionStepHandle(visible) || "private-label";
  }

  /** @param {Element | null | undefined} section */
  getSectionStepHandle(section) {
    if (!section) return "";
    const el = /** @type {HTMLElement} */ (section);
    for (const entry of this.SECTION_CLASS_TO_STEP) {
      const className = entry[0];
      const handle = entry[1];
      if (el.classList.contains(className)) return handle;
    }
    const fromAttr = el.getAttribute("data-step-handle") || el.dataset.stepHandle;
    if (!fromAttr) return "";
    const normalized = fromAttr.trim().replace(/^["']|["']$/g, "");
    return this.getAllowedSteps().includes(normalized) ? normalized : "";
  }

  /** @param {{ render?: boolean }} [opts] */
  applyStepFromUrl(opts = {}) {
    const { step, id, preview } = this.parseUrlState();
    let nextStep = this.state.step;

    if (preview) {
      nextStep = "private-label";
    } else if (id) {
      nextStep = this.resolveUrlStep(step, true);
    } else if (step) {
      nextStep = this.resolveUrlStep(step, false);
    }

    if (!nextStep) return nextStep;
    this.state.step = nextStep;
    if (opts.render) {
      this.renderSectionsAndTabs();
      this.renderBreadcrumb();
    }
    return nextStep;
  }

  bindEvents() {
    document.addEventListener("input", this.onInput);
    document.addEventListener("click", this.onClick);

    // capture: dropdown hidden input changes
    document.addEventListener("change", this.onChangeCapture, true);

    // bubble: status filter
    document.addEventListener("change", this.onChangeBubble);

    window.addEventListener("pageshow", this.onPageShow);
    window.addEventListener("popstate", this.onPopState);

    // condensed toggle direct handler
    this.els.condensedToggle?.addEventListener("change", (/** @type {any} */ e) => {
      this.setCondensedMode(!!e.target?.checked);
      this.drawCanvasFromState({ confirmation: false });
    });
  }

  // =========================================================
  // API
  // =========================================================
  /** @param {any} path @param {any} [opts] */
  async request(path, { method = "GET", body = null, auth = true } = {}) {
    const url = getPlAppBaseUrl() + path;
    const headers = /** @type {Record<string,string>} */ ({ "Content-Type": "application/json" });
    if (auth && this.state.token) headers.Authorization = `Bearer ${this.state.token}`;
    const controller = new AbortController();

    this._setBusy(true);
    try {
      const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : null, signal: controller.signal });
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (_) {}
        const err = /** @type {any} */ (new Error(`HTTP ${res.status} for ${path}: ${JSON.stringify(body)}`));
        err.status = res.status;
        err.body = body;
        throw err;
      }
      return res.json();
    } finally {
      this._setBusy(false);
    }
  }

  _setBusy(/** @type {boolean} */ on) {
    this._pendingRequests = Math.max(0, this._pendingRequests + (on ? 1 : -1));
    if (this._pendingRequests > 0) {
      if (!document.getElementById('rs-pl-topbar')) {
        const bar = document.createElement('div');
        bar.id = 'rs-pl-topbar';
        document.body.appendChild(bar);
      }
    } else {
      const bar = document.getElementById('rs-pl-topbar');
      if (bar) {
        bar.style.opacity = '0';
        setTimeout(() => { if (!this._pendingRequests) bar.remove(); }, 220);
      }
    }
  }

  async generateToken() {
    try {
      const currentScript = document.querySelector('script[data-rs-private-label]');
      const { useAppProxyToken } = currentScript.dataset;

      let data;
      if (useAppProxyToken === "true") {
        const res = await fetch(`/apps/plp/auth/token`);
        data = await res.json();
      } else {
        data = await this.request("/auth/token", {
          method: "POST",
          auth: false,
          body: /** @type {any} */ ({ customer_id: document.body.getAttribute("data-customer-id") || "" }),
        });
      }

      this.state.token = data?.token || "";
    } catch (err) {
      console.error("Error generating token:", err);
    }
  }

  /** @returns {boolean} */
  getFergusonFilteringEnabled() {
    return this.state.fergusonFilteringEnabled;
  }

  async fetchTypes() {
    if (this.state.selection.types.length) return;
    try {
      const data = await this.request("/privateLabelType/list");
      this.state.selection.types = data?.privateLabelTypes || [];
    } catch (err) {
      console.error("Error fetching types:", err);
      this.state.selection.types = [];
    }
  }

  /** Fetch private label types with automatic retry on failure or timeout. */
  async fetchTypesWithRetry() {
    if (this.state.selection.types.length) return;
    const maxRetries = 2;
    const timeoutMs = 5000;
    let attempts = 0;

    while (attempts <= maxRetries) {
      try {
        const data = await this._requestWithTimeout("/privateLabelType/list", timeoutMs);
        this.state.selection.types = data?.privateLabelTypes || [];
        return; // success
      } catch (err) {
        attempts++;
        console.warn(`[private-label] fetchTypes attempt ${attempts}/${maxRetries + 1} failed:`, err.message);
        if (attempts > maxRetries) {
          console.error("[private-label] Error fetching types after all retries:", err);
          this.state.selection.types = [];
          return;
        }
        // Brief backoff before retry
        await new Promise(r => setTimeout(r, 300 * attempts));
      }
    }
  }

  /** Make an authenticated request to the Gadget PLP app with a timeout. */
  async _requestWithTimeout(path, timeoutMs) {
    const url = getPlAppBaseUrl() + path;
    const headers = { "Content-Type": "application/json" };
    if (this.state.token) headers.Authorization = `Bearer ${this.state.token}`;

    this._setBusy(true);
    try {
      const controller = new AbortController();
      let cancelled = false;
      const timeoutId = setTimeout(() => {
        cancelled = true;
        controller.abort();
      }, timeoutMs);

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      // If our own timer fired just before the response arrived, treat it as a retryable error.
      // After clearTimeout, `cancelled` is authoritative — a late abort cannot flip it anymore.
      if (cancelled) {
        throw new Error(`Request to ${path} timed out after ${timeoutMs}ms`);
      }

      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (_) {}
        const err = new Error(`HTTP ${res.status} for ${path}: ${JSON.stringify(body)}`);
        err.status = res.status;
        err.body = body;
        throw err;
      }
      return res.json();
    } catch (err) {
      if (err.name === "AbortError") {
        throw new Error(`Request to ${path} timed out after ${timeoutMs}ms`);
      }
      throw err;
    } finally {
      this._setBusy(false);
    }
  }

  /** Show skeleton cards in the types wrapper while data is loading. */
  showTypesSkeletons() {
    if (!this.els.typesWrapper) return;
    this.els.typesWrapper.innerHTML = "";

    for (let i = 0; i < 2; i++) {
      const card = document.createElement("div");
      card.className = "rs-pl-skeleton-card";
      card.setAttribute("aria-hidden", "true");
      card.innerHTML =
        `<div class="rs-pl-skeleton-img"></div>` +
        `<div class="rs-pl-skeleton-text">` +
          `<div class="rs-pl-skeleton-line rs-pl-skeleton-title"></div>` +
          `<div class="rs-pl-skeleton-line rs-pl-skeleton-desc"></div>` +
        `</div>` +
        `<div class="rs-pl-skeleton-btn"></div>`;
      this.els.typesWrapper.appendChild(card);
    }
  }

  /** Hide skeletons and show an error state if no types loaded. */
  hideTypesSkeletons() {
    if (!this.els.typesWrapper) return;
    // Only clear if content is still skeletons (renderTypeCards will overwrite on success)
    const hasSkeletons = this.els.typesWrapper.querySelector(".rs-pl-skeleton-card");
    if (hasSkeletons) {
      this.els.typesWrapper.innerHTML = "";
    }
  }

  /**
   * Keep retrying silently in the background while skeletons stay visible.
   * Never shows an error message — the loader just keeps trying until it succeeds.
   */
  startBackgroundRetries() {
    if (!this.els.typesWrapper) return;

    const maxSilentRetries = 5;
    let attempt = 0;

    const tryAgain = async () => {
      attempt++;
      if (attempt > maxSilentRetries) {
        // Last resort: fall back to empty list gracefully;
        // skeletons disappear and renderTypeCards renders nothing,
        // so the step just shows an empty card area — no error text.
        this.state.selection.types = [];
        this.hideTypesSkeletons();
        this.renderTypeCards();
        return;
      }
      try {
        const data = await this._requestWithTimeout("/privateLabelType/list", 8000);
        this.state.selection.types = data?.privateLabelTypes || [];
        this.hideTypesSkeletons();
        this.renderTypeCards();
      } catch (err) {
        console.warn(`[private-label] bg retry ${attempt}/${maxSilentRetries} failed:`, err.message);
        // Stagger: wait ~2s then try again, keeping skeletons visible
        setTimeout(tryAgain, 2000);
      }
    };

    // Kick off first delayed retry (initial attempt already ran in fetchTypesWithRetry)
    setTimeout(tryAgain, 1500);
  }

  /** @param {any} typeId */
  async fetchProducts(typeId) {
    if (!typeId) return;
    if (this.state.selection.productsTypeId === typeId) return;
    try {
      const data = await this.request("/privateLabelImage/list", {
        method: "POST",
        body: /** @type {any} */ ({
          privateLabelTypeId: typeId,
          companyExternalId: this.getAccountId() || "",
          companyName: this.getCompanyName() || "",
        }),
      });
      this.state.selection.products = data?.privateLabelImages || [];
      if (typeof data?.fergusonFilteringEnabled === "boolean") this.state.fergusonFilteringEnabled = data.fergusonFilteringEnabled;
      this.state.selection.productsTypeId = typeId;
    } catch (err) {
      console.error("Error fetching products:", err);
      this.state.selection.products = [];
      this.state.selection.productsTypeId = "";
    }
  }

  async fetchPartSetups(/** @type {string} */ typeId) {
    if (!typeId) return;
    const accountKey = this.state.selection.accountNumber || this.getAccountId();
    const cacheKey = `${typeId}:${accountKey}`;
    if (this.state.selection.partSetupsKey === cacheKey) return;
    try {
      const params = new URLSearchParams({
        privateLabelTypeId: typeId,
        companyExternalId: this.getAccountId() || "",
        salesArea: this.getSalesArea(),
        stateCode: this.getStateCode(),
        companyName: this.getCompanyName() || "",
      });
      const data = await this.request(`/itemMaster/list?${params}`);
      this.state.selection.partSetups = data?.privateLabelPartSetups || [];
      this.state.selection.partSetupsKey = cacheKey;
    } catch (err) {
      console.error("Error fetching part setups:", err);
      this.state.selection.partSetups = [];
      this.state.selection.partSetupsKey = "";
    }
  }

  /** @param {{ searchText?: string; plcaId?: string }} [params] */
  buildListUrl({ searchText, plcaId } = {}) {
    const params = new URLSearchParams();
    params.set("accountId", this.getAccountId() || "");
    params.set("salesOrganization", this.getSalesOrg());
    params.set("distributionChannel", this.getDistChannel());
    params.set("page", plcaId ? "1" : String(this.state.list.page));
    params.set("limit", plcaId ? "1" : String(this.state.list.limit));
    if (searchText) params.set("searchText", searchText);
    if (plcaId) params.set("plcaId", plcaId);

    if (!plcaId && this.state.list.filterStatus && this.state.list.filterStatus !== "all") {
      params.set("status", this.state.list.filterStatus);
    }

    if (!plcaId && this.state.list.filterPartNumber && this.state.list.filterPartNumber !== "") {
      params.set("partNumber", this.state.list.filterPartNumber);
    }

    // Use cursor for fast-path pagination when no text search or filters are active
    const useCursor = !searchText && !plcaId &&
      (!this.state.list.filterStatus || this.state.list.filterStatus === "all") &&
      !this.state.list.filterPartNumber;
    if (useCursor) {
      const cursorForPage = this.state.list.cursorStack[this.state.list.page - 2];
      if (cursorForPage) params.set("after", cursorForPage);
    }

    return `/privateLabel/search?${params.toString()}`;
  }

  buildCustomerPartNumberListUrl() {
    const params = new URLSearchParams();
    params.set("plcaId", this.state.edit.labelId);
    params.set("accountId", this.getAccountId() || "");
    params.set("page", String(this.state.cpnView.page));
    params.set("limit", String(this.state.cpnView.limit));
    return `/privateLabelPartNumberAssignment/list?${params.toString()}`;
  }

  async fetchCustomerPartNumberList() {
    try {
      return await this.request(this.buildCustomerPartNumberListUrl());
    } catch (err) {
      console.error("Error fetching customer part number list:", err);
      return null;
    }
  };

  async fetchList({ searchText = "", plcaId = "" } = {}) {
    try {
      return await this.request(this.buildListUrl({ searchText, plcaId }));
    } catch (err) {
      console.error("Error fetching private label list:", err);
      return null;
    }
  }

  buildCountUrl() {
    const params = new URLSearchParams();
    params.set("accountId", this.getAccountId() || "");
    params.set("salesOrganization", this.getSalesOrg());
    params.set("distributionChannel", this.getDistChannel());
    if (this.state.list.filterStatus && this.state.list.filterStatus !== "all") {
      params.set("status", this.state.list.filterStatus);
    }
    if (this.state.list.filterPartNumber) {
      params.set("partNumber", this.state.list.filterPartNumber);
    }
    return `/privateLabel/search-count?${params.toString()}`;
  }

  async fetchCount() {
    try {
      const result = await this.request(this.buildCountUrl());
      return typeof result?.total === "number" ? result.total : null;
    } catch (err) {
      console.error("Error fetching private label count:", err);
      return null;
    }
  }

  getAllowedSteps() {
    return Object.keys(this.STEP_LABELS);
  }

  /** @param {any} step */
  cleanStep(step) {
    const value = (step || "").trim();
    return this.getAllowedSteps().includes(value) ? value : "";
  }

  parseUrlState() {
    const url = new URL(window.location.href);
    return {
      step: this.cleanStep(url.searchParams.get("step")),
      id: (url.searchParams.get("id") || "").trim(),
      preview: (url.searchParams.get("preview") || "").trim(),
    };
  }

  /** @param {any} step */
  resolveUrlStep(step, hasId = false) {
    const clean = this.cleanStep(step);

    if (hasId) {
      return clean || "label-confirmation";
    }

    if (clean === "label-selection") return "label-selection";
    if (clean === "private-label") return "private-label";
    if (clean === "assign-part-number") return "assign-part-number";
    if (clean === "customer-part-number") return "customer-part-number";
    if (clean === "label-creation" || clean === "label-confirmation") return "label-selection";

    return "private-label";
  }

  /** @param {{ step?: any; replace?: boolean }} [opts] */
  syncUrl({ step = this.state.step, replace = false } = {}) {
    const url = new URL(window.location.href);
    const nextStep = this.cleanStep(step) || "private-label";

    url.searchParams.set("step", nextStep);

    const shouldKeepId =
      !!this.state.edit.labelId &&
      (this.state.edit.enabled ||
        nextStep === "assign-part-number" ||
        nextStep === "customer-part-number" ||
        nextStep === "label-confirmation" ||
        nextStep === "label-creation");

    if (shouldKeepId) {
      url.searchParams.set("id", this.state.edit.labelId);
    } else {
      url.searchParams.delete("id");
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextUrl === currentUrl) return;

    if (replace) {
      window.history.replaceState({}, "", nextUrl);
    } else {
      window.history.pushState({}, "", nextUrl);
    }
  }

  /** @param {any} labelId */
  getRowByLabelId(labelId) {
    if (!labelId) return null;
    return document.querySelector(`.rs-table-body__row[data-label-id="${String(labelId)}"]`);
  }

  /** @param {any} labelId */
  async findAndRenderRowByLabelId(labelId) {
    if (!labelId) return null;

    const list = await this.fetchList({ plcaId: String(labelId) });
    if (!Array.isArray(list?.data) || !list.data.length) return null;

    this.renderList(list);
    return this.getRowByLabelId(labelId);
  }

  /** @param {any} row */
  async hydrateEditStateFromRow(row) {
    if (!row) return false;

    this.enterEditModeFromRow(row);

    if (!this.state.selection.typeId || !this.state.selection.typeHandle) {
      await this.fetchTypes();
      const typeTitle = (row.dataset.typeTitle || "").trim();
      const _types = /** @type {any[]} */ (this.state.selection.types);
      const match = typeTitle
        ? _types.find((t) => (t?.title || "").trim() === typeTitle)
        : _types.find((t) => String(t?.id || "") === String(this.state.selection.typeId));
      const _m = /** @type {any} */ (match);
      if (!this.state.selection.typeId) this.state.selection.typeId = _m?.id || "";
      this.state.selection.typeHandle = this._getLabelTypeCategory(_m?.title) || _m?.folder || this.state.selection.typeHandle;
    }

    if (this.state.selection.typeId) {
      await Promise.all([
        this.fetchProducts(this.state.selection.typeId),
        this.fetchPartSetups(this.state.selection.typeId),
      ]);
    }

    this.fillCreationInputsFromState();

    const _editScope601 = ".rs-private-label-creation-section";
    const defaultSelectedId = this.updateProductDropdownOptions(this.state.selection.products, _editScope601);
    const editImageId = this.state.edit.previewOnImageId;
    const _products601 = /** @type {any[]} */ (this.state.selection.products);
    const editProduct = editImageId
      ? _products601.find(function(p) { return /** @type {any} */ (_img(p)) && String(/** @type {any} */ (_img(p)).id) === String(editImageId); })
      : null;
    const selectedProductId = (editProduct ? String(/** @type {any} */ (editProduct).id) : "") || defaultSelectedId || "";

    if (selectedProductId) {
      const ok = this.selectProductInDropdown(selectedProductId, _editScope601);
      const productIdToApply = ok ? selectedProductId : defaultSelectedId;
      if (productIdToApply) {
        this.applyProductToCanvas(productIdToApply);
      }
    }

    if (this.els.condensedToggle) {
      this.els.condensedToggle.checked = !!this.state.canvas.condensedFont;
    }

    this.setCondensedMode(!!this.state.canvas.condensedFont);
    this.drawCanvasFromState({ confirmation: false });

    return true;
  }

  /** @param {any} labelId */
  async restoreLabelById(labelId) {
    let row = this.getRowByLabelId(labelId);

    if (!row) {
      row = await this.findAndRenderRowByLabelId(labelId);
    }

    if (!row) return false;

    return this.hydrateEditStateFromRow(row);
  }

  async restoreFromUrl() {
    const { step, id, preview } = this.parseUrlState();

    if (!step && !id && !preview) {
      this.syncUrl({ step: this.state.step || "private-label", replace: true });
      return;
    }

    if (preview) {
      await this.setStep("private-label", { syncUrl: false });
      const row = this.getRowByLabelId(preview) || await this.findAndRenderRowByLabelId(preview);
      if (row) {
        const opened = await this._openListingPreviewModal(row);
        if (opened) return;
      }

      this._showPreviewDeepLinkError("The requested private label preview is unavailable.");
      this._removePreviewUrlParam();
      return;
    }

    if (id) {
      const targetStep = this.resolveUrlStep(step, true);
      this.state.step = targetStep;
      this.renderSectionsAndTabs();
      this.renderBreadcrumb();

      // Paint the CPN table spinner immediately, before restoreLabelById's chain of fetches
      // (fetchTypes/fetchProducts/fetchPartSetups) runs — those fetches make the topbar
      // flicker off between requests, and the table would otherwise show nothing during
      // that whole window since renderCustomerPartNumberList() only ran after it finished.
      if (targetStep === "customer-part-number") {
        if (this.els.cpnTableBody) {
          this.els.cpnTableBody.innerHTML =
            `<div class="rs-pl-list-loading">` +
            `<span class="rs-loading-spinner" style="width:32px;height:32px;margin:0;" aria-hidden="true"></span></div>`;
        }
        this.els.cpnPreviewImageWrapper?.classList.remove("rs-pl-cpn-loaded");
        this.els.cpnDescriptionWrapper?.classList.remove("rs-pl-cpn-loaded");
      }

      const restored = await this.restoreLabelById(id);

      if (restored) {
        if (targetStep === "assign-part-number") {
          this.renderAssignPartNumber();
          await this.setStep("assign-part-number", { edit: true, syncUrl: false });
        } else if (targetStep === "customer-part-number") {
          // Paint the description from freshly-hydrated state before the (slower) assignments
          // fetch resolves, so the section never shows its static placeholder sample content.
          this.renderCustomerPartNumberDescription();
          await this.renderCustomerPartNumberList();
          await this.setStep("customer-part-number", { edit: true, syncUrl: false });
        } else {
          await this.setStep(targetStep, { edit: true, syncUrl: false });
        }

        this.syncUrl({ step: targetStep, replace: true });
        return;
      }
    }

    const fallbackStep = this.resolveUrlStep(step, !!id);
    await this.setStep(fallbackStep, { syncUrl: false });
    this.syncUrl({ step: fallbackStep, replace: true });
  }

  // =========================================================
  // NAV + RENDER
  // =========================================================
  /** @param {any} step @param {any} [opts] */
  async setStep(step, opts = {}) {
    if (!step) return;
    this._clearConfirmationError();
    this._clearModalError();
    this.state.step = step;
    this.renderSectionsAndTabs();
    this.renderBreadcrumb();
    await this.renderStepContent(opts);
    if (opts.syncUrl !== false) {
      this.syncUrl({ step, replace: !!opts.replaceHistory });
    }
    window.scrollTo(0, 0);
  }

  /** @param {any} [opts] */
  async renderStepContent(opts = {}) {
    if (this.state.step === "label-selection") {
      this.showTypesSkeletons();
      await this.fetchTypesWithRetry();
      this.hideTypesSkeletons();
      if (!this.state.selection.types.length) {
        // Don't show an error — keep skeletons visible and retry silently in bg
        this.hideTypesSkeletons();
        this.showTypesSkeletons();
        this.startBackgroundRetries();
        return;
      }
      this.renderTypeCards();
    }

    if (this.state.step === "label-creation") {
      if (opts.edit || this.state.edit.enabled) this.fillCreationInputsFromState();

      // show/hide condensed control based on type
      this.els.condensedWrap?.classList.toggle(
        "rs-hidden",
        !LABEL_TYPE[this.state.selection.typeHandle]?.condensed
      );

      // line4 only for t-series-digital
      const line4Wrapper = this.els.canvasInputs?.[3]?.closest?.(".rs-input__content");
      line4Wrapper?.classList.toggle(
        "rs-hidden",
        LABEL_TYPE[this.state.selection.typeHandle]?.maxLines !== 4
      );

      // build dropdown options (defaults to first option)
      const _creationScope = ".rs-private-label-creation-section";
      const defaultSelectedId = this.updateProductDropdownOptions(this.state.selection.products, _creationScope);

      // EDIT MODE: force-select previewOnImageId if available
      const _editImgId718 = this.state.edit.enabled ? this.state.edit.previewOnImageId : "";
      const _products718 = /** @type {any[]} */ (this.state.selection.products);
      const editProductId = _editImgId718
        ? (/** @type {any} */ (_products718.find(function(p) { return /** @type {any} */ (_img(p)) && String(/** @type {any} */ (_img(p)).id) === String(_editImgId718); })) || {}).id || ""
        : "";
      if (editProductId) {
        const ok = this.selectProductInDropdown(editProductId, _creationScope);
        if (ok) this.applyProductToCanvas(editProductId);
        else if (defaultSelectedId) this.applyProductToCanvas(defaultSelectedId);
      } else if (defaultSelectedId) {
        this.applyProductToCanvas(defaultSelectedId);
      }

      // keep condensed toggle consistent
      if (this.els.condensedToggle) this.els.condensedToggle.checked = !!this.state.canvas.condensedFont;
      this.setCondensedMode(!!this.state.canvas.condensedFont);

      this.drawCanvasFromState({ confirmation: false });
    }

    if (this.state.step === "label-confirmation") {
      this.mountConfirmationCanvas();

      // Populate the confirmation "Preview On" dropdown and restore the current selection.
      // Scoped explicitly to the confirmation section — relying on .rs-hidden visibility
      // alone (the getDropdownRoot fallback) can resolve to the label-creation step's
      // dropdown instance instead during a step transition, applying the wrong product's
      // label-area geometry while this step's text content is drawn, causing font size /
      // centering to look wrong for reasons unrelated to the actual rendering math.
      const _confScope = ".rs-private-label-confirmation-section";
      const _confDefaultId = this.updateProductDropdownOptions(this.state.selection.products, _confScope);
      const _confCurImgId = this.state.canvas.previewOnImageId;
      const _confProds = /** @type {any[]} */ (this.state.selection.products);
      const _confCurProd = _confCurImgId
        ? _confProds.find(function(p) { return /** @type {any} */ (_img(p)) && String(/** @type {any} */ (_img(p)).id) === String(_confCurImgId); })
        : null;
      const _confSelId = (_confCurProd ? String(_confCurProd.id) : "") || _confDefaultId;
      if (_confSelId) this.selectProductInDropdown(_confSelId, _confScope);

      this.drawCanvasFromState({ confirmation: true });
      this.renderConfirmationText();

      // In edit mode, hide the agree section (terms + checkbox + their divider) but keep buttons visible
      document.querySelectorAll(".rs-pl-agree-checkbox").forEach((cbEl) => {
        const _agreeDetails = cbEl.closest(".rs-group-custom");
        if (!_agreeDetails) return;
        _agreeDetails.classList.toggle("rs-hidden", !!this.state.edit.enabled);
        const _divider = _agreeDetails.nextElementSibling;
        if (_divider) _divider.classList.toggle("rs-hidden", !!this.state.edit.enabled);
      });
    }

    if (this.state.step === "private-label") {
      await this.loadAndRenderList();
      // Pre-fetch label types in background so the selection step feels instant
      this.fetchTypes().catch(() => {});
    }
  }

  renderSectionsAndTabs() {
    this.els.sections = Array.from(document.querySelectorAll(this.S.sections));
    this.els.tabButtons = Array.from(document.querySelectorAll(this.S.tabButtons));
    this.els.tabSection = document.querySelector(this.S.tabSection);

    const step = this.cleanStep(this.state.step) || this.state.step;
    if (!step) return;

    let matched = 0;
    for (const entry of this.SECTION_CLASS_TO_STEP) {
      const className = entry[0];
      const handle = entry[1];
      const sections = document.querySelectorAll(`.rs-pl-sections.${className}`);
      if (!sections.length) continue;
      matched++;
      const show = handle === step;
      sections.forEach((/** @type {Element} */ section) => {
        section.classList.toggle("rs-hidden", !show);
      });
    }

    this.els.sections.forEach((/** @type {any} */ section) => {
      const hasKnownClass = this.SECTION_CLASS_TO_STEP.some((entry) =>
        section.classList.contains(entry[0])
      );
      if (hasKnownClass) return;
      const handle = this.getSectionStepHandle(section);
      if (!handle) return;
      matched++;
      section.classList.toggle("rs-hidden", handle !== step);
    });
    if (!matched) return;

    if (step === "private-label" || step === "assign-part-number" || step === "customer-part-number") {
      this.els.tabSection?.classList.add("rs-hidden");
    } else {
      this.els.tabSection?.classList.remove("rs-hidden");
    }

    if (!this.els.tabButtons.length) return;

    this.els.tabButtons.forEach((/** @type {any} */ b) => b.classList.remove("rs-active", "rs-selected"));
    const idx = this.els.tabButtons.findIndex((/** @type {any} */ b) => b.getAttribute("data-tab") === this.state.step);
    if (idx >= 0) {
      this.els.tabButtons[idx]?.classList.add("rs-active");
      this.els.tabButtons.slice(0, idx).forEach((/** @type {any} */ b) => b.classList.add("rs-selected"));
    }
  }

  renderBreadcrumb() {
    if (!this.els.breadCrumb || !this.state.step) return;

    const list = this.els.breadCrumb.querySelector(this.S.breadCrumbList);
    if (!list) return;

    const _e = /** @type {any} */ (this.els);

    if (this.state.step === 'label-selection' || this.state.step === 'label-creation' || this.state.step === 'label-confirmation') {
      const ls  = _e.breadCrumbLinkStyle || '';
      const ss  = _e.breadCrumbSepStyle  || '';
      const sep = _e.breadCrumbSepText   || '/';
      const item = (/** @type {string} */ href, /** @type {string} */ text) =>
        `<li class="rs-breadcrumb__item"><a href="${href}" class="rs-breadcrumb__link" style="${ls}">${text}</a>` +
        `<span class="rs-breadcrumb__separator" style="${ss}">${sep}</span></li>`;
      const previousPages =
        (typeof window.__t === "function" && window.__translations?.private_label_previous_pages)
          ? window.__t("private_label_previous_pages")
          : "Previous Pages";
      const privateLabelPrograms =
        (typeof window.__t === "function" && window.__translations?.private_label_programs)
          ? window.__t("private_label_programs")
          : "Private Label Programs";
      list.innerHTML =
        item('javascript:history.back()', previousPages) +
        item(`${window.location.pathname}?step=private-label`, privateLabelPrograms) +
        `<li class="rs-breadcrumb__item rs-breadcrumb__item--current" aria-current="page">${this.STEP_LABELS['label-selection']}</li>`;
    } else {
      list.innerHTML = _e.breadCrumbOriginalHTML || '';
      const current = list.querySelector(this.S.breadCrumbCurrent);
      if (current) current.textContent = this.STEP_LABELS[this.state.step] || this.state.step;
    }
  }

  renderAssignPartNumber() {
    this.els.apnAccountNumber && (this.els.apnAccountNumber.textContent = this.state.selection.accountNumber || "");
    this.els.apnSalesArea && (this.els.apnSalesArea.textContent = this.getSalesAreaDisplay());
    const apnImgUrl = this.state.form.fullPreviewImage || this.state.form.previewImage;
    if (this.els.apnPreviewImageWrapper) {
      this.els.apnPreviewImageWrapper.innerHTML = "";
      if (apnImgUrl) {
        let img = document.createElement("img");
        img.src = apnImgUrl;
        img.alt = "Preview Image";
        img.className = "rs-pl-apn-preview-image";
        const _apnLineCount = [this.state.form.line1, this.state.form.line2, this.state.form.line3, this.state.form.line4].filter((s) => s?.trim()).length || 1;
        const _apnOverhead = this.state.selection.typeHandle === "non-t-series" ? LABEL_RENDER.JPEG_OVERHEAD_PX_NON_T : LABEL_RENDER.JPEG_OVERHEAD_PX_T;
        const _apnH = Math.round((_apnLineCount * LABEL_RENDER.JPEG_PX_PER_LINE + _apnOverhead) / 4);
        img.style.cssText = this.state.edit.isLogo ? "max-width:160px;width:auto;height:auto;display:block;" : `height:${_apnH}px;width:auto;max-width:160px;display:block;`;
        this.els.apnPreviewImageWrapper.appendChild(img);
      } else {
        const missingEl = document.createElement("p");
        missingEl.className = "rs-pl-apn-preview-missing";
        missingEl.style.cssText = "color:#6B6B6B;font-size:12px;margin:0;";
        missingEl.textContent = "Preview image unavailable for this label.";
        this.els.apnPreviewImageWrapper.appendChild(missingEl);
      }
    }
    this.els.apnEmail && (this.els.apnEmail.value = document.body.getAttribute("data-customer-email") || "");
    this.els.apnLine1 && (this.els.apnLine1.textContent = this.state.form.line1 || "");
    this.els.apnLine2 && (this.els.apnLine2.textContent = this.state.form.line2 || "");
    if (this.state.form.line3 && this.els.apnLine3) {
      this.els.apnLine3.parentElement?.classList.remove("rs-hidden");
      this.els.apnLine3.textContent = this.state.form.line3;
    }
    if (this.state.form.line4 && this.els.apnLine4) {
      this.els.apnLine4.parentElement?.classList.remove("rs-hidden");
      this.els.apnLine4.textContent = this.state.form.line4;
    }
    let apnPartNumberSelect = /** @type {any} */ (this.templates.apnRow).querySelector(this.S.apnResideoPartNumber);
    if (!apnPartNumberSelect) return;
    let listWrapper = apnPartNumberSelect.querySelector(".rs-custom-select__menu");
    if (!listWrapper) return;
    listWrapper.innerHTML = "";
    const _products = /** @type {any[]} */ (this.state.selection.products);
    const _assignedForLabel = new Set(this.state.edit.assignedBasePartNumbers || []);
    const usePartSetups = this.state.selection.partSetups.length > 0;
    if (usePartSetups) {
      /** @type {any[]} */ (this.state.selection.partSetups).forEach((setup) => {
        let id = setup?.id || null;
        let partName = setup?.partNumber || null;
        let description = setup?.description || "";
        if (id && partName && listWrapper && !_assignedForLabel.has(partName)) {
          let li = document.createElement("li");
          li.className = "rs-custom-select__option";
          li.setAttribute("data-part-setup-id", id);
          // Store the matching product ID as a fallback for renderLabelPositionOptions
          // in case the products-only path is active (no partSetups loaded).
          const _matchProd = _products.find(
            (p) => /** @type {any} */ (p).partName === partName || /** @type {any} */ (p).partNumber === partName
          );
          if (_matchProd) li.setAttribute("data-product-id", String(_matchProd.id));
          li.setAttribute("role", "option");
          li.setAttribute("data-description", description);
          li.dataset.value = partName;
          li.setAttribute("aria-label", partName);
          let label = document.createElement("span");
          label.className = "rs-custom-select__option-label";
          label.innerHTML = description
            ? `${partName}<span class="rs-part-number-description">, ${description}</span>`
            : partName;
          li.appendChild(label);
          listWrapper.appendChild(li);
        }
      });
    } else {
      /** @type {any[]} */ (this.state.selection.products).forEach((product) => {
        let id = product?.id || null;
        let partName = /** @type {any} */ (_img(product))?.partName || product?.partNumber || null;
        if (id && partName && listWrapper && !_assignedForLabel.has(partName)) {
          let li = document.createElement("li");
          li.className = "rs-custom-select__option";
          li.setAttribute("data-product-id", id);
          li.setAttribute("role", "option");
          li.setAttribute("data-description", product?.description || "");
          li.dataset.value = partName;
          li.setAttribute("aria-label", partName);
          let label = document.createElement("span");
          label.className = "rs-custom-select__option-label";
          const _desc = product?.description || "";
          label.innerHTML = _desc
            ? `${partName}<span class="rs-part-number-description">, ${_desc}</span>`
            : partName;
          li.appendChild(label);
          listWrapper.appendChild(li);
        }
      });
    }
    this.duplicateRows(4, true);
    this._syncApnPartNumberColWidth();
    this._initApnPnDropdownFloat();

    const submitBtn = /** @type {HTMLButtonElement|null} */ (document.querySelector('.rs-pl-apn-submit-btn'));
    if (!this.state.selection.partSetups.length && !this.state.selection.products.length) {
      this._showApnFormError('No Resideo part numbers are available for this account. Please contact support.');
      if (submitBtn) submitBtn.disabled = true;
    } else {
      this._clearApnFormError();
    }
  };

  /** Prevent the APN table from horizontally scrolling while a dropdown is open,
      so the dropdown menu can't detach from its trigger. Temporarily removes the
      overflow-x:auto scrollport (which clips and scrolls the dropdown away) by
      setting overflow-x to visible — eliminating the scrollbar entirely.
      When the dropdown closes, the original overflow is restored. */
  _initApnPnDropdownFloat() {
    if (this._apnPnDropdownObserver) {
      this._apnPnDropdownObserver.disconnect();
      this._apnPnDropdownObserver = null;
    }
    const selects = this.els.apnTableBody?.querySelectorAll('.rs-pl-apn-resideo-part-number-select .rs-custom-select, .rs-pl-apn-label-position-select .rs-custom-select');
    if (!selects?.length) return;

    const tableContainer = /** @type {HTMLElement|null} */ (this.els.apnTableBody?.closest('.rs-table-new'));
    if (!tableContainer) return;

    this._apnPnDropdownObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const select = /** @type {HTMLElement} */ (mutation.target);
        if (!select.classList.contains('open')) continue;
        // Remove the scrollport so the dropdown can't scroll away and no scrollbar shows
        tableContainer.style.overflowX = 'visible';
        const menu = /** @type {HTMLElement} */ (select.querySelector('.rs-custom-select__menu'));
        if (menu) menu.style.zIndex = '9999';
      }
      const anyOpen = tableContainer.querySelector('.rs-custom-select.open');
      if (!anyOpen) {
        // Restore normal horizontal scrolling
        tableContainer.style.overflowX = '';
      }
    });

    selects.forEach((/** @type {any} */ select) => {
      this._apnPnDropdownObserver?.observe(select, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /** @param {any} partNumberId @param {any} row */
  renderLabelPositionOptions(partNumberId, row) {
    if (!partNumberId || !row) return;
    let currentSelect = row.querySelector(".rs-pl-apn-label-position-select");
    if (!currentSelect) return;
    let listWrapper = currentSelect.querySelector(".rs-custom-select__menu");
    if (!listWrapper) return;
    listWrapper.innerHTML = "";
    const _partSetups = /** @type {any[]} */ (this.state.selection.partSetups);
    const _products = /** @type {any[]} */ (this.state.selection.products);
    // partNumberId is the part-setup id whenever one exists (see the click handler's
    // dataset.partSetupId || dataset.productId preference) — check partSetups first so this
    // doesn't false-match a PrivateLabelImage that happens to share the same numeric id.
    let findEntry =
      _partSetups.find((s) => String(s.id) === String(partNumberId)) ||
      _products.find((p) => String(p.id) === String(partNumberId));
    if (!findEntry) return;
    // If the resolved entry has no labelPositions (e.g. it was a PrivateLabelImage),
    // cross-reference via part number — partSetups carry labelPositions.
    if (!/** @type {any} */ (findEntry)?.labelPositions?.edges?.length) {
      const partNum = /** @type {any} */ (findEntry).partNumber || /** @type {any} */ (findEntry).partName || "";
      const matchingSetup = _partSetups.find(
        (s) => String(/** @type {any} */ (s).partNumber) === String(partNum) || String(/** @type {any} */ (s).partName) === String(partNum)
      );
      if (/** @type {any} */ (matchingSetup)?.labelPositions?.edges?.length) {
        findEntry = matchingSetup;
      } else {
        const matchingProduct = _products.find(
          (p) => /** @type {any} */ (p).partName === partNum || /** @type {any} */ (p).partNumber === partNum
        );
        if (matchingProduct) findEntry = matchingProduct;
      }
    }
    const _rawPositions = /** @type {any[]} */ (/** @type {any} */ (findEntry)?.labelPositions?.edges || []);
    const seen = new Set();
    const positions = _rawPositions.filter((pos) => {
      const id = pos?.node?.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (!positions.length) return;
    positions.forEach((pos) => {
      let id = pos.node?.id || null;
      let name = pos.node?.name || null;
      let li = document.createElement("li");
      li.className = "rs-custom-select__option";
      li.setAttribute("role", "option");
      li.setAttribute("data-position-id", id);
      li.dataset.value = id;
      li.setAttribute("aria-label", name);
      let label = document.createElement("span");
      label.className = "rs-custom-select__option-label";
      label.textContent = name;
      li.appendChild(label);
      listWrapper.appendChild(li);
    });
    currentSelect.classList.remove("rs-hidden");
    const toggleBtn = /** @type {HTMLButtonElement|null} */ (currentSelect.querySelector(".rs-custom-select__button"));
    const icon = /** @type {HTMLElement|null} */ (currentSelect.querySelector(".rs-custom-select__icon"));
    // Auto-select when exactly one position is available — matches admin-side behavior.
    // Hide the chevron and disable the button so it renders as static text, not a dropdown.
    if (positions.length === 1) {
      const posId = /** @type {any} */ (positions[0]).node?.id || null;
      const posName = /** @type {any} */ (positions[0]).node?.name || null;
      const input = currentSelect.querySelector('input[name="apn_label_position"]');
      if (input) input.value = String(posId || "");
      const valueEl = currentSelect.querySelector(".rs-custom-select__value");
      if (valueEl) valueEl.textContent = posName || "";
      const singleLi = listWrapper.querySelector("[data-position-id]");
      if (singleLi) singleLi.setAttribute("aria-selected", "true");
      if (icon) icon.classList.add("rs-hidden");
      if (toggleBtn) { toggleBtn.disabled = true; toggleBtn.style.pointerEvents = "none"; }
      this._updateApnRowPreviewBtn(row);
    } else {
      if (icon) icon.classList.remove("rs-hidden");
      if (toggleBtn) { toggleBtn.disabled = false; toggleBtn.style.pointerEvents = ""; }
    }
  }

  /** Synchronize all Resideo Part Number column widths to the widest value so downstream columns align across rows. */
  _syncApnPartNumberColWidth() {
    const body = /** @type {HTMLElement|null} */ (this.els.apnTableBody);
    if (!body) return;
    const section = /** @type {HTMLElement|null} */ (body.closest('.rs-pl-apn-table-section'));
    if (!section) return;
    section.style.removeProperty('--rs-apn-pn-col-w');
    requestAnimationFrame(() => {
      const maxW = Math.max(
        0,
        ...Array.from(body.querySelectorAll('.rs-pl-apn-resideo-part-number-select'))
          .map((el) => /** @type {HTMLElement} */ (el.closest('.rs-group-custom'))?.offsetWidth || 0)
      );
      if (maxW > 0) section.style.setProperty('--rs-apn-pn-col-w', `${maxW}px`);
    });
  }

  /** Hide part setup options already selected in other rows or already assigned to this label. */
  refreshPartSetupVisibility() {
    const assignedByLabel = new Set(this.state.edit.assignedBasePartNumbers || []);
    const rows = Array.from(/** @type {any} */ (this.els).apnTableBody?.querySelectorAll(".rs-table-body__row") || []);
    const selectedByRow = /** @type {string[]} */ (rows.map((r) => {
      const inp = /** @type {HTMLElement} */ (r).querySelector('input[name="apn_resideo_part_number"]');
      return (/** @type {any} */ (inp)?.value || "").trim();
    }));
    rows.forEach((row, rowIndex) => {
      const menu = /** @type {HTMLElement} */ (row).querySelector(".rs-pl-apn-resideo-part-number-select .rs-custom-select__menu");
      if (!menu) return;
      const selectedInOtherRows = new Set(
        selectedByRow.filter((v, i) => i !== rowIndex && v),
      );
      menu.querySelectorAll(".rs-custom-select__option").forEach((opt) => {
        const el = /** @type {HTMLElement} */ (opt);
        const partNumber = (el.dataset.value || "").trim();
        el.classList.toggle("rs-hidden", selectedInOtherRows.has(partNumber) || assignedByLabel.has(partNumber));
      });
    });
  }

  /** Show/hide the preview button for an APN row based on all 3 fields being filled. */
  /** @param {any} row */
  _updateApnRowPreviewBtn(row) {
    if (!row) return;
    const partNumber = row.querySelector('input[name="apn_resideo_part_number"]')?.value?.trim();
    const customerPart = row.querySelector('input[name="apn_customer_part_number"]')?.value?.trim();
    const labelPosition = row.querySelector('input[name="apn_label_position"]')?.value?.trim();
    const previewBtn = row.querySelector('.rs-pl-apn-preview-btn');
    if (!previewBtn) return;
    const lpSelect = row.querySelector('.rs-pl-apn-label-position-select');
    const lpNotAvailable = lpSelect && lpSelect.classList.contains('rs-hidden');
    if (partNumber && customerPart && (lpNotAvailable || labelPosition)) {
      previewBtn.classList.remove('rs-hidden');
    } else {
      previewBtn.classList.add('rs-hidden');
    }
  }

  /** @param {any} email */
  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /** Disable the APN submit button if any basic inline validation errors exist (max length, email format). */
  _updateApnSubmitBtnState() {
    const btn = /** @type {HTMLButtonElement|null} */ (document.querySelector('.rs-pl-apn-submit-btn'));
    if (!btn) return;
    const section = document.querySelector('.rs-pl-apn-table-section');
    if (!section) return;

    // Inline field errors (e.g. CPN max length, row-level backend errors)
    if (section.querySelector('.rs-error')) {
      btn.disabled = true;
      return;
    }

    // Label position required only when options are available; skip if select is hidden or has no options
    const apnRows = Array.from(section.querySelectorAll('.rs-table-body__row'));
    for (const row of apnRows) {
      const lpSelect = row.querySelector('.rs-pl-apn-label-position-select');
      if (lpSelect && !lpSelect.classList.contains('rs-hidden')) {
        const lpInput = /** @type {HTMLInputElement|null} */ (lpSelect.querySelector('input[name="apn_label_position"]'));
        const hasOptions = lpSelect.querySelector('.rs-custom-select__option');
        if (hasOptions && !lpInput?.value?.trim()) { btn.disabled = true; return; }
      }
    }

    // Second email format (if filled and invalid)
    const secondEmailEl = this.els.apnSecondEmail;
    if (secondEmailEl) {
      const val = secondEmailEl.value.trim();
      if (val && !this._isValidEmail(val)) {
        btn.disabled = true;
        return;
      }
    }

    btn.disabled = false;
  }

  /** Show a backend validation error message on a specific row field. */
  /** @param {any} row @param {any} field @param {any} message */
  _showApnRowBackendError(row, field, message) {
    if (field === 'basePartNumber') {
      const wrapper = row.querySelector('input[name="apn_resideo_part_number"]')?.closest('.rs-custom-select__wrapper');
      if (wrapper) {
        wrapper.classList.add('rs-error');
        // Suppress the static template error ("Resideo Part Number is required") so only the backend message shows.
        const staticErr = /** @type {HTMLElement|null} */ (wrapper.querySelector('.rs-custom-select__error'));
        if (staticErr) staticErr.style.display = 'none';
        let errEl = wrapper.querySelector('.rs-apn-row-error');
        if (!errEl) {
          errEl = document.createElement('div');
          errEl.className = 'rs-apn-row-error';
          errEl.setAttribute('role', 'alert');
          errEl.style.cssText = 'display:flex;align-items:center;gap:4px;margin-top:4px;';
          wrapper.appendChild(errEl);
        }
        // Clear then set so the live region fires even if the message is unchanged
        errEl.textContent = '';
        errEl.innerHTML = `<svg width="10" height="10" viewBox="0 0 48 48" fill="none" style="color:#D22730;flex-shrink:0" aria-hidden="true"><path d="M48.0001 48.2632H6.10352e-05V0.263184H48.0001V48.2632ZM24.1407 32.5972C23.2704 32.5972 22.5874 32.8512 22.0919 33.3599C21.6097 33.8554 21.3682 34.4719 21.3682 35.2085C21.3682 35.9183 21.6097 36.5277 22.0919 37.0366C22.5874 37.5321 23.2703 37.7798 24.1407 37.7798C24.9978 37.7798 25.6744 37.5322 26.17 37.0366C26.6655 36.5277 26.9131 35.9183 26.9131 35.2085C26.9131 34.4719 26.6655 33.8554 26.17 33.3599C25.6745 32.851 24.9978 32.5972 24.1407 32.5972ZM21.5089 8.24854L22.0118 28.8804H26.1495L26.6319 8.24854H21.5089Z" fill="currentColor"/></svg><span style="color:#D22730;font-size:12px;font-weight:500;">${message}</span>`;
        errEl.style.display = 'flex';
      }
    } else if (field === 'customerPartNumber') {
      const cpnInput = /** @type {HTMLInputElement|null} */ (row.querySelector('input[name="apn_customer_part_number"]'));
      const wrapper = cpnInput?.closest('.rs-input__content');
      if (wrapper) {
        this._setFieldError(wrapper, message, '.rs-input__error p', cpnInput);
      }
    }
  }

  /** Clear backend-injected row errors when the user edits a field. */
  /** @param {any} row */
  _clearApnRowBackendErrors(row) {
    const pnWrapper = row.querySelector('input[name="apn_resideo_part_number"]')?.closest('.rs-custom-select__wrapper');
    if (pnWrapper) {
      pnWrapper.classList.remove('rs-error');
      const errEl = pnWrapper.querySelector('.rs-apn-row-error');
      if (errEl) errEl.style.display = 'none';
      // Restore the static template error div to CSS-controlled visibility.
      const staticErr = pnWrapper.querySelector('.rs-custom-select__error');
      if (staticErr) staticErr.style.display = '';
    }
    const cpnInput = /** @type {HTMLInputElement|null} */ (row.querySelector('input[name="apn_customer_part_number"]'));
    const cpnWrapper = cpnInput?.closest('.rs-input__content');
    if (cpnWrapper) {
      this._clearFieldError(cpnWrapper, '.rs-input__error p', cpnInput);
    }
  }

  /** Body-level live region — reliably announces form errors (see rs-location-picker announce). */
  _ensureApnFormErrorLiveRegion() {
    let region = document.getElementById('rs-pl-apn-form-error-live');
    if (!region) {
      region = document.createElement('div');
      region.id = 'rs-pl-apn-form-error-live';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'alert');
      region.setAttribute('aria-live', 'assertive');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    return region;
  }

  /** @param {string} message */
  _announceApnFormError(message) {
    const region = this._ensureApnFormErrorLiveRegion();
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  }

  /** Select error text so TTS extensions (e.g. Read Aloud) can read it from the selection. */
  /** @param {HTMLElement} errEl */
  _selectApnFormErrorForTts(errEl) {
    errEl.focus({ preventScroll: true });
    try {
      const range = document.createRange();
      range.selectNodeContents(errEl);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch (_) {
      // Selection is best-effort for third-party TTS tools.
    }
  }

  /** Show a form-level error message above the APN submit button. */
  /** @param {any} message */
  _showApnFormError(message) {
    const btn = /** @type {HTMLButtonElement|null} */ (document.querySelector('.rs-pl-apn-submit-btn'));
    if (!btn) return;

    this._clearApnFormError();

    const wrap = document.createElement('div');
    wrap.className = 'rs-pl-apn-form-error-wrap';

    const errP = document.createElement('p');
    errP.id = 'rs-pl-apn-form-error';
    errP.className = 'rs-pl-apn-form-error';
    errP.setAttribute('tabindex', '0');
    errP.setAttribute('role', 'alert');
    errP.textContent = message;

    wrap.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" style="flex-shrink:0" aria-hidden="true"><path d="M10 10H0V0H10V10ZM5.0293 6.73633C4.84795 6.73633 4.70577 6.78949 4.60254 6.89551C4.50215 6.99873 4.45215 7.12686 4.45215 7.28027C4.45215 7.42815 4.50209 7.55511 4.60254 7.66113C4.70577 7.76432 4.84798 7.8164 5.0293 7.81641C5.20787 7.81641 5.34891 7.76437 5.45215 7.66113C5.55535 7.55512 5.60644 7.42812 5.60645 7.28027C5.60645 7.12691 5.55525 6.99871 5.45215 6.89551C5.34891 6.78948 5.20787 6.73633 5.0293 6.73633ZM4.48145 1.66406L4.58594 5.96191H5.44824L5.54785 1.66406H4.48145Z" fill="#D22730"/></svg>';
    wrap.appendChild(errP);

    const buttonsGroup = btn.closest('.rs-group-custom, .rs-group');
    if (buttonsGroup?.parentNode) {
      buttonsGroup.parentNode.insertBefore(wrap, buttonsGroup);
    } else {
      btn.parentNode?.insertBefore(wrap, btn);
    }

    btn.setAttribute('aria-describedby', 'rs-pl-apn-form-error');
    this._announceApnFormError(message);

    errP.scrollIntoView({ behavior: 'smooth', block: 'center' });
    requestAnimationFrame(() => this._selectApnFormErrorForTts(errP));
  }

  _clearApnFormError() {
    document.querySelector('.rs-pl-apn-form-error-wrap')?.remove();
    const region = document.getElementById('rs-pl-apn-form-error-live');
    if (region) region.textContent = '';
    document.querySelector('.rs-pl-apn-submit-btn')?.removeAttribute('aria-describedby');
    window.getSelection()?.removeAllRanges();
  }

  /** Show an error message inside the submission modal (above the action buttons). */
  /** @param {string} message */
  _showModalError(message) {
    const modal = this.els.modal;
    if (!modal) return;
    let errEl = modal.querySelector('#rs-pl-modal-error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.id = 'rs-pl-modal-error';
      errEl.setAttribute('role', 'alert');
      errEl.style.cssText = 'display:flex;align-items:center;gap:8px;padding:10px 12px;background:#FFF2F2;border:1px solid #D22730;border-radius:4px;margin-bottom:8px;';
      const buttons = modal.querySelector('.rs-modal__buttons');
      if (buttons) buttons.parentNode?.insertBefore(errEl, buttons);
      else modal.querySelector('.rs-modal__body')?.appendChild(errEl);
    }
    errEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" style="flex-shrink:0" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fill="#D22730"/></svg><span style="color:#D22730;font-size:14px;font-weight:500;line-height:1.4;">${message}</span>`;
    errEl.style.display = 'flex';
  }

  _clearModalError() {
    const errEl = this.els.modal?.querySelector('#rs-pl-modal-error');
    if (errEl) errEl.style.display = 'none';
  }

  /** Show an error message above the button group on the confirmation step. */
  /** @param {string} message */
  _showConfirmationError(message) {
    let errEl = document.getElementById('rs-pl-confirmation-error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.id = 'rs-pl-confirmation-error';
      errEl.setAttribute('role', 'alert');
      errEl.style.cssText = 'display:flex;align-items:center;gap:8px;padding:10px 12px;background:#FFF2F2;border:1px solid #D22730;border-radius:4px;margin-bottom:8px;width:100%;box-sizing:border-box;';
      // The save-draft button lives inside an rs-group-custom div; insert the error before that group.
      // That button isn't always present when this fires (e.g. the modal may already be closed),
      // so fall back to the confirmation section itself, which is always in the DOM.
      const saveDraftBtn = document.querySelector('.rs-button-save-for-draft');
      const btnGroup = saveDraftBtn?.closest('.rs-group-custom');
      const anchor = btnGroup ?? saveDraftBtn?.parentNode;
      if (anchor?.parentNode) {
        anchor.parentNode.insertBefore(errEl, anchor);
      } else {
        const confirmationSection = document.querySelector('.rs-private-label-confirmation-section');
        confirmationSection?.prepend(errEl);
      }
    }
    errEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" style="flex-shrink:0" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fill="#D22730"/></svg><span style="color:#D22730;font-size:14px;font-weight:500;line-height:1.4;">${message}</span>`;
    errEl.style.display = 'flex';
  }

  _clearConfirmationError() {
    const errEl = document.getElementById('rs-pl-confirmation-error');
    if (errEl) errEl.style.display = 'none';
  }

  /** Open the save modal, update aria-hidden, and move focus inside. */
  _openModal() {
    const modal = this.els.modal;
    if (!modal) return;
    modal.classList.remove('rs-hidden');
    modal.setAttribute('aria-hidden', 'false');
    const firstFocusable = /** @type {HTMLElement|null} */ (
      modal.querySelector('button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
    firstFocusable?.focus();
  }

  /** Close the save modal, restore aria-hidden, and return focus to the trigger. */
  /** @param {HTMLElement|null} [returnFocusTo] */
  _closeModal(returnFocusTo) {
    const modal = this.els.modal;
    if (!modal) return;
    modal.classList.add('rs-hidden');
    modal.setAttribute('aria-hidden', 'true');
    (returnFocusTo ?? /** @type {HTMLElement|null} */ (document.querySelector('.rs-button-submit')))?.focus();
  }

  duplicateRows(count = 4, first = false) {
    if (first) this.els.apnTableBody.innerHTML = "";
    for (let i = 0; i < count; i++) {
      if (!this.templates.apnRow) continue;
      const secondRow = /** @type {HTMLElement} */ (this.templates.apnRow.cloneNode(true));
      const rowId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      secondRow.dataset.rowId = rowId;
      this._wireRowAriaDescribedby(secondRow, rowId);
      this.els.apnTableBody.appendChild(secondRow);
    }
    let rowCount = this.els.apnTableBody.querySelectorAll(".rs-table-body__row").length;
    this.els.apnActionSelectedAllBtnLabel.textContent = this.state.confirmationPrefix.apnSelectedAll.replace("#", rowCount);
  }

  /**
   * Show an inline field error: adds rs-error, sets the error text (always clears first so
   * the DOM mutation fires and live regions announce it even when the text hasn't changed),
   * and marks the input aria-invalid.
   * @param {Element|null} wrapper - the .rs-input__content or .rs-custom-select__wrapper
   * @param {string} message - error text to display
   * @param {string} [errorSelector] - selector for the <p> inside the error div (default ".rs-input__error p")
   * @param {HTMLElement|null} [input] - the actual input/button to mark aria-invalid
   */
  _setFieldError(wrapper, message, errorSelector = '.rs-input__error p', input = null) {
    if (!wrapper) return;
    wrapper.classList.add('rs-error');
    const errorEl = wrapper.querySelector(errorSelector);
    if (errorEl) {
      errorEl.textContent = '';        // clear first — guarantees a mutation even if text unchanged
      errorEl.textContent = message;
    }
    input?.setAttribute('aria-invalid', 'true');
  }

  /**
   * Clear an inline field error: removes rs-error, blanks the error text, and resets aria-invalid.
   * @param {Element|null} wrapper
   * @param {string} [errorSelector]
   * @param {HTMLElement|null} [input]
   */
  _clearFieldError(wrapper, errorSelector = '.rs-input__error p', input = null) {
    if (!wrapper) return;
    wrapper.classList.remove('rs-error');
    const errorEl = wrapper.querySelector(errorSelector);
    if (errorEl) errorEl.textContent = '';
    input?.setAttribute('aria-invalid', 'false');
  }

  /**
   * Give each cloned APN row's error divs unique IDs and wire aria-describedby on the
   * corresponding controls so screen readers announce inline validation messages.
   * @param {HTMLElement} row
   * @param {string} rowId
   */
  _wireRowAriaDescribedby(row, rowId) {
    // Resideo Part Number select — button is the focusable control
    const pnSelect = row.querySelector('.rs-pl-apn-resideo-part-number-select');
    if (pnSelect) {
      const pnErrId = `apn-pn-error-${rowId}`;
      const pnErrDiv = pnSelect.querySelector('.rs-custom-select__error');
      if (pnErrDiv) pnErrDiv.id = pnErrId;
      const pnBtn = /** @type {HTMLElement|null} */ (pnSelect.querySelector('.rs-custom-select__button'));
      if (pnBtn) pnBtn.setAttribute('aria-describedby', pnErrId);
    }

    // Customer Part Number input
    const cpnWrapper = row.querySelector('.rs-pl-apn-customer-part-number');
    if (cpnWrapper) {
      const cpnErrId = `apn-cpn-error-${rowId}`;
      const cpnErrDiv = cpnWrapper.querySelector('.rs-input__error');
      if (cpnErrDiv) cpnErrDiv.id = cpnErrId;
      const cpnInput = /** @type {HTMLElement|null} */ (cpnWrapper.querySelector('input'));
      if (cpnInput) cpnInput.setAttribute('aria-describedby', cpnErrId);
    }
  }

  /** @param {any} backendStatus @param {any} plpPartNumber */
  mapPartAssignmentDisplayStatus(backendStatus, plpPartNumber) {
    const hasPartNumber = Boolean(plpPartNumber?.trim());
    if (backendStatus === "Pending") return "Pending";
    if (backendStatus === "Sent to SAP Obsolete" || backendStatus === "Error in SAP Obsolete" || backendStatus === "Obsolete") return "Obsolete";
    if (backendStatus === "Approved" || backendStatus === "Sent to SAP") {
      return hasPartNumber ? "Orderable" : backendStatus;
    }
    if (backendStatus === "Error in SAP") {
      return backendStatus;
    }
    return backendStatus || "";
  }

  /** @param {any} displayStatus */
  getPartStatusClass(displayStatus) {
    switch (displayStatus) {
      case "Orderable":
      case "Approved": return "rs-approved-status";
      case "Pending": return "rs-pending-status";
      case "Obsolete": return "rs-obsolete-status";
      default: return "";
    }
  }

  // Paints the account/label description block from already-hydrated edit state.
  // Called immediately once a label is selected (before the assignments fetch resolves)
  // so the section never shows its static placeholder sample content (e.g. "ProAC").
  renderCustomerPartNumberDescription() {
    this.els.cpnPreviewImageWrapper?.classList.add("rs-pl-cpn-loaded");
    this.els.cpnDescriptionWrapper?.classList.add("rs-pl-cpn-loaded");
    this.els.cpnAccountNumber && (this.els.cpnAccountNumber.textContent = this.state.selection.accountNumber || "");
    this.els.cpnSalesArea && (this.els.cpnSalesArea.textContent = this.getSalesAreaDisplay());
    const cpnImgUrl = this.state.form.fullPreviewImage || this.state.form.previewImage;
    if (this.els.cpnPreviewImageWrapper) {
      this.els.cpnPreviewImageWrapper.innerHTML = "";
      if (cpnImgUrl) {
        let img = document.createElement("img");
        img.src = cpnImgUrl;
        img.alt = "Preview Image";
        img.className = "rs-pl-cpn-preview-image";
        const _cpnLineCount = [this.state.form.line1, this.state.form.line2, this.state.form.line3, this.state.form.line4].filter((s) => s?.trim()).length || 1;
        const _cpnOverhead = this.state.selection.typeHandle === "non-t-series" ? LABEL_RENDER.JPEG_OVERHEAD_PX_NON_T : LABEL_RENDER.JPEG_OVERHEAD_PX_T;
        const _cpnH = Math.round((_cpnLineCount * LABEL_RENDER.JPEG_PX_PER_LINE + _cpnOverhead) / 4);
        img.style.cssText = this.state.edit.isLogo ? "max-width:160px;width:auto;height:auto;display:block;" : `height:${_cpnH}px;width:auto;max-width:160px;display:block;`;
        this.els.cpnPreviewImageWrapper.appendChild(img);
      }
    }
    this.els.cpnLine1 && (this.els.cpnLine1.textContent = this.state.form.line1 || "");
    this.els.cpnLine2 && (this.els.cpnLine2.textContent = this.state.form.line2 || "");
    if (this.els.cpnLine3) {
      this.els.cpnLine3.parentElement?.classList.toggle("rs-hidden", !this.state.form.line3);
      this.els.cpnLine3.textContent = this.state.form.line3 || "";
    }
    if (this.els.cpnLine4) {
      this.els.cpnLine4.parentElement?.classList.toggle("rs-hidden", !this.state.form.line4);
      this.els.cpnLine4.textContent = this.state.form.line4 || "";
    }
  }

  /** @param {any} viewCustomerPartNumberData */
  renderCustomerPartNumber(viewCustomerPartNumberData) {
    if (!viewCustomerPartNumberData || !this.els.cpnTableBody) return;
    this.els.cpnTableBody.innerHTML = "";
    this.renderCustomerPartNumberDescription();

    const assignments = viewCustomerPartNumberData?.partNumberAssignments ?? [];
    if (!assignments.length) {
      const empty = document.createElement("p");
      empty.className = "text-medium rs-pd-y-8 rs-pd-x-16";
      const emptyEm = document.createElement("em");
      emptyEm.textContent = "No part number assignments found.";
      empty.appendChild(emptyEm);
      this.els.cpnTableBody.appendChild(empty);
    }

    assignments.forEach((/** @type {any} */ assignment) => {
      if (!this.templates.cpnRow) return;
      let cpnRow = /** @type {Element} */ (this.templates.cpnRow.cloneNode(true));
      let resideoPartNumberEl = cpnRow.querySelector(".rs-pl-cpn-resideo-parnt-number p");
      if (resideoPartNumberEl) resideoPartNumberEl.textContent = assignment?.resideoPartNumber || "";
      let customerPartNumberEl = cpnRow.querySelector(".rs-pl-cpn-customer-parnt-number p");
      if (customerPartNumberEl) customerPartNumberEl.textContent = assignment?.customerPartNumber || "";
      let labelPositionEl = cpnRow.querySelector(".rs-pl-cpn-label-position p");
      if (labelPositionEl) labelPositionEl.textContent = assignment?.labelPosition?.name || "";
      let partStatusEl = cpnRow.querySelector(".rs-pl-cpn-part-status p");
      if (partStatusEl) {
        const displayStatus = this.mapPartAssignmentDisplayStatus(assignment?.partStatus, assignment?.plpPartNumber);
        partStatusEl.textContent = displayStatus;
        const statusClass = this.getPartStatusClass(displayStatus);
        if (statusClass && partStatusEl.parentElement) partStatusEl.parentElement.classList.add(statusClass);
      }
      let partCreatedEl = cpnRow.querySelector(".rs-pl-cpn-part-created p");
      if (partCreatedEl) partCreatedEl.textContent = assignment?.partCreated || "";
      let partApprovedEl = cpnRow.querySelector(".rs-pl-cpn-part-approved p");
      if (partApprovedEl) partApprovedEl.textContent = assignment?.partApproved || "";
      this.els.cpnTableBody.appendChild(cpnRow);
    });

    this.state.cpnView.total = Number(viewCustomerPartNumberData?.pagination?.total) || 0;
    this.state.cpnView.totalPages = Number(viewCustomerPartNumberData?.pagination?.totalPages) || 0;
    this.state.cpnView.page = Number(viewCustomerPartNumberData?.pagination?.page) || this.state.cpnView.page;
    this.state.cpnView.limit = Number(viewCustomerPartNumberData?.pagination?.limit) || this.state.cpnView.limit;

    this.updatePagination({
      pagination: this.els.cpnPagination,
      info: this.els.cpnPaginationInfo,
      limit: Math.max(1, this.state.cpnView.limit),
      total: this.state.cpnView.total,
      page: Math.max(1, this.state.cpnView.page),
      totalPages: Math.max(0, this.state.cpnView.totalPages)
    });

  }

  // =========================================================
  // SELECTION STEP
  // =========================================================
  renderTypeCards() {
    if (!this.els.typesWrapper || !this.els.typesItem) return;
    if (!this.state.selection.types.length) return;

    const template = this.els.typesItem.cloneNode(true);
    this.els.typesWrapper.innerHTML = "";

    /** @type {any[]} */ (this.state.selection.types).forEach((/** @type {any} */ type) => {
      if (!type) return;

      const card = template.cloneNode(true);
      card.classList.remove("rs-hidden");

      let img = card.querySelector("img");
      const title = card.querySelector(".rs-heading p");
      const desc = card.querySelector(".rs-text p");
      const btn = card.querySelector(".rs-pl-select-pdp-btn");

      if (type?.image?.url) {
        if (!img) {
          const imgContainer = card.querySelector(".image-block");
          if (imgContainer) {
            img = document.createElement("img");
            img.className = "image-block__image";
            imgContainer.innerHTML = "";
            imgContainer.appendChild(img);
          }
        }
        if (img) {
          img.src = type.image.url;
          img.srcset = "";
          img.removeAttribute("sizes");
          img.alt = type.title || "Private Label Image";
        }
      }

      const isFrench = _win.Shopify?.locale?.startsWith("fr");

      if (title) title.textContent = type.title || "";
      if (desc) desc.textContent = (isFrench && type.description_fr) ? type.description_fr : (type.description || "");

      if (btn) {
        // Always derive the handle from the title (mirrors admin getLabelTypeCategory).
        // type.folder is unreliable — it may be blank or use a different naming convention.
        btn.dataset.type = this._getLabelTypeCategory(type.title) || type.folder || "";
        btn.dataset.id = type.id || "";
        btn.dataset.step = "label-creation";
        const span = btn.querySelector("span");
        if (span) span.textContent = `Select ${type.title || ""}`;
      }

      this.els.typesWrapper.appendChild(card);
    });
  }

  // =========================================================
  // LIST/TABLE
  // =========================================================
  async loadAndRenderList() {
    const _tb = /** @type {any} */ (this.els).tableBody;
    if (_tb) {
      _tb.innerHTML =
        `<div class="rs-pl-list-loading">` +
        `<span class="rs-loading-spinner" style="width:32px;height:32px;margin:0;" aria-hidden="true"></span></div>`;
    }
    // Reset cursor stack and total whenever we go back to page 1 (search/filter change)
    if (this.state.list.page === 1) {
      this.state.list.cursorStack = [];
      this.state.list.nextCursor = null;
      this.state.list.total = 0;
    }

    const isPage1CursorMode = this.state.list.page === 1 && !this.state.list.searchText &&
      (!this.state.list.filterStatus || this.state.list.filterStatus === "all") &&
      !this.state.list.filterPartNumber;

    // Fire page fetch and count fetch in parallel on page 1 cursor mode
    const listPromise = this.fetchList({ searchText: this.state.list.searchText });
    const countPromise = isPage1CursorMode ? this.fetchCount() : Promise.resolve(null);

    const list = await listPromise;
    if (list) {
      this.renderList(list);
    } else {
      this.renderListError();
    }

    // Update count when it arrives (non-blocking — page is already rendered)
    if (isPage1CursorMode) {
      countPromise.then((total) => {
        if (typeof total !== "number") return;
        this.state.list.total = total;
        const ListTitle = document.querySelector('.rs-pl-list-heading');
        if (ListTitle) {
          let totalEL = ListTitle.querySelector('.rs-pl-list-total');
          const text = _win.__t?.('count_results', { count: total }) ?? null;
          if (totalEL) {
            totalEL.textContent = text;
          } else {
            const totalSpan = document.createElement('span');
            totalSpan.className = "rs-pl-list-total";
            totalSpan.textContent = text;
            ListTitle.appendChild(totalSpan);
          }
        }
        // Also update the pagination info to reflect total now that we have it
        this.updatePagination({
          pagination: this.els.apnPagination,
          info: this.els.apnPaginationInfo,
          limit: Math.max(1, this.state.list.limit),
          total: this.state.list.total,
          page: Math.max(1, this.state.list.page),
          totalPages: Math.max(0, this.state.list.totalPages),
          useCursor: this.state.list.useCursor,
          rowCount: this.els.tableBody?.children.length,
        });
      });
    }
  }

  renderListError() {
    if (!this.els.tableBody) return;
    this.els.tableBody.innerHTML =
      `<div class="rs-pl-list-loading" role="alert">` +
      `<p class="text-medium weight-500 text-brand-black">Unable to load private labels. Please try again.</p>` +
      `</div>`;
  }

  async renderCustomerPartNumberList() {
    if (this.els.cpnTableBody) {
      this.els.cpnTableBody.innerHTML =
        `<div class="rs-pl-list-loading">` +
        `<span class="rs-loading-spinner" style="width:32px;height:32px;margin:0;" aria-hidden="true"></span></div>`;
    }
    this.els.cpnPreviewImageWrapper?.classList.remove("rs-pl-cpn-loaded");
    this.els.cpnDescriptionWrapper?.classList.remove("rs-pl-cpn-loaded");
    const list = await this.fetchCustomerPartNumberList();
    if (list) this.renderCustomerPartNumber(list);
  }

  /** @param {any} list */
  renderList(list) {
    if (!this.els.tableBody || !this.templates.row) return;

    const data = Array.isArray(list?.data) ? list.data : [];
    this.els.tableBody.innerHTML = "";

    const serverTotal = Number(list?.total);
    this.state.list.useCursor = serverTotal === -1;
    this.state.list.nextCursor = list?.nextCursor ?? null;
    this.state.list.page = Number(list?.page) || this.state.list.page;
    this.state.list.limit = Number(list?.limit) || this.state.list.limit;

    if (this.state.list.useCursor) {
      // Store next cursor at position [page - 1] so we can reach page+1 later
      if (this.state.list.nextCursor) {
        this.state.list.cursorStack[this.state.list.page - 1] = this.state.list.nextCursor;
      }
      this.state.list.totalPages = 0;
      // Keep total as-is — we don't know it in cursor mode, so don't overwrite with 0
    } else {
      this.state.list.total = serverTotal || 0;
      this.state.list.totalPages = Number(list?.totalPages) || 0;
    }

    const ListTitile = document.querySelector('.rs-pl-list-heading');
    if (ListTitile) {
      let totalEL = ListTitile.querySelector('.rs-pl-list-total');
      if (!this.state.list.useCursor) {
        const text = _win.__t?.('count_results', { count: this.state.list.total }) ?? null;
        if (totalEL) {
          totalEL.textContent = text;
        } else {
          let totalSpan = document.createElement('span');
          totalSpan.className = "rs-pl-list-total";
          totalSpan.textContent = text;
          ListTitile.appendChild(totalSpan);
        }
      }
    }

    if (!data.length) {
      this.templates.empty && this.els.tableBody.appendChild(this.templates.empty);
      this.updatePagination({
        pagination: this.els.apnPagination,
        info: this.els.apnPaginationInfo,
        limit: Math.max(1, this.state.list.limit),
        total: this.state.list.total,
        page: Math.max(1, this.state.list.page),
        totalPages: Math.max(0, this.state.list.totalPages)
      });
      return;
    }

    this.renderPartNumberSelect(list);

    data.forEach((/** @type {any} */ item) => {
      if (!this.templates.row) return;
      const row = /** @type {HTMLElement} */ (this.templates.row.cloneNode(true));
      row.classList.remove("rs-hidden");

      // --- dataset used for EDIT ---
      row.dataset.labelId = item?.id || "";
      row.dataset.typeTitle = item?.privateLabel?.type?.title || "";
      row.dataset.typeId = item?.privateLabel?.typeId || item?.privateLabel?.type?.id || "";
      row.dataset.contractorName = item?.privateLabel?.contractorName || item?.contractorName || "";
      row.dataset.condensedFont = String(!!item?.privateLabel?.condensedFont);
      row.dataset.line1 = item?.privateLabel?.line1 || "";
      row.dataset.line2 = item?.privateLabel?.line2 || "";
      row.dataset.line3 = item?.privateLabel?.line3 || "";
      row.dataset.line4 = item?.privateLabel?.line4 || "";
      row.dataset.previewImageId = item?.privateLabel?.previewImage?.id || item?.privateLabel?.previewImageId || "";
      row.dataset.accountNumber = item?.accountId || "";
      row.dataset.previewImage = item?.privateLabel?.labelImageExport?.url || "";
      row.dataset.fullPreviewImage = item?.privateLabel?.logoImage?.url || item?.privateLabel?.labelImageDisplay?.url || "";
      row.dataset.migrated = String(!!item?.privateLabel?.migrated);
      row.dataset.isLogo = String(item?.privateLabel?.customizationType?.name !== "Text");
      const _assignedEdges = item?.partNumbers?.edges ?? [];
      row.dataset.assignedBasePartNumbers = JSON.stringify(
        _assignedEdges
          .filter((/** @type {any} */ e) => !OBSOLETE_STATUSES.has(e?.node?.status?.name || ""))
          .map((/** @type {any} */ e) => e?.node?.resideoPartNumber?.partNumber)
          .filter(Boolean)
      );

      // --- UI ---
      const accountEl = row.querySelector(".rs-pl-table-account-number p");
      if (accountEl) accountEl.textContent = item?.accountId || "";

      const salesAreaEl = row.querySelector(".rs-pl-table-sales-area p");
      if (salesAreaEl) {
        const saOrg = item?.salesOrganization;
        const saChannel = item?.distributionChannel;
        salesAreaEl.textContent =
          saOrg && saChannel
            ? `${String(saOrg).padStart(4, "0")}${String(saChannel).padStart(2, "0")}`
            : this.getSalesAreaDisplay();
      }


      const descEl = /** @type {HTMLElement|null} */ (row.querySelector(".rs-pl-table-description p"));
      if (descEl) {
        descEl.textContent = [item?.privateLabel?.line1, item?.privateLabel?.line2, item?.privateLabel?.line3, item?.privateLabel?.line4]
          .filter(Boolean)
          .join("\n");
        descEl.style.whiteSpace = "pre-wrap";
      }

      const wrapperImageEl = row.querySelector(".image-block");
      if (wrapperImageEl) {
        const imgUrl = item?.privateLabel?.labelImageDisplay?.url || item?.privateLabel?.labelImageExport?.url;
        if (imgUrl) {
          const newImageEL = document.createElement("img");
          newImageEL.src = imgUrl;
          newImageEL.alt = item?.privateLabel?.type?.title ? `${item?.privateLabel?.type?.title} Preview` : "Private Label Preview";
          newImageEL.className = "rs-pl-table-image";
          const isMigrated = !!item?.privateLabel?.migrated;
          const isLogoLabel = item?.privateLabel?.customizationType?.name !== "Text";
          const lineCount = [item?.privateLabel?.line1, item?.privateLabel?.line2, item?.privateLabel?.line3, item?.privateLabel?.line4].filter((s) => s?.trim()).length || 1;
          const _listTypeHandle = this._getLabelTypeCategory(item?.privateLabel?.type?.title || "");
          const _listOverhead = _listTypeHandle === "non-t-series" ? LABEL_RENDER.JPEG_OVERHEAD_PX_NON_T : LABEL_RENDER.JPEG_OVERHEAD_PX_T;
          const displayH = Math.round((lineCount * LABEL_RENDER.JPEG_PX_PER_LINE + _listOverhead) / 4);
          newImageEL.style.cssText = isLogoLabel ? "max-width:160px;width:auto;height:auto;display:block;" : `${isMigrated ? "" : `max-height:${displayH}px;`}max-width:160px;width:auto;height:auto;display:block;`;
          wrapperImageEl.innerHTML = "";
          wrapperImageEl.appendChild(newImageEL);
        }
      }

      const statusEl = row.querySelector(".rs-pl-table-status p");
      const statusName = item?.status?.name || "";
      const edges = item?.partNumbers?.edges ?? [];
      const hasNonObsoleteAssignments = edges.some((edge) => {
        const s = edge?.node?.status?.name || "";
        return !OBSOLETE_STATUSES.has(s);
      });
      const hasPlpPartNumber = edges.some((edge) => {
        const node = edge?.node;
        const s = node?.status?.name || "";
        return !OBSOLETE_STATUSES.has(s) && node?.plpPartNumber?.trim();
      });
      const displayStatus = this.mapPartAssignmentDisplayStatus(statusName, hasPlpPartNumber ? "plp" : "");
      if (statusEl) statusEl.textContent = displayStatus;

      // Skip Obsolete labels — filtered by the API but guard defensively
      if (OBSOLETE_STATUSES.has(statusName)) return;

      const statusWrap = statusEl?.closest(".rs-pl-table-status");
      statusWrap?.classList?.remove("rs-pending-status", "rs-approved-status", "rs-draft-status", "rs-obsolete-status");
      const listingStatusClass = this.getPartStatusClass(displayStatus);
      if (listingStatusClass) statusWrap?.classList.add(listingStatusClass);

      const assignBtn = row.querySelector(".rs-table-button-assign");
      const editBtn = row.querySelector(".rs-table-button-edit");
      const viewAssignmentsBtn = row.querySelector(".rs-table-button-view-cpn");
      const previewBtn = row.querySelector(".rs-table-button-preview");
      const deleteDraftBtn = row.querySelector(".rs-table-button-delete-draft");

      // Hide all action buttons first; reveal only what each status allows
      assignBtn?.classList.add("rs-hidden");
      editBtn?.classList.add("rs-hidden");
      viewAssignmentsBtn?.classList.add("rs-hidden");
      previewBtn?.classList.add("rs-hidden");
      deleteDraftBtn?.classList.add("rs-hidden");

      if (statusName === "Pending") {
        assignBtn?.classList.remove("rs-hidden");
        if (hasNonObsoleteAssignments) viewAssignmentsBtn?.classList.remove("rs-hidden");
        previewBtn?.classList.remove("rs-hidden");
      } else if (statusName === "Approved" || statusName === "Sent to SAP" || statusName === "Error in SAP") {
        assignBtn?.classList.remove("rs-hidden");
        if (hasNonObsoleteAssignments) viewAssignmentsBtn?.classList.remove("rs-hidden");
        previewBtn?.classList.remove("rs-hidden");
      } else if (statusName === "Draft") {
        statusWrap?.classList.add("rs-draft-status");
        editBtn?.classList.remove("rs-hidden");
        previewBtn?.classList.remove("rs-hidden");
        deleteDraftBtn?.classList.remove("rs-hidden");
      }

      this.els.tableBody.appendChild(row);
    });

    this.updatePagination({
      pagination: this.els.apnPagination,
      info: this.els.apnPaginationInfo,
      limit: Math.max(1, this.state.list.limit),
      total: this.state.list.total,
      page: Math.max(1, this.state.list.page),
      totalPages: Math.max(0, this.state.list.totalPages)
    });
  }

  /** @param {any} data */
  extractPartNumbers(data) {
    const counts = new Map();

    data.forEach((/** @type {any} */ item) => {
      const raw = item?.partNumbers.edges ?? "";
      raw
        .map((/** @type {any} */ s) => s)
        .filter(Boolean)
        .forEach((/** @type {any} */ pn) => {
          counts.set(pn.node.resideoPartNumber.partNumber, (counts.get(pn.node.resideoPartNumber.partNumber) ?? 0) + 1);
        });
    });

    return counts; // Map(partNumber -> count)
  }

  /** @param {any} list */
  renderPartNumberSelect(list) {
    const data = Array.isArray(list?.data) ? list.data : [];

    const selectRoot = document.querySelector(".rs-pl-part-number-select .rs-custom-select");
    if (!selectRoot) return;

    const menu = selectRoot.querySelector(".rs-custom-select__menu");
    const valueEl = selectRoot.querySelector(".rs-custom-select__value");
    const input = /** @type {HTMLInputElement|null} */ (selectRoot.querySelector('input[name="part_number"]'));
    const button = selectRoot.querySelector(".rs-custom-select__button");

    if (!menu || !valueEl || !input) return;

    const counts = this.extractPartNumbers(data);

    // Build sorted unique options
    const options = Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([pn, count]) => ({ value: pn, label: pn, count }));

    // Add "All" option at top
    options.unshift({ value: "", label: _win.__t?.('all_part_numbers') ?? "", count: null });

    const currentValue = input.value || "";

    // Clear menu
    menu.innerHTML = "";

    options.forEach((opt, idx) => {
      const li = document.createElement("li");
      li.className = "rs-custom-select__option";
      li.setAttribute("role", "option");
      li.tabIndex = idx === 0 ? 0 : -1;
      li.dataset.value = opt.value;
      li.setAttribute("aria-selected", String(opt.value === currentValue));
      li.setAttribute("aria-label", opt.label);

      const label = document.createElement("span");
      label.className = "rs-custom-select__option-label";
      label.textContent = opt.label;

      li.appendChild(label);

      li.addEventListener("click", () => {
        // Update UI
        input.value = opt.value;
        valueEl.textContent = opt.label;

        // Update aria-selected
        menu.querySelectorAll('[role="option"]').forEach((el) => {
          el.setAttribute("aria-selected", "false")
          el.classList.remove("is-selected", "is-active");
        });
        li.setAttribute("aria-selected", "true");
        li.classList.add("is-selected", "is-active");

        // Close dropdown (if your component uses aria-expanded)
        button?.setAttribute("aria-expanded", "false");
        this.state.list.filterPartNumber = opt.value;
        selectRoot.classList.remove("open");
      });

      menu.appendChild(li);
    });

    // If previous value no longer exists, reset it
    if (currentValue && !counts.has(currentValue)) {
      input.value = "";
      valueEl.textContent = _win.__t?.('select_resideo_part_number') ?? null;
    }
  }

  /** @param {{ pagination?: any; info?: any; total?: any; limit?: any; page?: any; totalPages?: any; useCursor?: boolean; rowCount?: number }} [opts]
   */
  updatePagination({ pagination, info, total, limit, page, totalPages, useCursor = false, rowCount } = {}) {
    if (!pagination && !info) return;

    if (useCursor) {
      const start = (page - 1) * limit + 1;
      const end = start + (rowCount ?? limit) - 1;
      if (total > 0) {
        // Count has arrived — show full range
        info.textContent = _win.__t?.('range_of_total', { start, end, total }) ?? `${start}–${end} of ${total}`;
      } else {
        // Count still loading — show range only
        info.textContent = start > 0 ? `${start}–${end}` : "0-0";
      }
    } else if (!total) {
      info.textContent = "0-0 of 0";
    } else {
      const start = (page - 1) * limit + 1;
      const end = Math.min(page * limit, total);
      info.textContent = _win.__t?.('range_of_total', { start: start, end: end, total: total }) ?? null;
    }

    const nextBtn = pagination.querySelector(".rs-button-pagination--next");
    const prevBtn = pagination.querySelector(".rs-button-pagination--prev");
    if (prevBtn) prevBtn.disabled = page <= 1;
    if (nextBtn) {
      if (useCursor) {
        nextBtn.disabled = !this.state.list.nextCursor;
      } else {
        nextBtn.disabled = totalPages ? page >= totalPages : true;
      }
    }
  }

  // =========================================================
  // EDIT MODE HELPERS
  // =========================================================
  /** @param {any} row */
  enterEditModeFromRow(row) {
    this.state.edit.enabled = true;
    this.state.edit.labelId = row.dataset.labelId || "";
    this.state.edit.previewOnImageId = row.dataset.previewImageId || "";
    // Clear any lingering success banner from a previous save so it doesn't bleed into edit mode.
    this.els.successBanner?.classList.remove("rs-banner--visible");

    // hydrate form — fall back to the Shopify customer name when no contractorName was saved
    this.state.form.contractorName = row.dataset.contractorName || this.getCompanyName();
    this.state.form.line1 = row.dataset.line1 || "";
    this.state.form.line2 = row.dataset.line2 || "";
    this.state.form.line3 = row.dataset.line3 || "";
    this.state.form.line4 = row.dataset.line4 || "";
    this.state.form.previewImage = row.dataset.previewImage || "";
    this.state.selection.accountNumber = row.dataset.accountNumber || "";
    this.state.form.fullPreviewImage = row.dataset.fullPreviewImage || "";

    // hydrate canvas flags
    this.state.canvas.condensedFont = row.dataset.condensedFont === "true";

    try {
      this.state.edit.assignedBasePartNumbers = JSON.parse(row.dataset.assignedBasePartNumbers || "[]");
    } catch {
      this.state.edit.assignedBasePartNumbers = [];
    }

    // hydrate type
    const typeTitle = (row.dataset.typeTitle || "").trim();
    const typeId = (row.dataset.typeId || "").trim();

    // derive handle from title via the same mapping used everywhere else
    this.state.selection.typeHandle = typeTitle ? this._getLabelTypeCategory(typeTitle) : "";
    this.state.selection.typeId = typeId || "";
    this.state.edit.isLogo = row.dataset.isLogo === "true";
  }

  exitEditMode() {
    this.state.edit.enabled = false;
    this.state.edit.labelId = "";
    this.state.edit.previewOnImageId = "";
    this.state.edit.assignedBasePartNumbers = [];
    this.state.edit.isLogo = false;
  }

  // =========================================================
  // FORM + VALIDATION
  // =========================================================
  /** @param {any} enabled */
  setCondensedMode(enabled) {
    const mode = FONT_MODE[enabled ? "condensed" : "normal"];
    this.state.canvas.condensedFont = enabled;
    this.state.canvas.maxChars = mode.maxChars;
    this.state.canvas.fontSize = mode.fontSize;
    this.state.canvas.lineHeight = mode.lineHeight;

    this.els.canvasInputs.forEach((/** @type {any} */ input) => {
      if (!input) return;
      const container = input.closest(".rs-input__content");
      const info = container?.querySelector(".rs-input__info p");
      if (info) info.textContent = enabled ? "Max 30 Character limit" : "Max 28 Character limit";
    });

    this.drawCanvasFromState({ confirmation: false });
  }

  validateCreation() {
    let ok = true;

    // contractor name required (optional in edit mode — the label already exists and the name
    // may be absent from historical data; user can edit it but it must not block saving).
    // Always query fresh so the cached reference can't return a stale/wrong element.
    const _nameInput = /** @type {HTMLInputElement|null} */ (
      document.querySelector('.rs-input[name="name"]')
    );
    if (_nameInput) {
      // In edit mode, sync input from state if state has a value (e.g., navigated directly
      // to label-confirmation without passing through label-creation).
      if (this.state.edit.enabled && !_nameInput.value.trim() && this.state.form.contractorName) {
        _nameInput.value = this.state.form.contractorName;
      }
      const v = _nameInput.value.trim();
      const c = _nameInput.closest(".rs-input__content");
      if (!v && !this.state.edit.enabled) {
        ok = false;
        this._setFieldError(c, this.ERRORS.required, '.rs-input__error p', _nameInput);
      } else {
        this._clearFieldError(c, '.rs-input__error p', _nameInput);
        // Sync name to state right here — validateCreation() is called before every
        // save/submit path, so this guarantees createPrivateLabel always sees the value.
        if (v) this.state.form.contractorName = v;
      }
    }

    // lines — skip in edit mode; the label already exists with valid server-side data
    if (!this.state.edit.enabled) {
      this.els.canvasInputs.forEach((/** @type {any} */ input) => {
        if (!input) return;
        const container = input.closest(".rs-input__content");
        const value = input.value.trim();

        const isOptional = input.name === "optional" || input.name === "optional_digital";
        if (isOptional && !value) {
          this._clearFieldError(container, '.rs-input__error p', input);
          return;
        }

        if (!value) {
          ok = false;
          this._setFieldError(container, this.ERRORS.required, '.rs-input__error p', input);
          return;
        }

        if (value.length > this.state.canvas.maxChars) {
          ok = false;
          this._setFieldError(container, this.ERRORS.maxChars, '.rs-input__error p', input);
        } else {
          this._clearFieldError(container, '.rs-input__error p', input);
        }
      });
    }

    return ok;
  }

  fillCreationInputsFromState() {
    // In edit mode, if no contractorName was saved with the label (old labels created before
    // the fix), fall back to the Shopify customer name so the field isn't left blank.
    const nameValue = this.state.form.contractorName || (this.state.edit.enabled ? this.getCompanyName() : "");
    const _nameInput = /** @type {HTMLInputElement|null} */ (document.querySelector('.rs-input[name="name"]'));
    if (_nameInput) _nameInput.value = nameValue;
    if (this.els.canvasInputs?.[0]) this.els.canvasInputs[0].value = this.state.form.line1 || "";
    if (this.els.canvasInputs?.[1]) this.els.canvasInputs[1].value = this.state.form.line2 || "";
    if (this.els.canvasInputs?.[2]) this.els.canvasInputs[2].value = this.state.form.line3 || "";
    if (this.els.canvasInputs?.[3]) this.els.canvasInputs[3].value = this.state.form.line4 || "";

    if (this.els.condensedToggle) this.els.condensedToggle.checked = !!this.state.canvas.condensedFont;
  }

  resetCreationState() {
    this.state.selection.typeId = "";
    this.state.selection.typeHandle = "";
    this.state.selection.products = [];

    this.state.form = /** @type {any} */ ({ contractorName: "", line1: "", line2: "", line3: "", line4: "" });

    // Clear canvas state before setCondensedMode so its drawCanvasFromState call is a no-op.
    this.state.canvas.src = "";
    this.state.canvas.previewOnImageId = "";
    this.state.canvas.partNumbers = "";
    this.setCondensedMode(false);

    // Clear DOM inputs to match the reset state.
    this.fillCreationInputsFromState();

    // Dismiss the success banner so it doesn't persist into the new creation flow.
    this.els.successBanner?.classList.remove("rs-banner--visible");

    // Re-enable the submit button — it gets disabled when the modal opens and must be
    // reset whenever the creation flow exits, not just on modal close/error paths.
    document.querySelectorAll(".rs-button-submit").forEach((btn) => {
      /** @type {HTMLButtonElement} */ (btn).disabled = false;
    });
  }

  // =========================================================
  // DROPDOWN
  // =========================================================
  /**
   * @param {string} [scopeSelector] Restrict the lookup to a specific step's section
   * (e.g. ".rs-private-label-confirmation-section") instead of the "first visible
   * instance" heuristic below. Prefer passing this explicitly wherever the caller
   * knows which step's dropdown it means — relying on .rs-hidden visibility alone is
   * fragile (e.g. during a step transition) and can silently resolve to the WRONG
   * step's dropdown instance, reading/writing that step's product selection instead.
   */
  getDropdownRoot(scopeSelector) {
    if (scopeSelector) {
      const scoped = document.querySelector(scopeSelector);
      if (scoped) {
        const el = scoped.querySelector(".rs-private-label-pdp-select");
        if (el) {
          return el.classList.contains("rs-custom-select__wrapper")
            ? el
            : (el.closest(".rs-custom-select__wrapper") || el);
        }
      }
    }

    // Fallback: multiple instances of .rs-private-label-pdp-select exist across steps.
    // Return the first one that is not inside a hidden section.
    const all = Array.from(document.querySelectorAll(".rs-private-label-pdp-select"));
    for (const el of all) {
      if (!el.closest(".rs-hidden")) {
        return el.classList.contains("rs-custom-select__wrapper")
          ? el
          : (el.closest(".rs-custom-select__wrapper") || el);
      }
    }
    return (
      document.querySelector(".rs-private-label-pdp-select .rs-custom-select__wrapper") ||
      document.querySelector(".rs-private-label-pdp-select")
    );
  }

  /** @param {any} products @param {string} [scopeSelector] */
  updateProductDropdownOptions(products, scopeSelector) {
    const root = this.getDropdownRoot(scopeSelector);
    if (!root) return null;

    const wrapper = root.classList.contains("rs-custom-select__wrapper")
      ? root
      : root.closest(".rs-custom-select__wrapper");
    if (!wrapper) return null;

    const menu = wrapper.querySelector(".rs-custom-select__menu");
    const valueDisplay = wrapper.querySelector(".rs-custom-select__value");
    const hiddenInput = /** @type {HTMLInputElement|null} */ (wrapper.querySelector('input[type="hidden"]'));
    if (!menu || !valueDisplay || !hiddenInput) return null;

    const template = menu.querySelector(".rs-custom-select__option");
    if (!template) return null;

    const templateClone = /** @type {HTMLElement} */ (template.cloneNode(true));
    menu.innerHTML = "";

    const safe = (products || []).filter(Boolean);
    if (!safe.length) return null;

    safe.forEach((/** @type {any} */ p, /** @type {any} */ idx) => {
      const opt = /** @type {HTMLElement} */ (templateClone.cloneNode(true));
      const label = (p.partName || "").trim();
      const value = String(p.id ?? "");

      opt.dataset.value = value;
      opt.setAttribute("aria-label", label);

      const selected = idx === 0;
      opt.classList.toggle("is-selected", selected);
      opt.setAttribute("aria-selected", selected ? "true" : "false");

      const labelEl = opt.querySelector(".rs-custom-select__option-label") || opt.querySelector("span");
      if (labelEl) labelEl.textContent = label;

      menu.appendChild(opt);

      if (selected) {
        valueDisplay.textContent = label;
        hiddenInput.value = value;
      }
    });

    return hiddenInput.value;
  }

  /** @param {any} optionEl */
  setDropdownSelectionFromOption(optionEl) {
    const wrapper = optionEl.closest(".rs-custom-select__wrapper");
    if (!wrapper) return null;

    const menu = wrapper.querySelector(".rs-custom-select__menu");
    const valueDisplay = wrapper.querySelector(".rs-custom-select__value");
    const hiddenInput = /** @type {HTMLInputElement|null} */ (wrapper.querySelector('input[type="hidden"]'));
    if (!menu || !valueDisplay || !hiddenInput) return null;

    const value = optionEl.dataset.value || "";
    const label = optionEl.getAttribute("aria-label") || optionEl.textContent?.trim() || "";

    Array.from(menu.querySelectorAll(".rs-custom-select__option")).forEach((opt) => {
      const sel = opt === optionEl;
      opt.classList.toggle("is-selected", sel);
      opt.setAttribute("aria-selected", sel ? "true" : "false");
    });

    hiddenInput.value = value;
    valueDisplay.textContent = label;
    hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
    return value;
  }

  /** @param {any} productId @param {string} [scopeSelector] */
  selectProductInDropdown(productId, scopeSelector) {
    const root = this.getDropdownRoot(scopeSelector);
    if (!root) return false;

    const wrapper = root.classList.contains("rs-custom-select__wrapper")
      ? root
      : root.closest(".rs-custom-select__wrapper");
    if (!wrapper) return false;

    const menu = wrapper.querySelector(".rs-custom-select__menu");
    const opt = menu?.querySelector(`.rs-custom-select__option[data-value="${String(productId)}"]`);
    if (!opt) return false;

    this.setDropdownSelectionFromOption(opt);
    return true;
  }

  // =========================================================
  // CANVAS
  // =========================================================
  /** @param {any} productId */
  async applyProductToCanvas(productId) {
    if (!this.els.canvas) return;

    // Ink metrics must come from the same font the server renders with — wait for it
    // before any measureText/fillText below (see ensureLabelFontLoaded).
    await ensureLabelFontLoaded(this.state.canvas.condensedFont ? "Roboto Condensed" : "Roboto");

    const p = productId
      ? /** @type {any[]} */ (this.state.selection.products).find((/** @type {any} */ x) => String(x?.id) === String(productId))
      : /** @type {any[]} */ (this.state.selection.products)?.[0];

    const img = /** @type {any} */ (_img(p));
    if (!img) return;

    const w = parseInt(img.sourceImgWidth, 10) || this.state.canvas.width;
    const h = parseInt(img.sourceImgHeight, 10) || this.state.canvas.height;

    this.els.canvas.setAttribute("width", w);
    this.els.canvas.setAttribute("height", h);

    this.state.canvas.width = w;
    this.state.canvas.height = h;

    this.state.canvas.sourceImgWidth = w;
    this.state.canvas.sourceImgHeight = h;

    // xPosition / yPosition use a top-left origin (pixels in source-image space).
    // Since canvas resolution = source image size, no additional scaling needed.
    const xPos = parseInt(img.xPosition, 10);
    const yPos = parseInt(img.yPosition, 10);
    if (!Number.isNaN(xPos)) this.state.canvas.paddingLeft = xPos;
    if (!Number.isNaN(yPos)) this.state.canvas.textY = yPos;

    this.state.canvas.labelAreaW = parseInt(img.labelAreaWidth, 10) || 0;
    this.state.canvas.labelAreaH = parseInt(img.labelAreaHeight, 10) || 0;

    this.state.canvas.previewOnImageId = img.id ? String(img.id) : "";
    this.state.canvas.partNumbers = img.partName || "";
    this.state.canvas.src = img.optimizedImage?.url || "";
  }

  drawCanvasFromState({ confirmation = false, apnPreview = false } = {}) {
    const canvas = confirmation
      ? document.getElementById("rs-canvas-confirmation")
      : apnPreview ? document.getElementById("rs-pl-asp-canvas") : this.els.canvas;

    if (!canvas) return;

    // Keep non-main canvases in sync with state dimensions (applyProductToCanvas only
    // writes to this.els.canvas, so the confirmation/apn canvases can be stale).
    if (confirmation || apnPreview) {
      canvas.setAttribute("width",  String(this.state.canvas.width));
      canvas.setAttribute("height", String(this.state.canvas.height));
    }

    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = this.state.canvas.src;

    img.onload = async () => {
      // Ensure the font is loaded before any ctx.measureText()/fillText() calls below.
      if (this.state.canvas.condensedFont) {
        await Promise.all([
          document.fonts.load('400 12px "Roboto Condensed"'),
          document.fonts.load('700 12px "Roboto Condensed"'),
        ]).catch(() => {});
      } else {
        await Promise.all([
          document.fonts.load('400 12px "Roboto"'),
          document.fonts.load('700 12px "Roboto"'),
        ]).catch(() => {});
      }

      const sourceW = this.state.canvas.sourceImgWidth || canvas.width;
      const sourceH = this.state.canvas.sourceImgHeight || canvas.height;

      // Oversample the buffer by devicePixelRatio so text/lines render crisply
      // on high-DPI screens, while all drawing coordinates below stay in
      // source-image space via ctx.scale(). Buffer size is derived only from
      // sourceW/sourceH and devicePixelRatio — never from clientWidth/getBoundingClientRect —
      // so it can't drift with browser zoom or CSS layout/reflow.
      const dpr = window.devicePixelRatio || 1;
      const bufW = Math.round(sourceW * dpr);
      const bufH = Math.round(sourceH * dpr);
      if (canvas.width !== bufW || canvas.height !== bufH) {
        canvas.width = bufW;
        canvas.height = bufH;
      }
      // Pin the CSS display width to source-image CSS pixels so the larger buffer
      // (used for crisp rendering) doesn't make the canvas grow on screen; height
      // is left to derive from width via the canvas's intrinsic aspect ratio.
      canvas.style.width = `${sourceW}px`;
      canvas.style.height = "auto";
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      ctx.clearRect(0, 0, sourceW, sourceH);
      ctx.drawImage(img, 0, 0, sourceW, sourceH);

      // Text-only layer (transparent background), used by the zoom preview so it
      // can show the label text over a flat light-gray fill instead of the photo.
      const textLayerScale = dpr * ZOOM_TEXT_RENDER_SCALE;
      const textLayer = document.createElement("canvas");
      textLayer.width = Math.round(sourceW * textLayerScale);
      textLayer.height = Math.round(sourceH * textLayerScale);
      const textCtx = /** @type {any} */ (textLayer.getContext("2d"));
      textCtx.setTransform(1, 0, 0, 1, 0, 0);
      textCtx.scale(textLayerScale, textLayerScale);

      const lines = confirmation
        ? [this.state.form.line1, this.state.form.line2, this.state.form.line3, this.state.form.line4]
            .map((s) => (s || "").trim())
            .filter(Boolean)
        : (/** @type {any} */ (this.els)).canvasInputs
            .map((/** @type {any} */ i) => i?.value?.trim())
            .filter(Boolean);

      if (!apnPreview) {
        this.toggleZoomPreview(lines.length > 0, confirmation);
      }

      if (!lines.length) return;

      // Match the positioning logic from ThermostatWithLabelOverlay / renderTextOnTemplate
      // in the admin repo. Canvas resolution equals source-image resolution so scale = 1.
      const maxLines = LABEL_TYPE[this.state.selection.typeHandle]?.maxLines ?? 3;
      const labelAreaH = this.state.canvas.labelAreaH;

      // Extra vertical padding only for Non-T Series labels.
      // Bottom padding also reserves room for descenders (e.g. "j", "@") on the last
      // line so they aren't clipped — matches renderTextOnTemplate.ts in the admin repo,
      // where paddedBottom = extraPad + last line's actualBoundingBoxDescent.
      const refW = this.state.canvas.refWidth || sourceW;
      const coordScale = sourceW > 0 ? sourceW / refW : 1;
      const isNonTSeries = this.state.selection.typeHandle === "non-t-series";
      const padTop = isNonTSeries ? 8 * coordScale : 0;

      const fontFamily = this.state.canvas.condensedFont ? "Roboto Condensed, Roboto" : "Roboto";
      ctx.fillStyle = this.state.canvas.color;
      ctx.textAlign = this.state.canvas.textAlign;
      ctx.textBaseline = this.state.canvas.textBaseline;
      textCtx.fillStyle = this.state.canvas.color;
      textCtx.textAlign = this.state.canvas.textAlign;
      textCtx.textBaseline = this.state.canvas.textBaseline;

      // Reserve descender space BEFORE deriving the final font size — carving it out of
      // post-sizing centering slack silently fails when lines nearly fill the area. Two
      // kinds of reservation, both measured at a PROVISIONAL font size (bootstrap
      // estimate, re-measured at the final size below):
      //  1. Inter-line: extra gap between adjacent lines whose ink would collide
      //     (descenders like "p"/"q"/"@" hitting the line below).
      //  2. Bottom: the last line's real descent so its glyphs aren't clipped at the
      //     label area's bottom edge. The inter-line reservation goes into the
      //     availableH subtraction only (not added to the Non-T 8px designPadBottom),
      //     so that constant isn't duplicated.
      // Uses per-string ink metrics so a line with no descenders reserves ~0 instead
      // of always reserving the font's max possible descent.
      const designPadBottom = isNonTSeries ? 8 * coordScale : 0;
      const provisionalFontSize =
        labelAreaH > 0
          ? (labelAreaH - padTop - designPadBottom) / maxLines / LABEL_RENDER.LINE_HEIGHT_MULT
          : this.state.canvas.fontSize * coordScale;
      // textBaseline "top" corrupts (swaps ascent/descent of) fontBoundingBoxAscent/
      // Descent in some browsers, even though the spec defines them as
      // baseline-independent — switch to "alphabetic" just for the measurement as a
      // precaution, since actualBoundingBoxDescent hasn't been confirmed unaffected.
      // Weights match the fill loop below: line 1 bold, rest regular.
      ctx.textBaseline = "alphabetic";
      const provisionalInk = lines.map((/** @type {any} */ t, /** @type {any} */ i) => {
        ctx.font = `${i === 0 ? "bold " : ""}${provisionalFontSize}px ${fontFamily}`;
        return measureLineInk(ctx, t, provisionalFontSize);
      });
      ctx.textBaseline = this.state.canvas.textBaseline;
      let reservedH = 0;
      if (labelAreaH > 0 && lines.length) {
        reservedH = Math.max(0, provisionalInk[lines.length - 1].descent);
        for (let i = 0; i < lines.length - 1; i++) {
          reservedH += Math.max(0, extraLineGap(provisionalInk[i], provisionalInk[i + 1], provisionalFontSize));
        }
      }

      let fontSize, lineHeight;
      if (labelAreaH > 0) {
        const availableH = Math.max(0, labelAreaH - padTop - designPadBottom - reservedH);
        lineHeight = availableH / maxLines;
        fontSize = lineHeight / LABEL_RENDER.LINE_HEIGHT_MULT;
      } else {
        fontSize = this.state.canvas.fontSize * coordScale;
        lineHeight = fontSize * LABEL_RENDER.LINE_HEIGHT_MULT;
      }

      // Height-only sizing can produce a font too large for a line at the actual character
      // limit (28 normal / 30 condensed) to fit within labelAreaWidth. Cap font size using
      // that fixed limit — not the current text — so it stays safe for the widest line the
      // user is allowed to type, without reintroducing per-keystroke resizing.
      const textLabelAreaW = this.state.canvas.labelAreaW;
      if (textLabelAreaW > 0) {
        const fontMode = isNonTSeries && this.state.canvas.condensedFont ? FONT_MODE.condensed : FONT_MODE.normal;
        const referenceText = WIDTH_REFERENCE_STRING.slice(0, fontMode.maxChars);
        // Bold weight matches line1's actual rendered weight (see fillText loop below,
        // idx === 0 uses "bold") — measuring at regular weight underestimates line1's
        // width, letting it size larger here than the admin overlay allows.
        ctx.font = `bold ${fontSize}px ${fontFamily}`;
        const referenceWidth = ctx.measureText(referenceText).width;
        if (referenceWidth > textLabelAreaW) {
          const shrink = textLabelAreaW / referenceWidth;
          fontSize *= shrink;
          lineHeight *= shrink;
        }
      }

      const textX = this.state.canvas.paddingLeft;
      const textY = this.state.canvas.textY;
      // Per-line ink at the FINAL (possibly width-capped) font size drives the actual
      // inter-line gaps and the bottom reservation used for centering. Re-measured
      // with "alphabetic" baseline as a precaution (see above); weights match the fill
      // loop below (line 1 bold, rest regular).
      let lineGaps = [];
      let lastLineDescent = 0;
      if (labelAreaH > 0 && lines.length) {
        ctx.textBaseline = "alphabetic";
        const finalInk = lines.map((/** @type {any} */ t, /** @type {any} */ i) => {
          ctx.font = `${i === 0 ? "bold " : ""}${fontSize}px ${fontFamily}`;
          return measureLineInk(ctx, t, fontSize);
        });
        ctx.textBaseline = this.state.canvas.textBaseline;
        lineGaps = [];
        for (let i = 0; i < lines.length - 1; i++) {
          lineGaps.push(lineHeight + Math.max(0, extraLineGap(finalInk[i], finalInk[i + 1], fontSize)));
        }
        lastLineDescent = Math.max(0, finalInk[lines.length - 1].descent);
      }

      // Center the actual lines within the label area, using the ACTUAL leftover space
      // (box height minus fixed design padding minus real block height, including the
      // extra inter-line gaps and lastLineDescent) rather than "emptySlots * lineHeight"
      // — that shortcut only balances when lineHeight still equals the height-only
      // value; once the width cap shrinks it, the leftover must be measured directly.
      // Inter-line gaps are internal to the text block (minimum spacings between two
      // specific lines); only the last line's descent is trailing space, so it stays
      // bottom-only — splitting it into the top/bottom leftover would visibly shift the
      // text block upward. When no descenders are present all gaps equal lineHeight and
      // the block height reduces to n*lineHeight + lastLineDescent — identical to the
      // previous last-line-only behavior.
      const gapExcess = lineGaps.reduce((sum, g) => sum + (g - lineHeight), 0);
      const contentHeight = lines.length * lineHeight + gapExcess + lastLineDescent;
      const designPadTop = padTop;
      const leftoverH = labelAreaH > 0
        ? Math.max(0, labelAreaH - designPadTop - designPadBottom - contentHeight)
        : 0;
      const startY = labelAreaH > 0
        ? textY + designPadTop + leftoverH / 2
        : textY;

      let curY = startY;
      lines.forEach((/** @type {any} */ text, /** @type {any} */ idx) => {
        const font = idx === 0 ? `bold ${fontSize}px ${fontFamily}` : `${fontSize}px ${fontFamily}`;
        ctx.font = font;
        ctx.fillText(text, textX, curY);
        textCtx.font = font;
        textCtx.fillText(text, textX, curY);
        // Advance by the pair's gap (descender clearance when needed); the last line
        // needs no advance — its descent is already in contentHeight.
        if (idx < lines.length - 1) curY += lineGaps[idx] ?? lineHeight;
      });

      // Dashed outlines — mirrors ThermostatWithLabelOverlay in the admin repo.
      // Scale line/dash sizes from CSS-pixel equivalents to canvas units.
      // Use sourceW (source-image space) since ctx.scale() handles buffer mapping.
      const refW2 = this.state.canvas.refWidth || sourceW;
      const borderScale = sourceW > 0 ? sourceW / refW2 : 1;
      const lw = Math.max(1, 1.5 * borderScale);
      const dash = [4 * borderScale, 2 * borderScale];

      ctx.lineWidth = lw;
      ctx.setLineDash(dash);

      // Text label area — blue
      const { labelAreaW, labelAreaH: laH, paddingLeft: lx, textY: ly } = this.state.canvas;
      if (laH > 0 && labelAreaW > 0) {
        ctx.strokeStyle = "rgba(59,130,246,0)";
        ctx.strokeRect(lx, ly, labelAreaW, laH);
      }

      ctx.setLineDash([]); // reset dash

      if (!apnPreview) {
        this.drawZoomCanvas(textLayer, sourceW, sourceH, confirmation);
      }
    };
  }

  // Hides the zoomed label preview box entirely until at least one line has content.
  /** @param {boolean} hasContent @param {boolean} [confirmation] */
  toggleZoomPreview(hasContent, confirmation = false) {
    // Scope by step: the creation step renders into the main group, the confirmation
    // step into its own (mirrors drawZoomCanvas' canvas targeting).
    const zoomGroup = document.querySelector(
      confirmation ? ".rs-canvas-zoom-group-confirmation" : ".rs-canvas-zoom-group:not(.rs-canvas-zoom-group-confirmation)"
    );
    if (zoomGroup) {
      /** @type {any} */ (zoomGroup).style.display = hasContent ? "" : "none";
    }
  }

  // Crops the label-area rect out of the text-only layer (transparent background,
  // same text pixels as the main canvas) and draws it magnified into the zoom canvas
  // over a flat light-gray fill, so the zoom shows the label text without the
  // product photo behind it.
  /** @param {any} sourceCanvas @param {any} sourceW @param {any} sourceH @param {boolean} [confirmation] */
  drawZoomCanvas(sourceCanvas, sourceW, sourceH, confirmation = false) {
    const zoomCanvas = /** @type {any} */ (document.getElementById(
      confirmation ? "rs-canvas-zoom-confirmation" : "rs-canvas-zoom"
    ));
    if (!zoomCanvas) return;

    const { labelAreaW, labelAreaH, paddingLeft: lx, textY: ly } = this.state.canvas;
    if (!(labelAreaW > 0) || !(labelAreaH > 0)) {
      zoomCanvas.style.display = "none";
      return;
    }
    zoomCanvas.style.display = "";

    // Crop tightly to the full label area rect (labelAreaW × labelAreaH, sized for
    // maxLines) — no surrounding thermostat body/bezel. Always the full box, not just
    // the active lines, so the zoom stays a fixed size as more lines are typed; text
    // recenters within it instead.
    const marginRatio = 0;
    const marginX = labelAreaW * marginRatio;
    const marginY = labelAreaH * marginRatio;
    const cropX = Math.max(0, lx - marginX);
    const cropY = Math.max(0, ly - marginY);
    const cropW = Math.min(sourceW - cropX, labelAreaW + marginX * 2);
    const cropH = Math.min(sourceH - cropY, labelAreaH + marginY * 2);

    const dpr = window.devicePixelRatio || 1;
    // Source rect is in CSS-pixel/source-image space; sourceCanvas (the text layer) is
    // oversampled by dpr * ZOOM_TEXT_RENDER_SCALE, so scale the crop rect into that
    // buffer's pixels to sample it.
    const textLayerScale = dpr * ZOOM_TEXT_RENDER_SCALE;
    const sx = cropX * textLayerScale;
    const sy = cropY * textLayerScale;
    const sw = cropW * textLayerScale;
    const sh = cropH * textLayerScale;

    // Scale the crop up to fill a box capped at ZOOM_MAX_WIDTH/HEIGHT, fitting both
    // dimensions so the zoom's aspect ratio matches the label area's real proportions.
    const maxW = Math.min(sourceW, ZOOM_MAX_WIDTH);
    const maxH = Math.min(sourceH, ZOOM_MAX_HEIGHT);
    const scale = Math.min(maxW / cropW, maxH / cropH);
    const destW = Math.round(cropW * scale);
    const destH = Math.round(cropH * scale);

    const bufW = Math.round(destW * dpr);
    const bufH = Math.round(destH * dpr);
    if (zoomCanvas.width !== bufW || zoomCanvas.height !== bufH) {
      zoomCanvas.width = bufW;
      zoomCanvas.height = bufH;
    }
    zoomCanvas.style.width = `${destW}px`;
    zoomCanvas.style.height = "auto";

    const zoomCtx = zoomCanvas.getContext("2d");
    zoomCtx.setTransform(1, 0, 0, 1, 0, 0);
    zoomCtx.imageSmoothingEnabled = true;
    zoomCtx.imageSmoothingQuality = "high";
    zoomCtx.clearRect(0, 0, zoomCanvas.width, zoomCanvas.height);
    zoomCtx.fillStyle = "#F5F5F5";
    zoomCtx.fillRect(0, 0, zoomCanvas.width, zoomCanvas.height);
    zoomCtx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, zoomCanvas.width, zoomCanvas.height);
  }

  mountConfirmationCanvas() {
    if (!this.els.canvas || !this.els.canvasWrapper) return;

    let existing = document.getElementById("rs-canvas-confirmation");
    if (existing) {
      // Sync dimensions to the current product selection in case it changed since last visit.
      existing.setAttribute("width", String(this.state.canvas.width));
      existing.setAttribute("height", String(this.state.canvas.height));
      return;
    }

    const clone = this.els.canvas.cloneNode(true);
    clone.id = "rs-canvas-confirmation";
    this.els.canvasWrapper.appendChild(clone);
  }

  // =========================================================
  // CONFIRMATION TEXT
  // =========================================================
  renderConfirmationText() {
    const accountNumber = this.getAccountId() || "";
    const _typesForConf = /** @type {any[]} */ (this.state.selection.types);
    const _matchedType = _typesForConf.find(
      (t) => this._getLabelTypeCategory(t?.title || "") === this.state.selection.typeHandle
    );
    const typeText = _matchedType?.title || (this.state.selection.typeHandle
      ? this.state.selection.typeHandle.replace(/-/g, " ")
      : "");

    this.els.confAccount &&
      (this.els.confAccount.textContent = `${this.state.confirmationPrefix.accountNumber} ${accountNumber}`);

    this.els.confLabelType &&
      (this.els.confLabelType.textContent = `${this.state.confirmationPrefix.labelType} ${typeText}`);

    this.els.confContractor &&
      (this.els.confContractor.textContent = `${this.state.confirmationPrefix.contractorName} ${this.state.form.contractorName}`);

    if (this.els.confLine1) {
      this.els.confLine1.textContent = `${this.state.confirmationPrefix.line1} ${this.state.form.line1}`;
      this.els.confLine1.closest(".rs-pl-line-1")?.classList.toggle("rs-hidden", !this.state.form.line1.trim());
    }

    if (this.els.confLine2) {
      this.els.confLine2.textContent = `${this.state.confirmationPrefix.line2} ${this.state.form.line2}`;
      this.els.confLine2.closest(".rs-pl-line-2")?.classList.toggle("rs-hidden", !this.state.form.line2.trim());
    }

    if (this.els.confLine3) {
      this.els.confLine3.textContent = `${this.state.confirmationPrefix.line3} ${this.state.form.line3}`;
      this.els.confLine3.closest(".rs-pl-line-3")?.classList.toggle("rs-hidden", !this.state.form.line3.trim());
    }

    if (this.els.confLine4) {
      this.els.confLine4.textContent = `${this.state.confirmationPrefix.line4} ${this.state.form.line4}`;
      const line4Row = this.els.confLine4.closest(".rs-pl-line-4");
      line4Row?.classList.toggle("rs-hidden", !this.state.form.line4.trim() || LABEL_TYPE[this.state.selection.typeHandle]?.maxLines !== 4);
    }
  }

  // =========================================================
  // CREATE PRIVATE LABEL (POST)
  // =========================================================
  /** @param {any} canvas */
  canvasToBase64(canvas, type = "image/png", quality = 0.92) {
    return type === "image/jpeg" ? canvas.toDataURL(type, quality) : canvas.toDataURL(type);
  }

  /** @param {{ canvas?: any; image?: any; fullSize?: any; minimizedWidth?: any; mimeType?: string; jpegQuality?: number }} [opts] */
  async buildPreviewImagesFromCanvasBase64({
    canvas,
    image,
    fullSize,
    minimizedWidth,
    mimeType = "image/png",
    jpegQuality = 0.92,
  } = {}) {
    if (!canvas) throw new Error("canvas is required");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Unable to get 2D context");

    if (image) {
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = image;
      });

      const w = fullSize?.width ?? img.naturalWidth ?? img.width;
      const h = fullSize?.height ?? img.naturalHeight ?? img.height;

      canvas.width = w;
      canvas.height = h;

      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      const lines = this.els.canvasInputs.map((/** @type {any} */ i) => i?.value?.trim()).filter(Boolean);
      ctx.font = `${this.state.canvas.fontSize}px Arial`;
      ctx.fillStyle = this.state.canvas.color;
      ctx.textAlign = this.state.canvas.textAlign;
      ctx.textBaseline = this.state.canvas.textBaseline;

      const anchorY = h - this.state.canvas.paddingBottom;
      const totalH = lines.length * this.state.canvas.lineHeight;
      const startY = anchorY - totalH / 2;

      lines.forEach((/** @type {any} */ text, /** @type {any} */ idx) => {
        ctx.fillText(text, this.state.canvas.paddingLeft, startY + idx * this.state.canvas.lineHeight);
      });
    }

    let fullPreviewOnImage;
    try {
      fullPreviewOnImage = this.canvasToBase64(canvas, mimeType, jpegQuality);
    } catch (e) {
      const _e = /** @type {any} */ (e);
      const msg = String(_e?.message || "").toLowerCase();
      if (_e?.name === "SecurityError" || msg.includes("tainted")) {
        throw new Error(
          "Canvas export blocked (tainted). The drawn image URL is cross-origin without CORS. Use a same-origin proxy URL for `image`."
        );
      }
      throw e;
    }

    let minimizedPreviewImage = null;
    const mw = Number(minimizedWidth);
    if (mw > 0) {
      const fullW = canvas.width;
      const fullH = canvas.height;
      const ratio = fullH / fullW;

      const miniW = Math.max(1, Math.round(mw));
      const miniH = Math.max(1, Math.round(miniW * ratio));

      const miniCanvas = document.createElement("canvas");
      miniCanvas.width = miniW;
      miniCanvas.height = miniH;

      const miniCtx = miniCanvas.getContext("2d");
      if (!miniCtx) throw new Error("Unable to get 2D context for mini canvas");
      miniCtx.drawImage(canvas, 0, 0, fullW, fullH, 0, 0, miniW, miniH);

      minimizedPreviewImage = this.canvasToBase64(miniCanvas, mimeType, jpegQuality);
    }

    return { fullPreviewOnImage, minimizedPreviewImage };
  }

  async createPrivateLabel({ pending = true } = {}) {
    // Always sync form state from the live inputs so values are never stale.
    // The "go confirmation" handler also does this, but other save paths (edit
    // mode, direct confirmation navigation) may not have gone through it.
    const inputs = /** @type {any[]} */ (this.els.canvasInputs);
    if (inputs?.[0]?.value?.trim()) this.state.form.line1 = inputs[0].value.trim();
    if (inputs?.[1]?.value?.trim()) this.state.form.line2 = inputs[1].value.trim();
    if (inputs?.[2]?.value !== undefined) this.state.form.line3 = inputs[2].value.trim();
    if (inputs?.[3]?.value !== undefined) this.state.form.line4 = inputs[3].value.trim();
    // Always sync contractorName from the DOM input so we capture whatever the user has
    // typed at the time of save, regardless of whether state was previously populated.
    const _nameFromInput = /** @type {HTMLInputElement|null} */ (document.querySelector('.rs-input[name="name"]'))?.value?.trim();
    if (_nameFromInput) this.state.form.contractorName = _nameFromInput;

    // Ensure the customer context (and its sales_area) has actually resolved before
    // reading it — company_id/myJWT being ready doesn't guarantee sales_area is present,
    // so this still needs its own check below rather than trusting the wait alone.
    await Promise.race([
      customerContextWait,
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);

    const salesOrganization = this.getSalesOrg();
    const distributionChannel = this.getDistChannel();
    if (!salesOrganization || !distributionChannel) {
      throw new Error(
        "Unable to determine your account's sales area. Please refresh the page and try again."
      );
    }

    const payload = {
      accountId: this.getAccountId(),
      typeId: this.state.selection.typeId,
      condensedFont: this.state.canvas.condensedFont,
      salesOrganization,
      distributionChannel,
      // On a PATCH (edit), only send plcaStatus when promoting to Pending.
      // Sending "Draft" on an already-Draft record triggers a backend rejection.
      ...(pending || !this.state.edit.enabled ? { plcaStatus: pending ? "Pending" : "Draft" } : {}),

      contractorName: this.state.form.contractorName,
      emailAddress: document.body.getAttribute("data-customer-email"),
      partNumbers: this.state.canvas.partNumbers,
      secondEmailAddress: null,
      notifyDistributor: false,

      line1: this.state.form.line1,
      line2: this.state.form.line2,
      line3: this.state.form.line3 || null,
      line4: this.state.form.line4 || null,
      previewImageId: this.state.canvas.previewOnImageId || null,
    };

    let method = "POST";

    if (this.state.edit.enabled && this.state.edit.labelId) {
      /** @type {any} */ (payload).id = this.state.edit.labelId;
      method = "PATCH";
    }


    return this.request("/privateLabel", { method: method, body: payload });
  }

  /** @param {any} plcaId */
  async deleteDraftLabel(plcaId) {
    const params = new URLSearchParams({ id: plcaId, accountId: this.getAccountId() || "" });
    return this.request(`/privateLabel?${params.toString()}`, { method: "DELETE" });
  }

  // =========================================================
  // PREVIEW MODALS
  // =========================================================

  /** Map type title → category handle (mirrors admin getLabelTypeCategory). */
  _getLabelTypeCategory(title = "") {
    if (title === "T Series Digital") return "t-series-digital";
    if (title === "Non-T Series")     return "non-t-series";
    return "t-series";
  }

  /** Render the label overlay onto thermostatWrap for a given image record. */
  /** @param {any} thermostatWrap @param {any} imgEl @param {any} selImg @param {any} lines @param {any} condensedFont @param {any} maxLines @param {boolean} [isLogo] @param {string} [labelImageUrl] */
  _buildLabelOverlay(thermostatWrap, imgEl, selImg, lines, condensedFont, maxLines, isLogo = false, labelImageUrl = "", typeHandle = "") {
    if (!imgEl.isConnected) return;
    thermostatWrap.querySelectorAll(".rs-preview-ov").forEach((/** @type {any} */ el) => el.remove());

    const natW  = imgEl.naturalWidth;
    const dispW = imgEl.clientWidth;
    const dispH = imgEl.clientHeight;
    if (!dispW || !natW) return;

    const coordW = parseFloat(selImg.sourceImgWidth  ?? "0") || natW;
    const coordH = parseFloat(selImg.sourceImgHeight ?? "0") || imgEl.naturalHeight;
    const scaleX = dispW / coordW;
    const scaleY = dispH / coordH;

    const xPos = parseFloat(selImg.xPosition       ?? "0") || 0;
    const yPos = parseFloat(selImg.yPosition       ?? "0") || 0;
    const laW  = parseFloat(selImg.labelAreaWidth  ?? "0") || 0;
    const laH  = parseFloat(selImg.labelAreaHeight ?? "0") || 0;

    if (isLogo && labelImageUrl) {
      const logoOverlay = document.createElement("img");
      logoOverlay.className = "rs-preview-ov";
      logoOverlay.src = labelImageUrl;
      logoOverlay.alt = "Logo Label Preview";
      logoOverlay.style.cssText = [
        "position:absolute;pointer-events:none;object-fit:contain;",
        `top:${yPos * scaleY}px;left:${xPos * scaleX}px;`,
        laW > 0 ? `width:${laW * scaleX}px;` : "max-width:60%;",
        laH > 0 ? `height:${laH * scaleY}px;` : "height:auto;",
      ].join("");
      thermostatWrap.appendChild(logoOverlay);
      return;
    }

    // Extra top/bottom padding only for Non-T Series labels
    const isNonTSeries = typeHandle === "non-t-series";
    const overlayPadTop = laH > 0 && isNonTSeries ? 8 * scaleY : 0;
    const overlayPadBottom = laH > 0 && isNonTSeries ? 8 * scaleY : 0;
    // Font size is derived from the label area's own size (rectDisplayH), not from the
    // source image's dimensions — the label area already represents the actual print
    // label bounds, so sizing off anything else (e.g. image resolution) is wrong.
    const rectDisplayH = laH * scaleY;
    const overlayFontFamily = condensedFont ? "Roboto Condensed, Roboto" : "Roboto";
    const rawAvailableH = laH > 0 ? rectDisplayH - overlayPadTop - overlayPadBottom : 0;

    // Reserve descender space BEFORE deriving the final font size (carving it out of
    // post-sizing centering slack silently fails when lines nearly fill the area):
    //  1. Inter-line: extra gap between adjacent lines whose ink would collide.
    //  2. Bottom: the last line's real descent so its glyphs aren't clipped at the
    //     label area's bottom edge.
    // Both measured at a PROVISIONAL font size; re-measured at the final size below for
    // the actual gaps and centering. Mirrors web/lib/labelTextLayout.ts in the admin
    // repo. Weights match the render below: line 1 bold, rest regular.
    const _activeForInk = lines.filter((/** @type {any} */ l) => l.trim());
    let reservedH = 0;
    if (rawAvailableH > 0 && _activeForInk.length) {
      const provisionalFontSize = rawAvailableH / maxLines / LABEL_RENDER.LINE_HEIGHT_MULT;
      const provCanvas = document.createElement("canvas");
      const provCtx = provCanvas.getContext("2d");
      if (provCtx) {
        const provInk = _activeForInk.map((/** @type {any} */ t, /** @type {any} */ i) => {
          provCtx.font = `${i === 0 ? "bold " : ""}${provisionalFontSize}px ${overlayFontFamily}`;
          return measureLineInk(provCtx, t, provisionalFontSize);
        });
        reservedH = Math.max(0, provInk[_activeForInk.length - 1].descent);
        for (let i = 0; i < _activeForInk.length - 1; i++) {
          reservedH += Math.max(0, extraLineGap(provInk[i], provInk[i + 1], provisionalFontSize));
        }
      }
    }

    let lineHeight   = laH > 0 ? Math.max(0, rawAvailableH - reservedH) / maxLines : 11 * scaleY;
    let fontSize     = lineHeight / LABEL_RENDER.LINE_HEIGHT_MULT;

    // Height-only sizing can produce a font too large for a line at the actual character
    // limit (28 normal / 30 condensed) to fit within labelAreaWidth. Cap font size using
    // that fixed limit — not the current text — so it stays safe for the widest line the
    // user is allowed to type, without reintroducing per-keystroke resizing.
    if (laW > 0) {
      const fontMode = isNonTSeries && condensedFont ? FONT_MODE.condensed : FONT_MODE.normal;
      const referenceText = WIDTH_REFERENCE_STRING.slice(0, fontMode.maxChars);
      const measureCanvas = document.createElement("canvas");
      const measureCtx = measureCanvas.getContext("2d");
      if (measureCtx) {
        const rectDisplayW = laW * scaleX;
        // Bold weight matches line1's actual rendered weight (font-weight:700 below) —
        // measuring at regular weight would underestimate line1's width.
        measureCtx.font = `bold ${fontSize}px ${overlayFontFamily}`;
        const referenceWidth = measureCtx.measureText(referenceText).width;
        if (referenceWidth > rectDisplayW) {
          const shrink = rectDisplayW / referenceWidth;
          fontSize *= shrink;
          lineHeight *= shrink;
        }
      }
    }

    if (laH > 0 && laW > 0) {
      const rect = document.createElement("div");
      rect.className = "rs-preview-ov";
      rect.style.cssText = [
        "position:absolute;pointer-events:none;box-sizing:border-box;",
        `top:${yPos * scaleY}px;left:${xPos * scaleX}px;`,
        `width:${laW * scaleX}px;height:${laH * scaleY}px;`,
        "border:1.5px dashed rgba(59,130,246,0);",
      ].join("");
      thermostatWrap.appendChild(rect);
    }

    const activeLines = lines.filter((/** @type {any} */ l) => l.trim());
    if (!activeLines.length) return;

    // Per-line ink at the FINAL (possibly width-capped) font size drives the actual
    // inter-line gaps and the bottom reservation used for centering.
    let lineGaps = [];
    let lastLineDescent = 0;
    if (rawAvailableH > 0 && activeLines.length) {
      const finalCanvas = document.createElement("canvas");
      const finalCtx = finalCanvas.getContext("2d");
      if (finalCtx) {
        const finalInk = activeLines.map((/** @type {any} */ t, /** @type {any} */ i) => {
          finalCtx.font = `${i === 0 ? "bold " : ""}${fontSize}px ${overlayFontFamily}`;
          return measureLineInk(finalCtx, t, fontSize);
        });
        lineGaps = [];
        for (let i = 0; i < activeLines.length - 1; i++) {
          lineGaps.push(lineHeight + Math.max(0, extraLineGap(finalInk[i], finalInk[i + 1], fontSize)));
        }
        lastLineDescent = Math.max(0, finalInk[activeLines.length - 1].descent);
      }
    }

    // Center the actual lines within the label area. Computed from the ACTUAL leftover
    // space (box height minus fixed padding minus real block height at the final,
    // possibly width-capped metrics, including the extra inter-line gaps and
    // lastLineDescent) rather than "emptySlots * lineHeight" — that shortcut only
    // balances against the box height when lineHeight still equals the height-only
    // value; once the width cap shrinks it, every line (not just the empty slots) takes
    // up less space, so the leftover must be measured directly. Inter-line gaps are
    // internal to the text block (minimum spacings between two specific lines); only
    // the last line's descent is trailing space, so it stays bottom-only — splitting it
    // into the top/bottom leftover would visibly shift the text block upward. When no
    // descenders are present all gaps equal lineHeight and the block height reduces to
    // n*lineHeight + lastLineDescent — identical to the previous behavior.
    const gapExcess = lineGaps.reduce((sum, g) => sum + (g - lineHeight), 0);
    const contentHeight = activeLines.length * lineHeight + gapExcess + lastLineDescent;
    const leftoverH = laH > 0 ? Math.max(0, rectDisplayH - overlayPadTop - overlayPadBottom - contentHeight) : 0;
    const centeringPadTop = overlayPadTop + leftoverH / 2;
    const centeringPadBottom = overlayPadBottom + leftoverH / 2 + lastLineDescent;

    const textArea = document.createElement("div");
    textArea.className = "rs-preview-ov";
    textArea.style.cssText = [
      "position:absolute;pointer-events:none;overflow:hidden;box-sizing:border-box;",
      "display:flex;flex-direction:column;justify-content:flex-start;",
      `top:${yPos * scaleY}px;left:${xPos * scaleX}px;`,
      laW > 0 ? `width:${laW * scaleX}px;` : "",
      laH > 0 ? `height:${laH * scaleY}px;` : "",
      `padding-top:${centeringPadTop}px;padding-bottom:${centeringPadBottom}px;`,
    ].join("");

    const inner = document.createElement("div");
    inner.style.cssText = [
      "width:100%;color:#505050;",
      `font-size:${fontSize}px;line-height:${lineHeight}px;font-weight:400;`,
      condensedFont ? "font-family:Roboto Condensed,Roboto;" : "font-family:Roboto;",
      condensedFont ? "letter-spacing:-0.02em;" : "letter-spacing:0.01em;",
    ].join("");

    activeLines.forEach((/** @type {any} */ line, /** @type {any} */ idx) => {
      const lineEl = document.createElement("div");
      // margin-bottom adds the descender clearance above the next line (0 unless needed).
      const marginBottom = idx < activeLines.length - 1
        ? `margin-bottom:${(lineGaps[idx] ?? lineHeight) - lineHeight}px;`
        : "";
      lineEl.style.cssText =
        "white-space:nowrap;overflow:hidden;" + (idx === 0 ? "font-weight:700;" : "") + marginBottom;
      lineEl.textContent = line;
      inner.appendChild(lineEl);
    });
    textArea.appendChild(inner);
    thermostatWrap.appendChild(textArea);
  }

  /**
   * @param {{ lines: string[]; condensedFont: boolean; typeId: string; maxLines: number;
   *           savedImgId?: string; partSetupId?: string; typeHandle?: string;
   *           alwaysShowDropdown?: boolean; onSelectChange?: ((id: string) => void)|null;
   *           isLogo?: boolean; labelImageUrl?: string }} opts
   */
  async _openPreviewModal({ lines, condensedFont, typeId, maxLines, savedImgId = "", partSetupId = "", typeHandle = "", alwaysShowDropdown = true, onSelectChange = null, isLogo = false, labelImageUrl = "" }) {
    this._closeListingPreviewModal(true);

    let images = /** @type {any[]} */ ([]);
    try {
      const _plpCompanyName = this.getCompanyName() || "";
      const _plpCompanyExternalId = this.getAccountId() || "";
      const cacheKey = `${typeId}:${partSetupId}:${_plpCompanyExternalId}:${_plpCompanyName}`;
      if (this._previewImageCache.has(cacheKey)) {
        images = this._previewImageCache.get(cacheKey);
      } else {
        const reqBody = /** @type {any} */ ({
          privateLabelTypeId: typeId,
          companyExternalId: _plpCompanyExternalId,
          companyName: _plpCompanyName,
        });
        if (partSetupId) reqBody.privateLabelPartSetupId = partSetupId;
        const data = await this.request("/privateLabelImage/list", { method: "POST", body: reqBody });
        images = data?.privateLabelImages || [];
        if (typeof data?.fergusonFilteringEnabled === "boolean") this.state.fergusonFilteringEnabled = data.fergusonFilteringEnabled;
        this._previewImageCache.set(cacheKey, images);
      }
    } catch (err) {
      console.error("[preview modal] fetch images error:", err);
    }
    if (!images.length) return false;

    // ── Backdrop ─────────────────────────────────────────────────
    const backdrop = document.createElement("div");
    backdrop.setAttribute("role", "dialog");
    backdrop.setAttribute("aria-modal", "true");
    backdrop.style.cssText = "position:fixed;inset:0;z-index:10000000;background:rgba(0,0,0,0.6);";
    backdrop.addEventListener("click", () => this._closeListingPreviewModal());

    // ── Modal card ───────────────────────────────────────────────
    const modal = document.createElement("div");
    modal.style.cssText = [
      "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);",
      "z-index:10000001;background:#fff;width:90vw;max-width:600px;",
      "border-radius:8px;box-shadow:0 10px 25px rgba(0,0,0,0.3);",
      "overflow:hidden;display:flex;flex-direction:column;max-height:90vh;",
    ].join("");
    modal.addEventListener("click", (e) => e.stopPropagation());

    // ── Header ───────────────────────────────────────────────────
    const header = document.createElement("div");
    header.style.cssText =
      "display:flex;align-items:center;justify-content:space-between;" +
      "padding:24px 24px 16px;border-bottom:1px solid #E5E7EB;flex-shrink:0;";
    const title = document.createElement("h2");
    title.textContent = "Private Label Preview";
    title.style.cssText = "font-size:20px;font-weight:600;margin:0;";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"' +
      ' fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    closeBtn.style.cssText =
      "border:none;background:none;cursor:pointer;padding:4px;display:flex;" +
      "align-items:center;color:#6B7280;";
    closeBtn.addEventListener("click", () => this._closeListingPreviewModal());
    header.appendChild(title);
    header.appendChild(closeBtn);

    // ── Body ─────────────────────────────────────────────────────
    const body = document.createElement("div");
    body.style.cssText = "padding:24px;overflow-y:auto;";

    const showDropdown = alwaysShowDropdown || images.length > 1;
    const select = document.createElement("select");
    select.style.cssText =
      "width:100%;height:36px;padding:0 8px;font-size:14px;" +
      "border:1px solid #D1D5DB;border-radius:6px;background:#fff;margin-bottom:16px;";
    if (showDropdown) {
      const dropLabel = document.createElement("label");
      dropLabel.textContent = "Preview on Product";
      dropLabel.style.cssText = "display:block;font-size:14px;font-weight:500;margin-bottom:4px;";
      images.forEach((img) => {
        const opt = document.createElement("option");
        opt.value = String(img.id ?? "");
        opt.textContent = (img.partName || "").trim() || "(Unknown)";
        select.appendChild(opt);
      });
      body.appendChild(dropLabel);
      body.appendChild(select);
    }

    const imageArea = document.createElement("div");
    imageArea.style.cssText =
      "background:#F7F7F7;border-radius:8px;padding:32px;display:flex;justify-content:center;";
    const thermostatWrap = document.createElement("div");
    thermostatWrap.style.cssText = "position:relative;width:100%;max-width:380px;";
    imageArea.appendChild(thermostatWrap);
    body.appendChild(imageArea);

    modal.appendChild(header);
    modal.appendChild(body);
    document.body.appendChild(backdrop);
    document.body.appendChild(modal);

    /** @type {ResizeObserver|null} */
    this._previewRO = null;
    this._previewModal   = modal;
    this._previewBackdrop = backdrop;

    const self = this;
    let previewToken = 0;
    function renderPreview(/** @type {any} */ selectedImgId) {
      const selImg = images.find((i) => String(i.id ?? "") === String(selectedImgId)) || images[0];
      if (self._previewRO) { self._previewRO.disconnect(); self._previewRO = null; }
      thermostatWrap.innerHTML = "";
      if (!selImg) return;

      const imgEl = document.createElement("img");
      imgEl.src   = selImg.optimizedImage?.url || "";
      imgEl.alt   = "Thermostat Preview";
      imgEl.style.cssText = "width:100%;height:auto;display:block;";
      thermostatWrap.appendChild(imgEl);

      // Wait for the label font so _buildLabelOverlay's ink metrics match the
      // server-rendered JPG/BMP (see ensureLabelFontLoaded). The token guards
      // against a stale async completion redrawing after the modal closed or the
      // selection changed.
      const overlay = async () => {
        await ensureLabelFontLoaded(condensedFont ? "Roboto Condensed" : "Roboto");
        const token = ++previewToken;
        if (self._previewModal !== modal) return;
        self._buildLabelOverlay(thermostatWrap, imgEl, selImg, lines, condensedFont, maxLines, isLogo, labelImageUrl, typeHandle);
        if (window.ResizeObserver) {
          const ro = new ResizeObserver(() => {
            if (token === previewToken && self._previewModal === modal) {
              self._buildLabelOverlay(thermostatWrap, imgEl, selImg, lines, condensedFont, maxLines, isLogo, labelImageUrl, typeHandle);
            }
          });
          ro.observe(imgEl);
          self._previewRO = ro;
        }
      };
      if (imgEl.complete && imgEl.naturalWidth > 0) {
        overlay();
      } else {
        imgEl.addEventListener("load", overlay);
      }
    }

    const initialImg = (savedImgId && images.find((i) => String(i.id ?? "") === String(savedImgId)))
      ? savedImgId
      : images[0]?.id;
    select.value = String(initialImg ?? "");
    renderPreview(initialImg);
    if (showDropdown) {
      select.addEventListener("change", () => {
        if (onSelectChange) onSelectChange(select.value);
        renderPreview(select.value);
      });
    }
    return true;
  }

  async _openApnPreviewModal(partSetupId = "") {
    const savedImgId = this.state.canvas.previewOnImageId || this.state.edit.previewOnImageId || "";
    await this._openPreviewModal({
      lines: [this.state.form.line1 || "", this.state.form.line2 || "", this.state.form.line3 || "", this.state.form.line4 || ""],
      condensedFont: !!this.state.canvas.condensedFont,
      typeId: this.state.selection.typeId || "",
      typeHandle: this.state.selection.typeHandle || "",
      maxLines: LABEL_TYPE[this.state.selection.typeHandle]?.maxLines ?? 3,
      savedImgId,
      partSetupId,
      alwaysShowDropdown: false,
      onSelectChange: (imgId) => { this.state.canvas.previewOnImageId = imgId; },
    });
  }

  async _openCpnPreviewModal() {
    const savedImgId = this.state.canvas.previewOnImageId || this.state.edit.previewOnImageId || "";
    await this._openPreviewModal({
      lines: [this.state.form.line1 || "", this.state.form.line2 || "", this.state.form.line3 || "", this.state.form.line4 || ""],
      condensedFont: !!this.state.canvas.condensedFont,
      typeId: this.state.selection.typeId || "",
      typeHandle: this.state.selection.typeHandle || "",
      maxLines: LABEL_TYPE[this.state.selection.typeHandle]?.maxLines ?? 3,
      savedImgId,
      onSelectChange: (imgId) => { this.state.canvas.previewOnImageId = imgId; },
    });
  }

  /** @param {any} row */
  async _openListingPreviewModal(row) {
    const typeCategory = this._getLabelTypeCategory(row.dataset.typeTitle || "");
    return this._openPreviewModal({
      lines: [row.dataset.line1 || "", row.dataset.line2 || "", row.dataset.line3 || "", row.dataset.line4 || ""],
      condensedFont: row.dataset.condensedFont === "true",
      typeId: row.dataset.typeId || "",
      typeHandle: typeCategory,
      maxLines: LABEL_TYPE[typeCategory]?.maxLines ?? 3,
      savedImgId: row.dataset.previewImageId || "",
      isLogo: row.dataset.isLogo === "true",
      labelImageUrl: row.dataset.fullPreviewImage || "",
    });
  }

  _removePreviewUrlParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("preview")) return;
    url.searchParams.delete("preview");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  /** @param {string} message */
  _showPreviewDeepLinkError(message) {
    const heading = document.querySelector(".rs-pl-list-heading");
    if (!heading?.parentElement) return;

    let error = document.getElementById("rs-pl-preview-link-error");
    if (!error) {
      error = document.createElement("div");
      error.id = "rs-pl-preview-link-error";
      error.setAttribute("role", "alert");
      error.style.cssText =
        "margin:12px 0;padding:12px 16px;border-left:4px solid #D22730;" +
        "background:#FFF4F4;color:#7A151A;font-size:14px;";
      heading.parentElement.insertBefore(error, heading.nextSibling);
    }
    error.textContent = message;
  }

  /** @param {boolean} [preserveUrl] */
  _closeListingPreviewModal(preserveUrl = false) {
    if (this._previewModal)   { this._previewModal.remove();   this._previewModal   = null; }
    if (this._previewBackdrop){ this._previewBackdrop.remove(); this._previewBackdrop = null; }
    if (this._previewRO)      { this._previewRO.disconnect();  this._previewRO      = null; }
    if (!preserveUrl) this._removePreviewUrlParam();
  }

  // =========================================================
  // EVENTS
  // =========================================================
  /** @param {any} e */
  async onInput(e) {
    const target = e.target;

    // search input
    if (target?.classList?.contains("rs-input") && target.closest(".rs-pl-search")) {
      const txt = target.value.trim();
      this.state.list.searchText = txt;
      this.state.list.page = 1;

      clearTimeout(this._searchDebounceTimer);
      if (txt.length > 3 || !txt.length) {
        this._searchDebounceTimer = setTimeout(async () => {
          if (!txt.length) this.state.list.searchText = "";
          await this.loadAndRenderList();
        }, 400);
      }
      return;
    }

    if (target?.name === "apn_customer_part_number") {
      const wrapper = target.closest(".rs-input__content");
      const value = target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
      target.value = value;
      const companyFormatError = this._getCompanyCpnFormatError(value);
      if (wrapper && value.length > 18) {
        this._setFieldError(wrapper, this.ERRORS.apnPartNumber, '.rs-input__error p', target);
      } else if (wrapper && companyFormatError) {
        this._setFieldError(wrapper, companyFormatError, '.rs-input__error p', target);
      } else {
        this._clearFieldError(wrapper, '.rs-input__error p', target);
      }
      this._updateApnRowPreviewBtn(target.closest('.rs-table-body__row'));
      this._updateApnSubmitBtnState();
      return;
    }

    if (target?.name === "apn_second_email") {
      const wrapper = target.closest(".rs-input__content");
      const val = target.value.trim();
      if (val && !this._isValidEmail(val)) {
        this._setFieldError(wrapper, this.ERRORS.emailFormat, '.rs-input__error p', target);
      } else {
        this._clearFieldError(wrapper, '.rs-input__error p', target);
      }
      this._updateApnSubmitBtnState();
      return;
    }

    // live validation + canvas redraw for label lines
    if (!target?.classList?.contains("rs-input")) return;
    if (!this.els.canvasInputs.includes(target)) return;

    const container = target.closest(".rs-input__content");
    const value = target.value || "";

    if (value.length > this.state.canvas.maxChars) {
      this._setFieldError(container, this.ERRORS.maxChars, '.rs-input__error p', target);
    } else {
      this._clearFieldError(container, '.rs-input__error p', target);
    }

    this.drawCanvasFromState({ confirmation: false });
  }

  /** @param {any} e */
  async onClick(e) {
    const t = e.target;

    // pagination next/prev
    let nextBtnPagination = t.closest(".rs-button-pagination--next");
    if (nextBtnPagination) {
      let apnPagination = nextBtnPagination.closest(".rs-pl-apn-pagination");
      if (apnPagination) {
        if (this.state.list.useCursor) {
          if (!this.state.list.nextCursor) return;
        } else {
          if (this.state.list.page >= this.state.list.totalPages) return;
        }
        this.state.list.page += 1;
        await this.loadAndRenderList();
        return;
      }
      let cpnPagination = nextBtnPagination.closest(".rs-pl-cpn-pagination");
      if (cpnPagination) {
        if (this.state.cpnView.page >= this.state.cpnView.totalPages) return;
        this.state.cpnView.page += 1;
        await this.renderCustomerPartNumberList();
        return;
      }
    }

    let prevBtnPagination = t.closest(".rs-button-pagination--prev");
    if (prevBtnPagination) {
      let apnPagination = prevBtnPagination.closest(".rs-pl-apn-pagination");
      if (apnPagination) {
        if (this.state.list.page <= 1) return;
        this.state.list.page -= 1;
        await this.loadAndRenderList();
        return;
      }
      let cpnPagination = prevBtnPagination.closest(".rs-pl-cpn-pagination");
      if (cpnPagination) {
        if (this.state.cpnView.page <= 1) return;
        this.state.cpnView.page -= 1;
        await this.renderCustomerPartNumberList();
        return;
      }
    }

    if (t.closest(".rs-pl-apn-add-more-item")) {
      this.duplicateRows(1);
      this._initApnPnDropdownFloat();
      this.refreshPartSetupVisibility();
      return;
    }

    // limit option
    const limitOpt = t.closest(".rs-cart-pagination__rows-option");
    if (limitOpt) {
      let apnPagination = limitOpt.closest(".rs-pl-apn-pagination");
      if (apnPagination) {
        const value = Number(limitOpt.getAttribute("value")) || this.state.list.limit;

        apnPagination.querySelector(".rs-cart-pagination__rows-option.rs-active")?.classList.remove("rs-active");
        limitOpt.classList.add("rs-active");

        const btnText = apnPagination.querySelector(".rs-cart-pagination__rows-button span");
        btnText && (btnText.textContent = `Show ${value} rows`);

        this.state.list.limit = value;
        this.state.list.page = 1;
        await this.loadAndRenderList();
        return;
      }
      let cpnPagination = limitOpt.closest(".rs-pl-cpn-pagination");
      if (cpnPagination) {
        const value = Number(limitOpt.getAttribute("value")) || this.state.cpnView.limit;

        cpnPagination.querySelector(".rs-cart-pagination__rows-option.rs-active")?.classList.remove("rs-active");
        limitOpt.classList.add("rs-active");
        const btnText = cpnPagination.querySelector(".rs-cart-pagination__rows-button span");
        btnText && (btnText.textContent = `Show ${value} rows`);

        this.state.cpnView.limit = value;
        this.state.cpnView.page = 1;
        await this.renderCustomerPartNumberList();
        return;
      }
    }

    // filter open/apply
    if (t.closest(".rs-filter-button")) {
      document.querySelector(".rs-filter")?.classList.toggle("rs-active");
      return;
    }

    if (t.closest(".rs-pl-apply-filters-btn")) {
      this.state.list.page = 1;
      await this.loadAndRenderList();
      document.querySelector(".rs-filter")?.classList.remove("rs-active");
      return;
    }

    if (t.closest(".rs-pl-clear-btn ")) {
      this.state.list.filterPartNumber = "";
      this.state.list.filterStatus = "";
      this.state.list.page = 1;
      await this.loadAndRenderList();
      document.querySelector(".rs-filter")?.classList.remove("rs-active");
      let selectFilterStatus = document.querySelector(".rs-pl-status-select");
      if (selectFilterStatus) {
        selectFilterStatus.querySelectorAll(".rs-custom-select__option").forEach((opt) => {
          const isDefault = opt.getAttribute('data-value') === "all";
          opt.classList.toggle("is-selected", isDefault);
          opt.classList.toggle("is-active", isDefault);
          opt.setAttribute("aria-selected", String(isDefault));
        });
        const input = /** @type {HTMLInputElement|null} */ (selectFilterStatus.querySelector('input[name="status"]'));
        if (input) input.value = "all";
        const valueEl = selectFilterStatus.querySelector(".rs-custom-select__value");
        if (valueEl) valueEl.textContent = _win.__t?.('all') ?? null;
      }
      let selectFilterPartNumber = document.querySelector(".rs-pl-part-number-select");
      if (selectFilterPartNumber) {
        selectFilterPartNumber.querySelectorAll(".rs-custom-select__option").forEach((opt) => {
          const isDefault = opt.getAttribute('data-value') === "";
          opt.classList.toggle("is-selected", isDefault);
          opt.classList.toggle("is-active", isDefault);
          opt.setAttribute("aria-selected", String(isDefault));
        });
        const input = /** @type {HTMLInputElement|null} */ (selectFilterPartNumber.querySelector('input[name="part_number"]'));
        if (input) input.value = "";
        const valueEl = selectFilterPartNumber.querySelector(".rs-custom-select__value");
        if (valueEl) valueEl.textContent = _win.__t?.('all_part_numbers') ?? null;
      }
      return;
    }

    // create new
    const createNewLabelBtn = t.closest(".rs-create-new-pl-btn");
    if (createNewLabelBtn) {
      createNewLabelBtn.disabled = true;
      this.exitEditMode(); // NEW label, not edit
      this.resetCreationState();
      await this.setStep("label-selection");
      createNewLabelBtn.disabled = false;
      return;
    }

    // back button
    const backBtn = t.closest(".rs-button-back-to-selection");
    if (backBtn) {
      const step = backBtn.getAttribute("data-step") || "label-selection";
      await this.setStep(step, { edit: this.state.edit.enabled });
      return;
    }

    // choose type card
    const typeBtn = t.closest(".rs-pl-select-pdp-btn");
    if (typeBtn) {
      const typeHandle = typeBtn.dataset.type || "";
      const typeId = typeBtn.dataset.id || "";
      const step = typeBtn.dataset.step || "label-creation";
      if (!typeHandle || !typeId) return;

      this.state.selection.typeHandle = typeHandle; // folder handle is best
      this.state.selection.typeId = typeId;

      // If editing and user changes type, old product might not exist -> clear product selection
      if (this.state.edit.enabled) {
        this.state.edit.previewOnImageId = "";
      } else {
        // New (non-edit) label creation: clear any values entered for a previously selected
        // type so they don't leak into the newly selected type's form (e.g. Non T Series -> T Series).
        this.state.form = /** @type {any} */ ({ contractorName: "", line1: "", line2: "", line3: "", line4: "" });
        this.fillCreationInputsFromState();
      }

      await Promise.all([
        this.fetchProducts(typeId),
        this.fetchPartSetups(typeId),
      ]);
      await this.setStep(step, { edit: this.state.edit.enabled });
      return;
    }

    // dropdown option click
    const option = t.closest(".rs-private-label-pdp-select .rs-custom-select__option");
    if (option) {
      const id = this.setDropdownSelectionFromOption(option);
      if (id) {
        this.applyProductToCanvas(id);
        // keep edit previewOnImageId in sync if editing
        if (this.state.edit.enabled) {
          const _selP1 = /** @type {any[]} */ (this.state.selection.products).find(function(p) { return String(p.id) === String(id); });
          this.state.edit.previewOnImageId = (_selP1 && /** @type {any} */ (_img(_selP1))?.id) ? String(/** @type {any} */ (_img(_selP1)).id) : "";
        }
        this.drawCanvasFromState({ confirmation: this.state.step === "label-confirmation" });
      }
      return;
    }

    // EDIT draft label
    const editBtn = t.closest(".rs-table-button-edit");
    if (editBtn) {
      const row = editBtn.closest(".rs-table-body__row");
      if (!row) return;

      await this.hydrateEditStateFromRow(row);
      await this.setStep("label-confirmation", { edit: true });
      return;
    }

    const viewCustomerPartNumberBtn = t.closest(".rs-table-button-view-cpn");
    if (viewCustomerPartNumberBtn) {
      const row = viewCustomerPartNumberBtn.closest(".rs-table-body__row");
      if (!row) return;
      this.enterEditModeFromRow(row);
      await this.renderCustomerPartNumberList();
      await this.setStep("customer-part-number");
      return;
    }

    // go confirmation
    const confirmBtn = t.closest(".rs-button-go-confirmation");
    if (confirmBtn) {
      if (!this.validateCreation()) return;

      this.state.form.line1 = this.els.canvasInputs?.[0]?.value.trim() || "";
      this.state.form.line2 = this.els.canvasInputs?.[1]?.value.trim() || "";
      this.state.form.line3 = this.els.canvasInputs?.[2]?.value.trim() || "";
      this.state.form.line4 = this.els.canvasInputs?.[3]?.value.trim() || "";
      // Always re-query so we get the live input value regardless of cache state.
      const _goConfName = /** @type {HTMLInputElement|null} */ (document.querySelector('.rs-input[name="name"]'))?.value?.trim();
      if (_goConfName) this.state.form.contractorName = _goConfName;

      const step = confirmBtn.getAttribute("data-step") || "label-confirmation";
      await this.setStep(step, { edit: this.state.edit.enabled });
      return;
    }

    // edit type/details from confirmation
    if (t.closest(".rs-pl-product-details-edit")) {
      const step = t.closest(".rs-pl-product-details-edit")?.getAttribute("data-step") || "label-selection";
      await this.setStep(step, { edit: this.state.edit.enabled });
      return;
    }

    if (t.closest(".rs-pl-label-details-edit")) {
      const step = t.closest(".rs-pl-label-details-edit")?.getAttribute("data-step") || "label-creation";
      await this.setStep(step, { edit: this.state.edit.enabled });
      return;
    }

    // submit opens modal
    const submitBtn = /** @type {HTMLButtonElement|null} */ (t.closest(".rs-button-submit"));
    if (submitBtn) {
      if (!this.state.edit.enabled) {
        let isMobile = window.innerWidth <= 749;
        let agreeWrapper = isMobile ? document.querySelector(".rs-agree-mobile.rs-desktop-hidden") : document.querySelector(".rs-agree-desktop.rs-mob-hidden");
        if (!agreeWrapper) return;
        const agree = /** @type {HTMLInputElement|null} */ (agreeWrapper.querySelector(".rs-checkbox__input"));
        if (agree?.checked === false) {
          agree.closest(".rs-checkbox__content")?.classList.add("rs-error");
          return;
        }
      }
      if (!this.validateCreation()) return;

      submitBtn.disabled = true;
      this._openModal();
      return;
    }

    // close modal
    if (t.closest(".rs-pl-modal-close-btn")) {
      this._closeModal(/** @type {HTMLButtonElement|null} */ (document.querySelector(".rs-button-submit")));
      const submit = /** @type {HTMLButtonElement|null} */ (document.querySelector(".rs-button-submit"));
      if (submit) submit.disabled = false;
      return;
    }

    // save it later (Pending — user agreed to submit; "later" refers to assigning part numbers, not to submitting)
    const saveLater = t.closest(".rs-save-it-later-btn");
    if (saveLater) {
      if (!this.validateCreation()) return;
      this._clearModalError();
      saveLater.disabled = true;
      try {
        await this.createPrivateLabel({ pending: true });
        this._closeModal(null);
        this.exitEditMode();
        this.resetCreationState();
        await this.setStep("private-label");
        const bannerText = this.els.successBanner?.querySelector(".rs-text p");
        const originalText = bannerText?.textContent;
        if (bannerText) bannerText.textContent = "Label is submitted";
        this.els.successBanner?.classList.add("rs-banner--visible");
        setTimeout(() => {
          this.els.successBanner?.classList.remove("rs-banner--visible");
          if (bannerText && originalText !== undefined) bannerText.textContent = originalText;
        }, 5000);
      } catch (/** @type {any} */ err) {
        console.error(err);
        const isDuplicate = err?.status === 409 || String(err?.body?.error ?? "").toLowerCase().includes("already exists");
        this._showModalError(isDuplicate ? this.ERRORS.duplicate : (err?.body?.error ?? err?.message ?? "An error occurred. Please try again."));
        const submitBtn = /** @type {HTMLButtonElement|null} */ (document.querySelector(".rs-button-submit"));
        if (submitBtn) submitBtn.disabled = false;
      } finally {
        saveLater.disabled = false;
      }
      return;
    }

    // Assign Part Number Modal Button
    const assignModalBtn = t.closest(".rs-assign-part-number-btn");
    if (assignModalBtn) {
      this._closeModal(null);
      this._clearConfirmationError();
      try {
        const data = await this.createPrivateLabel({ pending: true });
        if (data) {
          this.state.edit.enabled = true;
          this.state.edit.labelId = data.privateLabelCompanyAssociation.id;
          this.state.edit.assignedBasePartNumbers = [];
          this.state.form.previewImage = data.privateLabel.labelImageDisplay?.url || "";
          this.renderAssignPartNumber();
          await this.setStep("assign-part-number");
          return;
        }
      } catch (/** @type {any} */ err) {
        console.error(err);
        const isDuplicate = err?.status === 409 || String(err?.body?.error ?? "").toLowerCase().includes("already exists");
        this._showConfirmationError(isDuplicate ? this.ERRORS.duplicate : (err?.body?.error ?? err?.message ?? "An error occurred. Please try again."));
        const submitBtn = /** @type {HTMLButtonElement|null} */ (document.querySelector(".rs-button-submit"));
        if (submitBtn) submitBtn.disabled = false;
      }
    }

    // save draft
    const saveDraft = t.closest(".rs-button-save-for-draft");
    if (saveDraft) {
      if (!this.state.edit.enabled) {
        const agree = /** @type {HTMLInputElement|null} */ (document.querySelector(".rs-checkbox__input"));
        if (agree?.checked === false) {
          agree.closest(".rs-checkbox__content")?.classList.add("rs-error");
          return;
        }
      }
      // In edit mode the user may be on a step where the canvas inputs haven't been
      // populated yet; sync them from state so validateCreation sees real values.
      if (this.state.edit.enabled) this.fillCreationInputsFromState();
      if (!this.validateCreation()) return;

      this._clearConfirmationError();
      saveDraft.disabled = true;
      try {
        const _wasEditing = this.state.edit.enabled;
        await this.createPrivateLabel({ pending: false });
        this.state.list.showSuccessMessage = true;
        this.exitEditMode();
        this.resetCreationState();
        this._closeModal(null);
        await this.setStep("private-label");
        // Only show the success banner when creating a brand-new draft, not when saving
        // an existing label (editing an existing label should silently return to the list).
        if (!_wasEditing) {
          /** @type {any} */ (this.els).successBanner?.classList.add("rs-banner--visible");
          setTimeout(() => /** @type {any} */ (this.els).successBanner?.classList.remove("rs-banner--visible"), 5000);
        }
      } catch (/** @type {any} */ err) {
        console.error(err);
        const isDuplicate = err?.status === 409 || String(err?.body?.error ?? "").toLowerCase().includes("already exists");
        this._showConfirmationError(isDuplicate ? this.ERRORS.duplicate : (err?.body?.error ?? err?.message ?? "An error occurred. Please try again."));
      } finally {
        saveDraft.disabled = false;
      }
      return;
    }

    // Part number and label position dropdowns in assign part number step
    const selectOption = t.closest(".rs-custom-select__option");
    if (selectOption) {
      if (selectOption.closest(".rs-pl-apn-resideo-part-number-select")) {
        this._clearFieldError(selectOption.closest(".rs-pl-apn-resideo-part-number-select"), '.rs-custom-select__error span');
        let row = selectOption.closest(".rs-table-body__row");
        let customSelect = selectOption.closest(".rs-custom-select");
        let listWrapper = customSelect.querySelector(".rs-custom-select__menu");
        let elDescription = row.querySelector(this.S.apnResideoPartNumberDescription);
        // Part setups now carry labelPositions directly — prefer partSetupId for a reliable lookup.
        let id = selectOption.dataset.partSetupId || selectOption.dataset.productId || "";
        let partName = selectOption.dataset.value || "";
        let description = selectOption.dataset.description || "";
        if (elDescription) elDescription.textContent = description;
        customSelect.querySelector(".rs-custom-select__value").innerHTML = description
          ? `${partName}<span class="rs-apn-part-number-description">, ${description}</span>`
          : partName;
        customSelect.querySelector('input[name="apn_resideo_part_number"]').value = partName;
        listWrapper.querySelectorAll('[role="option"]').forEach((/** @type {any} */ el) => el.setAttribute("aria-selected", "false"));
        selectOption.setAttribute("aria-selected", "true");
        customSelect.querySelector(".rs-custom-select__button")?.setAttribute("aria-expanded", "false");
        customSelect.classList.remove("open");
        row.classList.add("rs-row-is-active");
        this.renderLabelPositionOptions(id, row);
        this._updateApnRowPreviewBtn(row);
        this._clearApnRowBackendErrors(row);
        this._updateApnSubmitBtnState();
        this.refreshPartSetupVisibility();
        this._syncApnPartNumberColWidth();
      }
      if (selectOption.closest(".rs-pl-apn-label-position-select")) {
        let row = selectOption.closest(".rs-table-body__row");
        let customSelect = selectOption.closest(".rs-custom-select");
        let listWrapper = customSelect.querySelector(".rs-custom-select__menu");
        let position = selectOption.dataset.value || "";
        let label = selectOption.getAttribute('aria-label') || "";
        customSelect.querySelector(".rs-custom-select__value").textContent = label;
        customSelect.querySelector('input[name="apn_label_position"]').value = position;
        listWrapper.querySelectorAll('[role="option"]').forEach((/** @type {any} */ el) => el.setAttribute("aria-selected", "false"));
        selectOption.setAttribute("aria-selected", "true");
        customSelect.querySelector(".rs-custom-select__button")?.setAttribute("aria-expanded", "false");
        customSelect.classList.remove("open");
        const lpWrapper = selectOption.closest(".rs-custom-select__wrapper");
        if (lpWrapper) {
          this._clearFieldError(lpWrapper, '.rs-custom-select__error span');
          const errEl = lpWrapper.querySelector(".rs-apn-row-error");
          if (errEl) errEl.style.display = "none";
        }
        this._updateApnRowPreviewBtn(row);
        this._updateApnSubmitBtnState();
      }
    }

    const rowSelect = t.closest(".rs-pl-apn-select-checkbox");
    if (rowSelect) {
      e.preventDefault(); // cloned rows share the same input id; prevent the label forwarding the click to row 1's input
      const row = rowSelect.closest(".rs-table-body__row");
      const input = row?.querySelector('.rs-checkbox__content input[type="checkbox"]');
      if (!input) return;
      input.checked = !input.checked;
      if (input.checked) {
        row.classList.add("rs-table-body__row--selected");
        this.state.assignPartNumber.selectedCount += 1;
        this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", String(this.state.assignPartNumber.selectedCount));
        this.els.apnSelectedBanner?.classList.remove("rs-hidden");
      } else {
        this.state.assignPartNumber.selectedCount = Math.max(0, this.state.assignPartNumber.selectedCount - 1);
        row.classList.remove("rs-table-body__row--selected");
        this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", String(this.state.assignPartNumber.selectedCount));
        if (this.state.assignPartNumber.selectedCount === 0) {
          this.els.apnSelectedBanner?.classList.add("rs-hidden");
        }
      }
    }

    const selectAllBtn = t.closest(".rs-pl-apn-select-all-btn");
    if (selectAllBtn) {
      const checkboxes = this.els.apnTableBody.querySelectorAll('.rs-table-body__row .rs-checkbox__content input[type="checkbox"]');
      checkboxes.forEach((/** @type {any} */ cb) => {
        const row = cb.closest(".rs-table-body__row");
        if (cb.checked === false) {
          cb.checked = true;
          row.classList.add("rs-table-body__row--selected");
          this.state.assignPartNumber.selectedCount += 1;
        }
      });
      this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", String(this.state.assignPartNumber.selectedCount));
      if (this.state.assignPartNumber.selectedCount > 0) {
        /** @type {any} */ (this.els).apnSelectedBanner?.classList.remove("rs-hidden");
      }
    }

    const deselectAllBtn = t.closest(".rs-pl-apn-unselect-all-btn");
    if (deselectAllBtn) {
      const checkboxes = this.els.apnTableBody.querySelectorAll('.rs-table-body__row .rs-checkbox__content input[type="checkbox"]');
      checkboxes.forEach((/** @type {any} */ cb) => {
        const row = cb.closest(".rs-table-body__row");
        if (cb.checked === true) {
          cb.checked = false;
          row.classList.remove("rs-table-body__row--selected");
          this.state.assignPartNumber.selectedCount = Math.max(0, this.state.assignPartNumber.selectedCount - 1);
        }
      });
      this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", String(this.state.assignPartNumber.selectedCount));
      if (this.state.assignPartNumber.selectedCount === 0) {
        this.els.apnSelectedBanner?.classList.add("rs-hidden");
      }
    }

    const selectAllCheckbox = t.closest(".rs-pl-apn-select-all-checkbox");
    if (selectAllCheckbox) {
      e.preventDefault(); // same duplicate-id reason as rowSelect
      const checkboxAllWrapper = selectAllCheckbox.closest('.rs-checkbox__content');
      const inputEl = checkboxAllWrapper?.querySelector('input[type="checkbox"]');
      if (!inputEl) return;
      inputEl.checked = !inputEl.checked;
      const nowChecked = inputEl.checked;
      const checkboxes = this.els.apnTableBody.querySelectorAll('.rs-table-body__row .rs-checkbox__content input[type="checkbox"]');
      checkboxes.forEach((/** @type {any} */ cb) => {
        const row = cb.closest(".rs-table-body__row");
        if (nowChecked && cb.checked === false) {
          cb.checked = true;
          row.classList.add("rs-table-body__row--selected");
          this.state.assignPartNumber.selectedCount += 1;
        } else if (!nowChecked && cb.checked === true) {
          cb.checked = false;
          row.classList.remove("rs-table-body__row--selected");
          this.state.assignPartNumber.selectedCount = Math.max(0, this.state.assignPartNumber.selectedCount - 1);
        }
      });
      this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", String(this.state.assignPartNumber.selectedCount));
      if (this.state.assignPartNumber.selectedCount === 0) {
        this.els.apnSelectedBanner?.classList.add("rs-hidden");
      } else {
        this.els.apnSelectedBanner?.classList.remove("rs-hidden");
      }
    }

    const removeSelectedRowsBtn = t.closest(".rs-pl-apn-remove-selected-btn");
    if (removeSelectedRowsBtn) {
      const checkboxes = this.els.apnTableBody.querySelectorAll('.rs-table-body__row .rs-checkbox__content input[type="checkbox"]');
      checkboxes.forEach((/** @type {any} */ cb) => {
        const row = cb.closest(".rs-table-body__row");
        if (cb.checked === true) {
          row.remove();
          this.state.assignPartNumber.selectedCount = Math.max(0, this.state.assignPartNumber.selectedCount - 1);
        }
      });
      this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", String(this.state.assignPartNumber.selectedCount));
      if (this.state.assignPartNumber.selectedCount === 0) {
        this.els.apnSelectedBanner?.classList.add("rs-hidden");
      }
    }

    const submitPartNumbersBtn = t.closest(".rs-pl-apn-submit-btn");
    if (submitPartNumbersBtn) {
      this._clearApnFormError();
      submitPartNumbersBtn.disabled = true;
      try {
        const rows = this.els.apnTableBody.querySelectorAll(".rs-table-body__row");
        let items = /** @type {any[]} */ ([]);
        let hasError = false;

        rows.forEach((/** @type {any} */ row) => {
          const elResideoPartNumber = row.querySelector('input[name="apn_resideo_part_number"]');
          const elCustomPartNumber = row.querySelector('input[name="apn_customer_part_number"]');
          const elLabelPosition = row.querySelector('input[name="apn_label_position"]');
          const resideoPartNumber = elResideoPartNumber?.value?.trim() || "";
          const customPartNumber = elCustomPartNumber?.value?.trim() || "";
          const labelPosition = elLabelPosition?.value?.trim() || "";

          // Blank rows are not errors — skip them so one filled row can be submitted
          if (!resideoPartNumber && !customPartNumber && !labelPosition) return;

          // Basic required-field validation (no business rules)
          if (!resideoPartNumber) {
            this._setFieldError(
              elResideoPartNumber?.closest(".rs-custom-select__wrapper"),
              'Resideo Part Number is required',
              '.rs-custom-select__error span'
            );
            hasError = true;
          }
          if (!customPartNumber) {
            this._setFieldError(
              elCustomPartNumber?.closest(".rs-input__content"),
              'Customer Part Number is required',
              '.rs-input__error p',
              elCustomPartNumber
            );
            hasError = true;
          }
          if (customPartNumber.length > 18) {
            this._setFieldError(
              elCustomPartNumber?.closest(".rs-input__content"),
              this.ERRORS.apnPartNumber,
              '.rs-input__error p',
              elCustomPartNumber
            );
            hasError = true;
          }
          // Company-specific CPN format validation
          const companyFormatError = this._getCompanyCpnFormatError(customPartNumber);
          if (companyFormatError) {
            this._setFieldError(
              elCustomPartNumber?.closest(".rs-input__content"),
              companyFormatError,
              '.rs-input__error p',
              elCustomPartNumber
            );
            hasError = true;
          }
          const lpRowSelect = row.querySelector('.rs-pl-apn-label-position-select');
          const lpHasOptions = lpRowSelect?.querySelector('.rs-custom-select__option') && !lpRowSelect.classList.contains('rs-hidden');
          if (lpHasOptions && !labelPosition) {
            this._setFieldError(
              elLabelPosition?.closest(".rs-custom-select__wrapper"),
              'Label Position is required',
              '.rs-custom-select__error span'
            );
            hasError = true;
          }

          if (resideoPartNumber && customPartNumber && customPartNumber.length <= 18 && (!lpHasOptions || labelPosition)) {
            items.push({
              rowId: row.dataset.rowId || "",
              resideoBaseNumber: resideoPartNumber,
              customerPartNumber: customPartNumber,
              labelPosition: labelPosition,
            });
          }
        });

        // Require at least one filled row
        if (!hasError && items.length === 0) {
          submitPartNumbersBtn.disabled = false;
          this._showApnFormError(this.ERRORS.atLeastOneRow);
          return;
        }

        // Email format validation
        const secondEmailVal = this.els.apnSecondEmail?.value?.trim();
        if (secondEmailVal && !this._isValidEmail(secondEmailVal)) {
          this._setFieldError(
            this.els.apnSecondEmail?.closest(".rs-input__content"),
            this.ERRORS.emailFormat,
            '.rs-input__error p',
            this.els.apnSecondEmail
          );
          hasError = true;
        }

        if (hasError) {
          submitPartNumbersBtn.disabled = false;
          return;
        }

        await this.request("/privateLabelPartNumberAssignment", {
          method: "POST",
          body: /** @type {any} */ ({
            plcaId: this.state.edit.labelId,
            items: items,
            accountNumber: this.getAccountId(),
            emailAddress: this.els.apnEmail?.value?.trim() || null,
            secondEmailAddress: secondEmailVal || null,
          }),
        });
        submitPartNumbersBtn.disabled = false;
        // Hide rows that were not part of this submission so the success view only shows completed rows.
        const submittedRowIds = new Set(items.map((it) => it.rowId));
        this.els.apnTableBody?.querySelectorAll('.rs-table-body__row').forEach((/** @type {HTMLElement} */ row) => {
          if (!submittedRowIds.has(row.dataset.rowId)) row.style.display = 'none';
        });
        document.body.classList.add("rs-pl-asp-submit-success");
        this._wrapApnEmailValues();
      } catch (err) {
        const _err = /** @type {any} */ (err);
        console.error("[rs-private-label] submitPartNumbers error:", _err);
        // Map rowId-based backend errors back to row DOM elements
        const rowErrors = _err.body?.rowErrors;
        if (_err.body?.code === "VALIDATION_FAILED" && rowErrors?.length) {
          const rows = this.els.apnTableBody.querySelectorAll(".rs-table-body__row");
          const rowById = new Map(Array.from(rows).map((r) => [r.dataset.rowId, r]));
          rowErrors.forEach((/** @type {any} */ e) => {
            const row = rowById.get(e.rowId);
            if (!row) return;
            if (e.basePartNumberError) this._showApnRowBackendError(row, 'basePartNumber', e.basePartNumberError);
            if (e.customerPartNumberError) this._showApnRowBackendError(row, 'customerPartNumber', e.customerPartNumberError);
          });
          this._updateApnSubmitBtnState();
        } else {
          submitPartNumbersBtn.disabled = false;
          this._showApnFormError(_err.body?.error ?? _err.message ?? "An error occurred while submitting. Please try again.");
        }
        submitPartNumbersBtn.disabled = false;
      }
    }

    const printPageBtn = t.closest(".rs-pl-asp-print-page-btn");
    if (printPageBtn) {
      window.print();
      return;
    }

    const cancelAssignPartNumbersBtn = t.closest(".rs-pl-apn-cancel-btn");
    if (cancelAssignPartNumbersBtn) {
      this.state.assignPartNumber.selectedCount = 0;
      this.els.apnActionBtnLabel.textContent = this.state.confirmationPrefix.apnActionBtnLabel.replace("#", "0");
      this.els.apnSelectedBanner?.classList.add("rs-hidden");
      let submitButtons = document.querySelectorAll('.rs-button-submit');
      submitButtons?.forEach((btn) => /** @type {HTMLButtonElement} */ (btn).disabled = false);
      this.exitEditMode();
      await this.setStep("private-label");
      return;
    }


    const apnPreviewBtn = t.closest(".rs-pl-apn-preview-btn");
    if (apnPreviewBtn) {
      const row = apnPreviewBtn.closest(".rs-table-body__row");
      const selectedOpt = row?.querySelector('.rs-pl-apn-resideo-part-number-select [aria-selected="true"]');
      const partSetupId = selectedOpt?.dataset?.partSetupId || selectedOpt?.dataset?.productId || "";
      await this._openApnPreviewModal(partSetupId);
    }

    const cpnPreviewBtn = t.closest(".rs-pl-cpn-preview-btn");
    if (cpnPreviewBtn) {
      await this._openCpnPreviewModal();
    }

    const previewImageListBtn = t.closest(".rs-table-button-preview");
    if (previewImageListBtn) {
      const row = previewImageListBtn.closest(".rs-table-body__row");
      if (!row) return;
      await this._openListingPreviewModal(row);
      return;
    }

    const apnPreviewCloseBtn = t.closest(".rs-pl-asp-popup-close-btn");
    if (apnPreviewCloseBtn) {
      this.els.apnPreviewModal.classList.add("rs-hidden");
    }

    const previewImagePopupCloseBtn = t.closest(".rs-pl-preview-image-popup-close-btn");
    if (previewImagePopupCloseBtn) {
      /** @type {any} */ (this.els).modalPreviewImage?.classList.add("rs-hidden");
      this._closeListingPreviewModal();
      return;
    }

    const assignButton = t.closest('.rs-table-button-assign');
    if (assignButton) {
      const row = assignButton.closest(".rs-table-body__row");
      if (!row) return;
      await this.hydrateEditStateFromRow(row);
      this.renderAssignPartNumber();
      await this.setStep("assign-part-number");
      return;
    }

    const deleteDraftButton = t.closest('.rs-table-button-delete-draft');
    if (deleteDraftButton) {
      const row = deleteDraftButton.closest(".rs-table-body__row");
      if (!row) return;
      const plcaId = row.dataset.labelId;
      if (!plcaId) return;
      if (!window.confirm("Are you sure you want to delete this draft label? This action cannot be undone.")) return;
      try {
        deleteDraftButton.disabled = true;
        await this.deleteDraftLabel(plcaId);
        row.remove();
        this.state.list.total = Math.max(0, this.state.list.total - 1);
      } catch (err) {
        console.error("Error deleting draft label:", err);
        deleteDraftButton.disabled = false;
      }
      return;
    }

  }


  /** @param {any} e */
  onChangeCapture(e) {
    // dropdown hidden input change -> update canvas
    const target = e.target;
    const wrapper =
      target?.closest?.(".rs-private-label-pdp-select .rs-custom-select__wrapper") ||
      target?.closest?.(".rs-private-label-pdp-select");
    if (!wrapper) return;

    const hidden = wrapper.querySelector('input[type="hidden"]');
    const id = hidden?.value;
    if (!id) return;

    this.applyProductToCanvas(id);
    if (this.state.edit.enabled) {
      const _selP2 = /** @type {any[]} */ (this.state.selection.products).find(function(p) { return String(p.id) === String(id); });
      this.state.edit.previewOnImageId = (_selP2 && /** @type {any} */ (_img(_selP2))?.id) ? String(/** @type {any} */ (_img(_selP2)).id) : "";
    }
    this.drawCanvasFromState({ confirmation: this.state.step === "label-confirmation" });
  }

  /** @param {any} e */
  onChangeBubble(e) {
    // status filter select
    const statusWrapper = e.target?.closest?.(".rs-pl-status-select");
    if (statusWrapper) {
      const hidden = statusWrapper.querySelector('input[type="hidden"]');
      const status = hidden?.value;
      if (!status) return;
      this.state.list.filterStatus = status;
    }
  }

  onPageShow() {
    if (this.state.step === "label-creation") {
      const _pageShowScope = ".rs-private-label-creation-section";
      const defaultSelectedId = this.updateProductDropdownOptions(this.state.selection.products, _pageShowScope);
      const _prods2382 = /** @type {any[]} */ (this.state.selection.products);
      const _editImg2382 = this.state.edit.previewOnImageId;
      const _canvasImg2382 = this.state.canvas.previewOnImageId;
      const _editProd2382 = _editImg2382 ? _prods2382.find(function(p) { return /** @type {any} */ (_img(p)) && String(/** @type {any} */ (_img(p)).id) === String(_editImg2382); }) : null;
      const _canvasProd2382 = _canvasImg2382 ? _prods2382.find(function(p) { return /** @type {any} */ (_img(p)) && String(/** @type {any} */ (_img(p)).id) === String(_canvasImg2382); }) : null;
      const selectedId = (_editProd2382 ? String(/** @type {any} */ (_editProd2382).id) : "") || (_canvasProd2382 ? String(/** @type {any} */ (_canvasProd2382).id) : "") || defaultSelectedId;
      if (selectedId) {
        const ok = this.selectProductInDropdown(selectedId, _pageShowScope);
        const productIdToApply = ok ? selectedId : defaultSelectedId;
        if (productIdToApply) this.applyProductToCanvas(productIdToApply);
      }
      this.drawCanvasFromState({ confirmation: false });
    }
  }

  async onPopState() {
    await this.restoreFromUrl();
  }

  // =========================================================
  // UTIL (customer context)
  // =========================================================
  _wrapApnEmailValues() {
    ['.rs-pl-apn-email-address', '.rs-pl-apn-second-email'].forEach((selector) => {
      const container = document.querySelector(selector);
      if (!container) return;
      const input = container.querySelector('input[type="email"]');
      if (!input || !input.value) return;
      const label = container.querySelector('.rs-input__label');
      // Replace the input with a wrapping span showing the same value.
      const span = document.createElement('span');
      span.className = 'rs-pl-apn-email-value';
      span.textContent = input.value;
      input.replaceWith(span);
      if (label) label.textContent = label.textContent.replace(/\s*$/, '');
    });
  }

  // =========================================================
  getAccountId() {
    return document.body.getAttribute("data-external-company-id");
  }
  // Re-reads the context cookie live rather than trusting the module-load-time
  // `customerContext` import, which can be stale/empty if this code runs before
  // the cookie is populated (see customerContextWait in rs-current-company-location).
  _getCurrentContext() {
    return getCtx() ?? customerContext;
  }
  // ctx.sales_area is an array of metaobject entries (each shaped { fields: {...} }),
  // not an object — mirrors the unwrapping in resolveSalesArea() in
  // rs-current-company-location.js. Reading `.fields` directly off the array (as this
  // code previously did) is always undefined, which silently produced the wrong values.
  _getSalesAreaFields() {
    const cand = this._getCurrentContext()?.sales_area;
    return (Array.isArray(cand) ? cand[0]?.fields || cand[0] : cand?.fields || cand) || {};
  }
  getSalesOrg() {
    return this._getSalesAreaFields()?.sales_org_id || "";
  }
  getDistChannel() {
    return this._getSalesAreaFields()?.distribution_channel_id || "";
  }
  getDivision() {
    return this._getSalesAreaFields()?.division_id || "";
  }
  getStateCode() {
    return this._getCurrentContext()?.state_code || "";
  }
  getSalesArea() {
    const org = this.getSalesOrg().padStart(4, "0");
    const channel = this.getDistChannel().padStart(2, "0");
    const division = this.getDivision().padStart(2, "0");
    return `${org}${channel}${division}`;
  }
  // Display format used in the PLP admin: sales org (4) + distribution channel (2).
  getSalesAreaDisplay() {
    const org = this.getSalesOrg().padStart(4, "0");
    const channel = this.getDistChannel().padStart(2, "0");
    return `${org}${channel}`;
  }
  getCompanyName() {
    return document.body.getAttribute("data-customer-name") || "";
  }

  /** @param {string} customPartNumber @returns {string|null} */
  _getCompanyCpnFormatError(customPartNumber) {
    if (!customPartNumber) return null;
    const companyName = (this.getCompanyName() || "").trim().toLowerCase();
    if (companyName === "michel re company inc") {
      if (!customPartNumber.startsWith("1C")) {
        return 'Customer part number must start with "1C".';
      }
      if (customPartNumber.length < 11 || customPartNumber.length > 12) {
        return "Customer part number must be between 11 and 12 characters.";
      }
    } else if (companyName.includes("ferguson")) {
      if (!customPartNumber.startsWith("H")) {
        return 'Customer part number must start with "H".';
      }
      if (customPartNumber.length > 15) {
        return "Customer part number must be 15 characters or fewer.";
      }
    }
    return null;
  }
}

// Boot
document.addEventListener("DOMContentLoaded", () => {
  const app = new PrivateLabelApp({ env: _win.gadget_env || "development" });
  app.init();
});
